//canvas.jsx
import { Canvas, useThree, invalidate } from "@react-three/fiber";
import { OrbitControls, useGLTF } from "@react-three/drei";
import { EffectComposer, Bloom } from "@react-three/postprocessing";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import * as BufferGeometryUtils from "three/examples/jsm/utils/BufferGeometryUtils";
import * as THREE from "three";
import html2canvas from "html2canvas";
import { useDrag, useDrop, DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Moveable from "react-moveable";
import React, {
  Component,
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import * as fabric from "fabric";
import GradientPicker from "react-best-gradient-color-picker";
import { useSpring, animated } from "react-spring";
import { CircularProgress } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "../../contexts/AuthContext";
import { getFirebaseAuth } from "../../config/firebase";
// Firebase imports removed - using backend API instead
import {
  uploadFileToStorage,
  fetchUserCanvases,
  upsertAsset,
  getCanvasLimit,
  getCurrentCanvasCount,
  fetchUserStorageUsage,
  getStorageLimitBytes,
  computeFileHash,
  generateNextCanvasName,
} from "../../services/dashboardService";
import { upsertCanvas } from "../../services/canvasService";

import WebFont from "webfontloader";
import useIdleTimer from "../../utils/useIdleTimer";
import ReactDOM from "react-dom";
import "./Canvas.css";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return <h2>Something went wrong.</h2>;
    }
    return this.props.children;
  }
}

const handleInputFocus = (e) => {
  e.target.select();
};

const fontWeights = [
  { value: 100, label: "Thin" },
  { value: 200, label: "Extra Light" },
  { value: 300, label: "Light" },
  { value: 400, label: "Normal" },
  { value: 500, label: "Medium" },
  { value: 600, label: "Semi Bold" },
  { value: 700, label: "Bold" },
  { value: 800, label: "Extra Bold" },
  { value: 900, label: "Black" },
];

const fontFamilies = [
  "Asap",
  "Barlow",
  "Bitter",
  "Chivo",
  "DM Sans",
  "Epilogue",
  "Fira Sans",
  "Fraunces",
  "Inter",
  "Jost",
  "Kanit",
  "Libre Franklin",
  "Montserrat",
  "MuseoModerno",
  "Noto Sans",
  "Noto Serif",
  "Noto Serif Display",
  "Overpass",
  "Poppins",
  "Raleway",
  "Roboto Condensed",
  "Roboto Slab",
  "Taviraj",
  "Urbanist",
  "Work Sans",
];

const Model = ({ url, scale, exposure, lightColor }) => {
  // Use the URL directly if it's already a proxy URL, otherwise construct it
  const resolvedUrl = url.startsWith("/api/serve-image")
    ? url
    : `/api/fetch-image?filePath=${encodeURIComponent(url)}`;

  // Load the GLTF model from the resolved URL
  const gltf = useGLTF(resolvedUrl);

  // Clone the scene so that each instance has its own copy.
  const clonedScene = useMemo(() => gltf.scene.clone(true), [gltf]);

  useEffect(() => {
    clonedScene.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        const originalGeometry = child.geometry;
        const smoothedGeometry =
          BufferGeometryUtils.mergeVertices(originalGeometry);
        child.geometry = smoothedGeometry;

        if (child.material) {
          const material = child.material;
          material.metalness = 0.8;
          material.roughness = 0.2;
          material.envMapIntensity = 1;

          if (material.map) {
            material.map.anisotropy = 16;
            material.map.minFilter = THREE.LinearMipmapLinearFilter;
          }
          if (material.normalMap) {
            material.normalMap.anisotropy = 16;
            material.normalMap.minFilter = THREE.LinearMipmapLinearFilter;
          }
          child.frustumCulled = false;
        }
      }
    });
  }, [clonedScene, exposure]);

  if (!gltf || !gltf.scene) return null;

  return (
    <>
      <ambientLight intensity={exposure} color={lightColor} />
      <primitive object={clonedScene} scale={scale} />
    </>
  );
};

const GLBViewerContent = ({
  fileUrl,
  scale,
  exposure,
  lightColor,
  controlsState,
  setControlsState,
  setShowControlsIndicator,
  dontShowAgain,
  setShowInteractiveModal,
}) => {
  const { camera, gl, scene } = useThree();
  const controlsRef = useRef();

  useEffect(() => {
    if (setShowControlsIndicator) {
      setShowControlsIndicator(false);
    }

    if (controlsState && controlsRef.current) {
      camera.position.copy(controlsState.cameraPosition);
      controlsRef.current.target.copy(controlsState.controlsTarget);
      invalidate(); // Force canvas re-render
    }
  }, [controlsState, camera]);

  useEffect(() => {
    // On mount, only once:
    if (!dontShowAgain) {
      setShowInteractiveModal(true);
    }
  }, []); // No dependencies => runs only once

  useEffect(() => {
    const updateControlsState = () => {
      if (controlsRef.current) {
        setControlsState({
          cameraPosition: camera.position.clone(),
          controlsTarget: controlsRef.current.target.clone(),
        });
      }
    };

    gl.domElement.addEventListener("mouseup", updateControlsState);
    gl.domElement.addEventListener("touchend", updateControlsState);

    return () => {
      gl.domElement.removeEventListener("mouseup", updateControlsState);
      gl.domElement.removeEventListener("touchend", updateControlsState);
    };
  }, [camera, gl, setControlsState]);

  return (
    <>
      <ambientLight intensity={exposure} color={lightColor} />
      <hemisphereLight
        skyColor={"#ffffff"}
        groundColor={"#444444"}
        intensity={0.8}
      />
      {/* <directionalLight position={[5, 5, 5]} intensity={exposure} castShadow shadow-mapSize-width={8192} shadow-mapSize-height={8192} shadow-radius={2} shadow-camera-near={0.5} shadow-camera-far={50}/> */}
      <spotLight
        position={[10, 10, 10]}
        angle={0.3}
        penumbra={0.5}
        intensity={exposure}
        castShadow
      />
      <pointLight position={[10, 10, 10]} />
      {fileUrl && (
        <Model
          url={fileUrl}
          scale={scale}
          exposure={exposure}
          lightColor={lightColor}
        />
      )}
      <OrbitControls
        ref={controlsRef}
        enablePan={false}
        enableRotate={true}
        enableDamping={true}
        dampingFactor={0.05}
        mouseButtons={{ RIGHT: THREE.MOUSE.ROTATE }}
        maxDistance={100000}
      />
      <EffectComposer>
        <Bloom
          intensity={0.8}
          luminanceThreshold={0.3}
          luminanceSmoothing={0.7}
        />
        {/* <DepthOfField focusDistance={0.02} focalLength={0.02} bokehScale={2} /> */}
      </EffectComposer>
    </>
  );
};

async function generateModelSnapshotInMemory(
  modelUrl,
  width = 1080,
  height = 1080,
) {
  return new Promise((resolve, reject) => {
    // 1) Create a new THREE.WebGLRenderer with no “canvas” option
    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      preserveDrawingBuffer: true,
      antialias: true,
    });

    // 2) Set the renderer size (this is the in-memory canvas size)
    renderer.setSize(width, height, /* updateStyle = */ false);

    // 3) Optionally match your main toneMapping / exposure
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;

    // 4) Make a new THREE.Scene + camera
    const scene = new THREE.Scene();
    scene.background = new THREE.Color("#ffffff");

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(0, 0, 3.5);

    // 5) Add some lights
    const ambient = new THREE.AmbientLight(0xffffff, 0.3);
    scene.add(ambient);

    const hemi = new THREE.HemisphereLight("#ffffff", "#444444", 0.8);
    scene.add(hemi);

    const spot = new THREE.SpotLight("#ffffff", 1);
    spot.position.set(10, 10, 10);
    spot.angle = 0.3;
    spot.penumbra = 0.5;
    spot.castShadow = true;
    scene.add(spot);

    const point = new THREE.PointLight("#ffffff", 1);
    point.position.set(10, 10, 10);
    scene.add(point);

    // 6) Use GLTFLoader to load model
    const loader = new GLTFLoader();
    loader.load(
      modelUrl,
      (gltf) => {
        const root = gltf.scene;
        scene.add(root);

        // For example: auto-center & scale
        const box = new THREE.Box3().setFromObject(root);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        root.position.x -= center.x;
        root.position.y -= center.y;
        root.position.z -= center.z;

        const maxDim = Math.max(size.x, size.y, size.z);
        const scaleFactor = 2 / maxDim;
        root.scale.set(scaleFactor, scaleFactor, scaleFactor);

        // 7) Render a couple frames
        renderer.render(scene, camera);
        requestAnimationFrame(() => {
          renderer.render(scene, camera);

          // 8) Grab dataURL from the renderer’s own canvas
          const dataUrl = renderer.domElement.toDataURL("image/png");

          // Clean up
          renderer.dispose();
          scene.remove(root);

          resolve(dataUrl);
        });
      },
      undefined,
      (err) => {
        reject(err);
      },
    );
  });
}

const ExposureSlider = ({ model, handleExposureChange, showHeading }) => {
  const truncatedFileName = model?.name
    ? model.name.length > 12
      ? model.name.slice(0, 12) + "...\u00A0"
      : model.name + "\u00A0"
    : "Unnamed Model\u00A0";

  return (
    <div className="exposure-customization">
      {showHeading && <h2 style={{ color: "white" }}>Exposure</h2>}
      <label>
        {truncatedFileName}:
        <input
          className="exposure-input-models"
          type="number"
          min="0"
          max="10"
          step="0.1"
          value={model.exposure ?? 1}
          onChange={(e) => handleExposureChange(e, model.id)}
          onBlur={(e) => handleExposureChange(e, model.id, true)}
          onFocus={handleInputFocus}
        />
        <input
          className="exposure-slider-models"
          type="range"
          min="0"
          max="10"
          step="0.1"
          value={model.exposure ?? 1}
          onChange={(e) => handleExposureChange(e, model.id)}
          onMouseUp={(e) => handleExposureChange(e, model.id, true)}
          style={{ width: "100%", marginBottom: "10px" }}
        />
      </label>
    </div>
  );
};

const ImageExposureSlider = ({
  image,
  handleBgExposureChange,
  showHeading,
}) => {
  // Shorten the image’s name for display if needed
  const truncatedFileName = image?.name
    ? image.name.length > 12
      ? image.name.slice(0, 12) + "...\u00A0"
      : image.name + "\u00A0"
    : "Unnamed Image\u00A0";

  return (
    <div className="exposure-customization">
      {showHeading && <h2 style={{ color: "white" }}>Exposure</h2>}
      <label>
        {truncatedFileName}:
        <input
          className="exposure-input-models"
          type="number"
          min="0"
          max="10"
          step="0.1"
          value={image.exposure ?? 1}
          onChange={(e) => handleBgExposureChange(e, image.id)}
          onBlur={(e) => handleBgExposureChange(e, image.id, true)}
          onFocus={handleInputFocus}
        />
        <input
          className="exposure-slider-models"
          type="range"
          min="0"
          max="10"
          step="0.1"
          value={image.exposure ?? 1}
          onChange={(e) => handleBgExposureChange(e, image.id)}
          onMouseUp={(e) => handleBgExposureChange(e, image.id, true)}
          style={{ width: "100%", marginBottom: "10px" }}
        />
      </label>
    </div>
  );
};

const ColorPickerBar = ({ color, onColorChange }) => {
  const [isPickerVisible, setIsPickerVisible] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const [offset, setOffset] = useState({ offsetX: 0, offsetY: 0 });
  const [pickerDimensions, setPickerDimensions] = useState({
    width: 180,
    height: 180,
  });
  const pickerRef = useRef(null);
  const dragHandleRef = useRef(null);
  const [initialOffsetX, setInitialOffsetX] = useState(0); // Default 50% width
  const [initialOffsetY, setInitialOffsetY] = useState(0); // Default 40% height

  const updateDimensions = () => {
    if (window.matchMedia("(max-width: 1919px)").matches) {
      setPickerDimensions({ width: 180, height: 180 });
      setInitialOffsetX(0.25);
      setInitialOffsetY(0.4);
    } else {
      setPickerDimensions({ width: 250, height: 250 });
      setInitialOffsetX(0.25);
      setInitialOffsetY(0.4);
    }
  };

  useEffect(() => {
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  const calculatePositionFromPercentage = (xPercentage, yPercentage) => {
    const xPos = window.innerWidth * xPercentage;
    const yPos = window.innerHeight * yPercentage;
    return { top: yPos, left: xPos };
  };

  const handleMouseDown = (e) => {
    if (dragHandleRef.current && dragHandleRef.current.contains(e.target)) {
      setDragging(true);
      setOffset({
        offsetX: e.clientX - pickerPosition.left,
        offsetY: e.clientY - pickerPosition.top,
      });
      document.body.style.userSelect = "none";
    }
  };

  const handleMouseMove = (e) => {
    if (dragging && pickerRef.current) {
      const newLeft = e.clientX - offset.offsetX;
      const newTop = e.clientY - offset.offsetY;
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const pickerWidth = pickerRef.current.offsetWidth;
      const pickerHeight = pickerRef.current.offsetHeight;
      const bottomBuffer = windowHeight * 0.001;
      const rightBuffer = windowWidth * 0.001;
      const clampedLeft = Math.min(
        Math.max(0, newLeft),
        windowWidth - pickerWidth - rightBuffer,
      );
      const clampedTop = Math.min(
        Math.max(0, newTop),
        windowHeight - pickerHeight - bottomBuffer,
      );
      setPickerPosition({ left: clampedLeft, top: clampedTop });
    }
  };

  const handleMouseUp = () => {
    setDragging(false);
    document.body.style.userSelect = "";
    onColorChange(color, true);
  };

  const handleBlur = () => {
    onColorChange(color, true);
  };

  useEffect(() => {
    if (dragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging]);

  const togglePicker = () => {
    setIsPickerVisible((prev) => {
      if (!prev) {
        const { top, left } = calculatePositionFromPercentage(
          initialOffsetX,
          initialOffsetY,
        );
        setPickerPosition({ top, left });
      }
      return !prev;
    });
  };
  return (
    <div className="color-picker-toggle-container">
      <div className="color-picker-toggle" onClick={togglePicker}>
        <div
          style={{
            width: "24px",
            height: "24px",
            backgroundColor: color,
            marginRight: "10px",
            borderRadius: "50%",
          }}
        />
        <span>{color}</span>
      </div>
      {isPickerVisible && (
        <div
          ref={pickerRef}
          className="color-picker-background"
          style={{
            top: `${pickerPosition.top}px`,
            left: `${pickerPosition.left}px`,
          }}
          onMouseUp={handleMouseUp}
          onBlur={handleBlur}
        >
          <div
            ref={dragHandleRef}
            onMouseDown={handleMouseDown}
            className="color-picker-drag"
            style={{
              width: "100%",
              backgroundColor: "#383838",
              cursor: "move",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
              marginBottom: "10px",
            }}
          />
          <GradientPicker
            value={color}
            onChange={onColorChange}
            width={pickerDimensions.width}
            height={pickerDimensions.height}
            hidePresets
            hideColorTypeBtns
          />
        </div>
      )}
    </div>
  );
};

const ImageAiBar = ({
  handleAddToCanvas,
  setUsageModalMessage,
  setShowUsageModal,
  setShowPromptRequiredModal,
  currentUser,
}) => {
  const [isPickerVisible, setIsPickerVisible] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const [offset, setOffset] = useState({ offsetX: 0, offsetY: 0 });
  const [pickerDimensions, setPickerDimensions] = useState({
    width: 180,
    height: 180,
  });
  const pickerRef = useRef(null);
  const dragHandleRef = useRef(null);
  const [initialOffsetX, setInitialOffsetX] = useState(0);
  const [initialOffsetY, setInitialOffsetY] = useState(0);
  const [generatedText, setGeneratedText] = useState("");
  const [textPrompt, setTextPrompt] = useState("");
  const [url, setUrl] = useState("");
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [textUsageStats, setTextUsageStats] = useState({
    currentCount: 0,
    monthlyLimit: 0,
    remaining: 0,
  });
  const [imageUsageStats, setImageUsageStats] = useState({
    currentCount: 0,
    monthlyLimit: 0,
    remaining: 0,
  });

  const placeholderImageUrl = "ai-placeholder-img2.png";

  // Fetch current usage stats from backend
  const fetchUsageStats = async () => {
    try {
      if (!currentUser) return;
      const idToken = await currentUser.getIdToken();

      // Validate token to prevent 'match' error
      if (!idToken || typeof idToken !== "string" || idToken.trim() === "") {
        throw new Error("Failed to obtain valid authentication token");
      }

      const response = await fetch("/api/ai/usage-stats", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTextUsageStats({
          currentCount: data.textUsage?.currentCount || 0,
          monthlyLimit: data.textUsage?.monthlyLimit || 0,
          remaining: data.textUsage?.remaining || 0,
        });
        setImageUsageStats({
          currentCount: data.imageUsage?.currentCount || 0,
          monthlyLimit: data.imageUsage?.monthlyLimit || 0,
          remaining: data.imageUsage?.remaining || 0,
        });

      }
    } catch (error) {

    }
  };

  // Fetch usage stats when component mounts and when user changes
  useEffect(() => {
    fetchUsageStats();
  }, [currentUser]);

  // Check for usage stats updates every 10 seconds for real-time reset display
  useEffect(() => {
    if (!currentUser) return;

    const interval = setInterval(() => {
      fetchUsageStats();
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [currentUser]);

  const handleAiClick = async () => {
    // Check if prompt is empty before making API call
    if (!prompt || prompt.trim() === "") {
      setShowPromptRequiredModal(true);
      return;
    }

    setLoading(true);
    try {
      // Get Firebase ID token for authentication
      if (!currentUser) {
        throw new Error("User not authenticated");
      }
      const idToken = await currentUser.getIdToken();

      // Validate token to prevent 'match' error
      if (!idToken || typeof idToken !== "string" || idToken.trim() === "") {
        throw new Error("Failed to obtain valid authentication token");
      }

      // 2. Now call your API endpoint with the token.
      const response = await fetch(`/api/generate-image`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ prompt }),
      });

      // 3. Check for a 403 error and handle it.
      if (!response.ok) {
        if (response.status === 403) {
          // Instead of alert, set the modal state
          setUsageModalMessage(
            "Monthly token limit reached for Visual Assistant image generation.",
          );
          setShowUsageModal(true);
          return;
        }
        throw new Error("Failed to generate image");
      }

      const data = await response.json();
      const imageUrl = data.imageUrl;
      setUrl(imageUrl);

      // Simply refetch current usage from backend
      fetchUsageStats();
    } catch (error) {
      // Error handling...
    } finally {
      setLoading(false);
    }
  };

  // Similarly, update your text generation function:
  const handleTextGeneration = async (prompt) => {
    // Check if prompt is empty before making API call
    if (!prompt || prompt.trim() === "") {
      setShowPromptRequiredModal(true);
      return;
    }

    try {
      // Get Firebase ID token for authentication
      if (!currentUser) {
        throw new Error("User not authenticated");
      }
      const idToken = await currentUser.getIdToken();

      // Validate token to prevent 'match' error
      if (!idToken || typeof idToken !== "string" || idToken.trim() === "") {
        throw new Error("Failed to obtain valid authentication token");
      }

      const response = await fetch(`/api/generate-text`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ prompt }),
      });

      if (!response.ok) {
        const errorData = await response.json();


        if (response.status === 403) {
          setUsageModalMessage(
            "Monthly token limit reached for Visual Assistant text generation.",
          );
          setShowUsageModal(true);
          return;
        }

        throw new Error(errorData.error || "Failed to generate text");
      }

      const data = await response.json();
      setGeneratedText(data.text);

      // Simply refetch current usage from backend
      fetchUsageStats();
    } catch (error) {

      setUsageModalMessage(`Error generating text: ${error.message}`);
      setShowUsageModal(true);
    }
  };

  const updateDimensions = () => {
    if (window.matchMedia("(max-width: 1919px)").matches) {
      setPickerDimensions({ width: "100%", height: "auto" });
      setInitialOffsetX(0.78); // Set 40% width for smaller screens
      setInitialOffsetY(0.02); // Set 35% height for smaller screens
    } else {
      setPickerDimensions({ width: "100%", height: "auto" });
      setInitialOffsetX(0.78); // Set 40% width for smaller screens
      setInitialOffsetY(0.02); // Set 35% height for smaller screens
    }
  };

  useEffect(() => {
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);
  const calculatePositionFromPercentage = (xPercentage, yPercentage) => {
    const xPos = window.innerWidth * xPercentage;
    const yPos = window.innerHeight * yPercentage;
    return { top: yPos, left: xPos };
  };
  const handleMouseDown = (e) => {
    if (dragHandleRef.current && dragHandleRef.current.contains(e.target)) {
      setDragging(true);
      setOffset({
        offsetX: e.clientX - pickerPosition.left,
        offsetY: e.clientY - pickerPosition.top,
      });
      document.body.style.userSelect = "none";
    }
  };
  const handleMouseMove = (e) => {
    if (dragging && pickerRef.current) {
      const newLeft = e.clientX - offset.offsetX;
      const newTop = e.clientY - offset.offsetY;
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const pickerWidth = pickerRef.current.offsetWidth;
      const pickerHeight = pickerRef.current.offsetHeight;
      const bottomBuffer = windowHeight * 0.001;
      const rightBuffer = windowWidth * 0.001;
      const clampedLeft = Math.min(
        Math.max(0, newLeft),
        windowWidth - pickerWidth - rightBuffer,
      );
      const clampedTop = Math.min(
        Math.max(0, newTop),
        windowHeight - pickerHeight - bottomBuffer,
      );
      setPickerPosition({
        left: clampedLeft,
        top: clampedTop,
      });
    }
  };

  const handleMouseUp = () => {
    setDragging(false);
    document.body.style.userSelect = "";
  };

  useEffect(() => {
    if (dragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging]);

  const togglePicker = () => {
    setIsPickerVisible((prev) => {
      if (!prev) {
        const { top, left } = calculatePositionFromPercentage(
          initialOffsetX,
          initialOffsetY,
        );
        setPickerPosition({ top, left });
      }
      return !prev;
    });
  };

  // Fetch AI usage stats from Firestore
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const fetchUsageStats = async () => {

      try {
        if (!currentUser) {

          return;
        }

        // Import Firestore service function
        const { getUserSubscriptionDetails } = await import(
          "../../services/firestoreService"
        );


        const userDetails = await getUserSubscriptionDetails(currentUser.uid);



        if (userDetails) {
          const imageStats = {
            currentCount: userDetails.imageCount || 0,
            monthlyLimit: userDetails.maxImagesPerMonth || 0,
            remaining:
              (userDetails.maxImagesPerMonth || 0) -
              (userDetails.imageCount || 0),
          };

          const textStats = {
            currentCount: userDetails.textCount || 0,
            monthlyLimit: userDetails.maxTextPerMonth || 0,
            remaining:
              (userDetails.maxTextPerMonth || 0) - (userDetails.textCount || 0),
          };



          setImageUsageStats(imageStats);
          setTextUsageStats(textStats);

          // Mark as initialized to prevent duplicate calls
          setIsInitialized(true);
        } else {

        }
      } catch (error) {

      }
    };

    // Add a small delay to ensure component is fully mounted
    const timer = setTimeout(() => {
      fetchUsageStats();
    }, 100);

    return () => clearTimeout(timer);
  }, [currentUser]); // This runs when currentUser changes

  // Add periodic refresh for usage stats that includes reset logic
  useEffect(() => {
    if (!currentUser) return;

    const refreshUsageStats = async () => {
      try {
        const idToken = await currentUser.getIdToken();

        // Validate token to prevent 'match' error
        if (!idToken || typeof idToken !== "string" || idToken.trim() === "") {
          throw new Error("Failed to obtain valid authentication token");
        }

        const response = await fetch("/api/ai/usage-stats", {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });

        if (response.ok) {
          const data = await response.json();

          setImageUsageStats({
            currentCount: data.imageUsage.currentCount,
            monthlyLimit: data.imageUsage.monthlyLimit,
            remaining: data.imageUsage.remaining,
          });

          setTextUsageStats({
            currentCount: data.textUsage.currentCount,
            monthlyLimit: data.textUsage.monthlyLimit,
            remaining: data.textUsage.remaining,
          });


        }
      } catch (error) {

      }
    };

    // Initial load
    refreshUsageStats();

    // Periodic refresh every 60 seconds (1 minute)
    const refreshInterval = setInterval(refreshUsageStats, 60000);

    return () => clearInterval(refreshInterval);
  }, [currentUser]);

  // Then add this separate useEffect for tracking the frontend display values
  useEffect(() => {

  }, [imageUsageStats, textUsageStats]);

  return (
    <>
      <div className="image-ai-toggle-container">
        <div className="image-ai-toggle" onClick={togglePicker}>
          <img id="image-ai-toggle-img" src="virtual-assistant.png" />
        </div>
        {isPickerVisible && (
          <div
            ref={pickerRef}
            className="image-ai-background"
            onClick={(e) => e.stopPropagation()} // Prevent click events inside popup from reaching the toggle
            style={{
              top: `${pickerPosition.top}px`,
              left: `${pickerPosition.left}px`,
            }}
          >
            <div className="image-ai-content">
              <img src="virtual-assistant.png" id="image-ai-title"></img>
              <hr style={{ margin: "0" }}></hr>

              {/* Image Generation Section with inline counter */}
              <div className="input-with-counter">
                <input
                  type="text"
                  placeholder="Imagine..."
                  onChange={(event) => setPrompt(event.target.value)}
                  style={{
                    color: "black",
                    backgroundColor: "white",
                    paddingLeft: "5px",
                    borderRadius: "5px",
                  }}
                />
                <span className="token-counter">
                  {imageUsageStats.currentCount}/{imageUsageStats.monthlyLimit}
                </span>
              </div>

              <button
                onClick={handleAiClick}
                className="imageai-button"
                style={{
                  color: "white",
                  backgroundColor: "#383838",
                  borderRadius: "5px",
                  width: "100%",
                }}
              >
                Generate Image
              </button>
              <img
                src={url || placeholderImageUrl}
                style={{
                  width: pickerDimensions.width,
                  height: pickerDimensions.height,
                }}
              />
              {loading && (
                <CircularProgress
                  sx={{ color: "#db0081", mx: "auto" }}
                ></CircularProgress>
              )}
              <button
                onClick={() => handleAddToCanvas(url)}
                disabled={!url}
                className="imageai-button"
                style={{
                  color: "white",
                  backgroundColor: "#383838",
                  borderRadius: "5px",
                  width: "100%",
                }}
              >
                Add to Canvas
              </button>
              <hr style={{ margin: "0" }}></hr>

              {/* Text Generation Section with inline counter */}
              <div className="ai-text-response">
                {generatedText && <p>Visual Assistant: {generatedText}</p>}
              </div>

              <div className="input-with-counter">
                <input
                  type="text"
                  placeholder="Enter a prompt..."
                  value={textPrompt}
                  onChange={(e) => setTextPrompt(e.target.value)}
                  style={{
                    color: "black",
                    backgroundColor: "white",
                    paddingLeft: "5px",
                    borderRadius: "5px",
                  }}
                />
                <span className="token-counter">
                  {textUsageStats.currentCount}/{textUsageStats.monthlyLimit}
                </span>
              </div>

              <button
                className="imageai-button"
                style={{
                  color: "white",
                  backgroundColor: "#383838",
                  borderRadius: "5px",
                  width: "100%",
                }}
                onClick={() => handleTextGeneration(textPrompt)}
              >
                Generate Text
              </button>

              <hr style={{ margin: "0" }}></hr>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const FontPickerBar = ({ fontFamily, onFontChange, fontFamilies }) => {
  const [isPickerVisible, setIsPickerVisible] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 100, left: 100 });
  const [initialOffsetX, setInitialOffsetX] = useState(0); // 50% as decimal
  const [initialOffsetY, setInitialOffsetY] = useState(0); // 30% as decimal
  const [offset, setOffset] = useState({ offsetX: 0, offsetY: 0 });
  const [searchTerm, setSearchTerm] = useState(""); // State for search input
  const [pickerDimensions, setPickerDimensions] = useState({
    width: 180,
    height: 180,
  });
  const pickerRef = useRef(null);
  const dragHandleRef = useRef(null);

  const updateDimensions = () => {
    if (window.matchMedia("(max-width: 1919px)").matches) {
      setPickerDimensions({ width: 200, height: 240 });
      setInitialOffsetX(0.25); // Adjust X offset for smaller screens
      setInitialOffsetY(0.05); // Adjust Y offset for smaller screens
    } else {
      setPickerDimensions({ width: 270, height: 310 });
      setInitialOffsetX(0.25); // Adjust X offset for smaller screens
      setInitialOffsetY(0.05); // Adjust Y offset for smaller screens
    }
  };

  useEffect(() => {
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  const handleMouseDown = (e) => {
    if (dragHandleRef.current && dragHandleRef.current.contains(e.target)) {
      setDragging(true);
      setOffset({
        offsetX: e.clientX - pickerPosition.left,
        offsetY: e.clientY - pickerPosition.top,
      });
      document.body.style.userSelect = "none";
    }
  };

  const calculatePositionFromPercentage = (xPercentage, yPercentage) => {
    const xPos = window.innerWidth * xPercentage;
    const yPos = window.innerHeight * yPercentage;
    return { top: yPos, left: xPos };
  };

  const handleMouseMove = (e) => {
    if (dragging && pickerRef.current) {
      const newLeft = e.clientX - offset.offsetX;
      const newTop = e.clientY - offset.offsetY;

      // Clamp the position to stay within screen bounds
      const clampedLeft = Math.max(0, newLeft);
      const clampedTop = Math.max(0, newTop);

      setPickerPosition({ left: clampedLeft, top: clampedTop });
    }
  };

  const handleMouseUp = () => {
    setDragging(false);
    document.body.style.userSelect = "";
  };

  useEffect(() => {
    if (dragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging]);

  const togglePicker = () => {
    setIsPickerVisible((prev) => {
      if (!prev) {
        const { top, left } = calculatePositionFromPercentage(
          initialOffsetX,
          initialOffsetY,
        );
        setPickerPosition({ top, left });
      }
      return !prev;
    });
  };

  const handleFontClick = (font) => {
    onFontChange({ target: { value: font } }, false); // Immediate change
    onFontChange({ target: { value: font } }, true); // Finalize for history
  };
  const filteredFontFamilies = fontFamilies.filter((font) =>
    font.toLowerCase().includes(searchTerm.toLowerCase()),
  );
  return (
    <div>
      <div onClick={togglePicker} className="font-picker-toggle">
        <span style={{ fontFamily: fontFamily }}>{fontFamily}</span>
      </div>
      {isPickerVisible && (
        <div
          ref={pickerRef}
          style={{
            position: "fixed",
            top: `${pickerPosition.top}px`,
            left: `${pickerPosition.left}px`,
            zIndex: 2,
            padding: "10px",
            backgroundColor: "#252627",
            borderRadius: "8px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            width: pickerDimensions.width,
            height: pickerDimensions.height,
          }}
        >
          <div
            ref={dragHandleRef}
            onMouseDown={handleMouseDown}
            className="font-picker-drag"
            style={{
              width: "100%",
              backgroundColor: "#383838",
              cursor: "move",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
              marginBottom: "10px",
            }}
          />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search fonts..."
            style={{
              width: "100%",
              padding: "5px",
              marginBottom: "10px",
              borderRadius: "5px",
              border: "1px solid #383838",
            }}
          />
          <div
            className="font-picker-scrollable-list"
            style={{
              overflowY: "auto",
              backgroundColor: "#383838",
              borderRadius: "5px",
              padding: "5px",
            }}
          >
            {filteredFontFamilies.map((font) => (
              <div
                key={font}
                onClick={() => handleFontClick(font)}
                style={{
                  padding: "5px 10px",
                  cursor: "pointer",
                  fontFamily: font,
                  backgroundColor:
                    font === fontFamily ? "#4a4a4a" : "transparent",
                  borderRadius: "5px",
                  color: "#fff",
                }}
              >
                {font}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const LayerPanel = ({
  layers,
  onSelectLayer,
  onMoveLayer,
  onRenameLayer,
  onToggleVisibility,
  onLayerHover,
  onLayerHoverEnd,
}) => {
  const [, drop] = useDrop({
    accept: "layer",
    drop: (item, monitor) => {
      const didDrop = monitor.didDrop();
      if (!didDrop && typeof onMoveLayer === "function") {
        onMoveLayer(item.index, item.index, true);
      }
    },
  });

  return (
    <div ref={drop} className="layers-panel">
      {layers.map((layer, index) => (
        <LayerItem
          key={layer.id}
          index={index}
          layer={layer}
          onSelectLayer={onSelectLayer}
          onMoveLayer={onMoveLayer}
          onRenameLayer={onRenameLayer}
          onToggleVisibility={onToggleVisibility}
          onLayerHover={onLayerHover}
          onLayerHoverEnd={onLayerHoverEnd}
        />
      ))}
    </div>
  );
};

const LayerItem = ({
  layer,
  index,
  onSelectLayer,
  onMoveLayer,
  onRenameLayer,
  onToggleVisibility,
  onLayerHover,
  onLayerHoverEnd,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isVisible, setIsVisible] = useState(layer?.isVisible || true);
  const [newName, setNewName] = useState(
    layer.fileName || layer.name || "Unnamed Layer",
  );

  useEffect(() => {
    setNewName(layer?.fileName || layer?.name || "Unnamed Layer");
  }, [layer?.fileName, layer?.name]);

  useEffect(() => {
    setIsVisible(layer?.isVisible || true);
  }, [layer?.isVisible]);

  const truncateName = (name) =>
    name?.length > 12 ? name.slice(0, 12) + "..." : name || "Unnamed";
  const lastMoveTimeRef = useRef(0);
  const MOVE_DELAY = 100;
  const [{ isDragging, offset }, drag, preview] = useDrag({
    type: "layer",
    item: { index, layer },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
      offset: monitor.getClientOffset() || { x: 0, y: 0 },
    }),
  });
  const [, drop] = useDrop({
    accept: "layer",
    hover: (item) => {
      const now = Date.now();
      if (item.index !== index && now - lastMoveTimeRef.current > MOVE_DELAY) {
        onMoveLayer(item.index, index, false); // Pass `false` to prevent saving history
        item.index = index; // Update dragged item index
        lastMoveTimeRef.current = now; // Update the last move time
      }
    },
    drop: (item) => {
      onMoveLayer(item.index, index, true); // Pass `true` to save history
    },
  });

  const handleVisibilityToggle = (e) => {
    e.stopPropagation();
    const newVisibility = !layer.isVisible;
    setIsVisible(newVisibility);
    onToggleVisibility(layer.id, newVisibility); // Update global state
  };

  const handleRename = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleNameChange = (e) => {
    setNewName(e.target.value);
  };

  const handleBlur = () => {
    setIsEditing(false);
    onRenameLayer(layer.id, newName);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      setIsEditing(false);
      onRenameLayer(layer.id, newName);
    }
  };

  const LayerDragPreview = ({ layer, offset }) => {
    const springProps = useSpring({
      transform: `scale(1.05)`,
      opacity: 0.9,
      config: { tension: 250, friction: 20 },
    });
    return (
      <animated.div
        style={{
          ...springProps,
          position: "absolute",
          top: offset.y,
          left: offset.x,
          pointerEvents: "none",
          padding: "5px",
          background: "#fff",
          boxShadow: "0px 5px 15px rgba(0,0,0,0.2)",
          borderRadius: "4px",
        }}
      >
        {truncateName(newName)}
      </animated.div>
    );
  };

  const handleMouseEnter = () => {
    onLayerHover(layer.id); // Tell parent component which layer is being hovered
  };

  const handleMouseLeave = () => {
    onLayerHoverEnd(layer.id); // Tell parent that hover has ended
  };
  return (
    <>
      {isDragging && <LayerDragPreview layer={layer} offset={offset} />}
      <div
        ref={(node) => drag(drop(node))}
        className="layer-item"
        onClick={() => !isEditing && onSelectLayer(layer.id)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          opacity: layer.isVisible ? 1 : 0.5,
          visibility: isDragging ? "hidden" : "visible", // Hide original during drag
        }}
      >
        <div className="layer-item-name">
          {isEditing ? (
            <input
              type="text"
              value={newName}
              onChange={handleNameChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span>{truncateName(newName)}</span>
          )}
        </div>
        <div className="layer-item-buttons-container">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRename(e);
            }}
          >
            <img src="Pencil-Icon-White.png" alt="Edit Layer Name" />
          </button>
          <button onClick={handleVisibilityToggle}>
            <img
              src={layer.isVisible ? "Eye-Icon-ON.png" : "Eye-Icon-OFF.png"}
              alt="Toggle Visibility"
            />
          </button>
        </div>
      </div>
    </>
  );
};

const getHighestZIndex = (layers) => {
  return layers.reduce((max, layer) => Math.max(max, layer.zIndex || 0), 0);
};
const reorderLayersByZIndex = (layers) => {
  return layers.slice().sort((a, b) => a.zIndex - b.zIndex);
};
const reorderZIndex = (items, orderedLayers) => {
  return items.map((item) => {
    const layerIndex = orderedLayers.findIndex((layer) => layer.id === item.id);
    return { ...item, zIndex: orderedLayers.length - layerIndex };
  });
};

const MoveableContainer = React.forwardRef(
  (
    {
      position,
      size,
      rotation,
      onUpdate,
      onClick,
      children,
      isSelected,
      isLayerHovered, // Add this new prop
      zIndex,
      verticalGuidelines = [],
      horizontalGuidelines = [],
      elementGuidelines = [],
      isTextbox = false,
      moveBy = { x: 0, y: 0 },
      resizeBy = { width: 0, height: 0 },
      flip = { scaleX: 1, scaleY: 1 },
      onTransformationChange,
      onArrowKeyNudge,
      onBlur,
      selectedContainer,
      canvasZoom = 1, // ADD THIS LINE
    },
    ref,
  ) => {
    //--------------------------------------------------------------------------
    // REFS & STATE
    //--------------------------------------------------------------------------
    const targetRef = useRef(null);
    const rafRef = useRef(null);
    const moveableRef = useRef(null);
    const canvasAreaRef = useRef(document.querySelector(".three-canvas-area"));
    const resizeApplied = useRef(false);
    const moveApplied = useRef(false);
    
    // This state is the key to the fix. We will change it to force
    // ONLY the Moveable controls to re-mount and re-read the object's style.
    const [moveableKey, setMoveableKey] = useState(0);

    const [shiftKeyPressed, setShiftKeyPressed] = useState(false);
    const [state, setState] = useState({
      position: position,
      size: { width: size.width, height: size.height },
      rotation: rotation,
      scale: [1, 1],
      keepRatio: false,
    });
    const [isEditing, setIsEditing] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isOutsideBounds, setIsOutsideBounds] = useState(false);
    const [isTransforming, setIsTransforming] = useState(false);

    //--------------------------------------------------------------------------
    // UTILITY FUNCTIONS
    //--------------------------------------------------------------------------
    const applyTransformDirectly = (position, size, rotation) => {
      if (targetRef.current) {
        const transformOriginX = size.width / 2;
        const transformOriginY = size.height / 2;
        targetRef.current.style.transformOrigin = `${transformOriginX}px ${transformOriginY}px`;
        targetRef.current.style.transform = `translate(${position.x}px, ${position.y}px) rotate(${rotation}deg) scale(1, 1)`;
        targetRef.current.style.width = `${size.width}px`;
        targetRef.current.style.height = `${size.height}px`;
        const threeJSElement = targetRef.current.querySelector("canvas");
        synchronizeBoundingBoxWithThreeJS(
          targetRef.current,
          threeJSElement,
          rotation,
          size,
          position,
        );
      }
    };

    const scheduleUpdate = useCallback(() => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(() => {
        const { position, size, rotation, scale } = state;
        applyTransformDirectly(position, size, rotation, scale);
      });
    }, [state]);

    const synchronizeBoundingBoxWithThreeJS = (
      boundingBoxElement,
      threeJSElement,
      rotation,
      size,
    ) => {
      if (boundingBoxElement && threeJSElement) {
        const boundingBoxWidth = size.width;
        const boundingBoxHeight = size.height;
        threeJSElement.style.width = `${boundingBoxWidth}px`;
        threeJSElement.style.height = `${boundingBoxHeight}px`;
        const transformOriginX = boundingBoxWidth / 2;
        const transformOriginY = boundingBoxHeight / 2;
        threeJSElement.style.transformOrigin = `${transformOriginX}px ${transformOriginY}px`;
        threeJSElement.style.transform = `scale(1, 1)`;
        const threeJSScene = threeJSElement.__threeRenderer?.scene?.children[0];
        if (threeJSScene) {
          const rotationInRadians = (rotation * Math.PI) / 180;
          threeJSScene.rotation.set(0, rotationInRadians, 0);
          const canvasWidth = threeJSElement.clientWidth;
          const canvasHeight = threeJSElement.clientHeight;
          const scaleX = boundingBoxWidth / canvasWidth;
          const scaleY = boundingBoxHeight / canvasHeight;
          threeJSScene.scale.set(scaleX, scaleY, 1);
          threeJSElement.__threeRenderer.render(
            threeJSElement.__threeRenderer.scene,
            threeJSElement.__threeRenderer.camera,
          );
        }
      }
    };

    const finalizeSync = () => {
      const boundingBoxElement = targetRef.current;
      const threeJSElement = boundingBoxElement.querySelector("canvas");
      if (boundingBoxElement && threeJSElement) {
        synchronizeBoundingBoxWithThreeJS(
          boundingBoxElement,
          threeJSElement,
          state.rotation,
          state.size,
          state.position,
        );
      }
    };

    const updateMoveable = useCallback(() => {
      if (moveableRef.current && targetRef.current) {
        // Force Moveable to recalculate the target element's bounds
        moveableRef.current.updateRect();
        // Add a small delay to ensure DOM has updated
        requestAnimationFrame(() => {
          if (moveableRef.current) {
            moveableRef.current.updateRect();
          }
        });
      }
    }, []);

    const checkIfOutsideBounds = useCallback(() => {
      const targetBounds = targetRef.current?.getBoundingClientRect();
      const canvasBounds = canvasAreaRef.current?.getBoundingClientRect();

      if (targetBounds && canvasBounds) {
        const isCompletelyOutside =
          targetBounds.right < canvasBounds.left + 10 || // completely past the left
          targetBounds.left > canvasBounds.right - 10 || // completely past the right
          targetBounds.bottom < canvasBounds.top + 10 || // completely above
          targetBounds.top > canvasBounds.bottom - 10; // completely below
        setIsOutsideBounds(isCompletelyOutside);
      }
    }, []);

    //--------------------------------------------------------------------------
    // EVENT HANDLERS
    //--------------------------------------------------------------------------
    const handleDoubleClick = () => {
      if (isTextbox) {
        setIsEditing(true);
        setTimeout(() => {
          const fabricCanvas =
            targetRef.current.querySelector("canvas")?.fabricCanvas;
          if (fabricCanvas) {
            const fabricTextBox = fabricCanvas.getObjects("textbox")[0];
            fabricCanvas.setActiveObject(fabricTextBox);
            fabricTextBox.enterEditing();
            fabricCanvas.renderAll();
          }
        }, 0);
      }
    };

    const handleBlur = () => {
      if (isTextbox) {
        setIsEditing(false);
        const fabricCanvas =
          targetRef.current.querySelector("canvas")?.fabricCanvas;
        if (fabricCanvas) {
          const fabricTextBox = fabricCanvas.getObjects("textbox")[0];
          fabricTextBox.exitEditing();
          fabricCanvas.discardActiveObject();
          fabricCanvas.renderAll();
          if (onBlur) {
            onBlur(selectedContainer, fabricTextBox.text);
          }
        }
      }
    };

    const handleClickOutside = (event) => {
      const fabricCanvas =
        targetRef.current.querySelector("canvas")?.fabricCanvas;
      const fabricTextBox = fabricCanvas?.getObjects("textbox")[0];
      if (
        targetRef.current &&
        !targetRef.current.contains(event.target) && // Click outside MoveableContainer
        fabricTextBox &&
        fabricTextBox.isEditing // Fabric.js text editor is in editing mode
      ) {
        handleBlur();
      }
    };

    const handleDrag = ({ beforeTranslate }) => {
      const newPosition = { x: beforeTranslate[0], y: beforeTranslate[1] };
      applyTransformDirectly(
        newPosition,
        state.size,
        state.rotation,
        state.scale,
      );
      setState((prevState) => ({ ...prevState, position: newPosition }));
    };

    // Create a reference to store the animation frame ID
    const rafIdRef = useRef(null);

    const handleResize = ({ width, height, drag: { beforeTranslate } }) => {
      const newPosition = { x: beforeTranslate[0], y: beforeTranslate[1] };
      const newSize = { width, height };

      // Apply the transform to the outer container immediately
      applyTransformDirectly(newPosition, newSize, state.rotation, state.scale);
      setState((prevState) => ({
        ...prevState,
        size: newSize,
        position: newPosition,
      }));

      // Cancel any existing animation frame to prevent multiple updates
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }

      // Schedule the Fabric update on the next animation frame
      rafIdRef.current = requestAnimationFrame(() => {
        const fabricCanvas =
          targetRef.current.querySelector("canvas")?.fabricCanvas;
        if (fabricCanvas) {
          const fabricTextBox = fabricCanvas.getObjects("textbox")[0];
          if (fabricTextBox) {
            // Store current text alignment and other important properties
            const textAlign = fabricTextBox.textAlign;
            const verticalAlign = fabricTextBox.top / fabricCanvas.height; // Calculate relative position

            // Update canvas dimensions
            fabricCanvas.setWidth(newSize.width);
            fabricCanvas.setHeight(newSize.height);

            // Update textbox width but NOT height to prevent vertical jitter
            fabricTextBox.set({
              width: newSize.width,
              // Do NOT update the height here - this prevents vertical jumping
            });

            // Maintain the same vertical positioning based on alignment
            if (
              textAlign === "left" ||
              textAlign === "center" ||
              textAlign === "right"
            ) {
              // Keep horizontal alignment as is - Fabric handles this automatically
            }

            // Re-apply vertical position based on the stored relative position
            fabricTextBox.set({
              top: verticalAlign * newSize.height,
            });

            // Render without causing a full layout recalculation
            fabricCanvas.renderAll();
          }
        }
      });
    };

    const handleRotate = ({ beforeRotate }) => {
      if (shiftKeyPressed) {
        beforeRotate = Math.round(beforeRotate / 15) * 15;
      }
      applyTransformDirectly(
        state.position,
        state.size,
        beforeRotate,
        state.scale,
      );
      setState((prevState) => ({ ...prevState, rotation: beforeRotate }));
    };

    const handleScale = ({ drag: { beforeTranslate }, scale }) => {
      const newPosition = { x: beforeTranslate[0], y: beforeTranslate[1] };
      applyTransformDirectly(newPosition, state.size, state.rotation, scale);
      setState((prevState) => ({ ...prevState, scale, position: newPosition }));
    };

    const handleResizeStart = ({ direction }) => {
      setIsTransforming(true);
      const isCornerHandle = direction[0] !== 0 && direction[1] !== 0;
      setState((prevState) => ({ ...prevState, keepRatio: isCornerHandle }));
    };

    const handleResizeEnd = (newSize) => {
      setIsTransforming(false);
      setTimeout(() => {
        finalizeSync();
      }, 100);
      onTransformationChange("resize", newSize);
    };

    const handleRotateEnd = (newRotation) => {
      setIsTransforming(false);
      finalizeSync();
      onTransformationChange("rotate", newRotation);
    };

    const handleDragEnd = (newPosition) => {
      setIsTransforming(false);
      finalizeSync();
      onTransformationChange("drag", newPosition);
    };

    //--------------------------------------------------------------------------
    // EFFECTS - INITIALIZATION
    //--------------------------------------------------------------------------
    useEffect(() => {
      canvasAreaRef.current = document.querySelector(".three-canvas-area");
    }, []);

    useEffect(() => {
      const handleKeyDown = (e) => {
        if (e.key === "Shift") {
          setShiftKeyPressed(true);
        }
      };

      const handleKeyUp = (e) => {
        if (e.key === "Shift") {
          setShiftKeyPressed(false);
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      window.addEventListener("keyup", handleKeyUp);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
        window.removeEventListener("keyup", handleKeyUp);
      };
    }, []);

    useEffect(() => {
      finalizeSync(); // Force sync on mount
    }, []);

    //--------------------------------------------------------------------------
    // EFFECTS - UPDATES & SYNCHRONIZATION
    //--------------------------------------------------------------------------
    useEffect(() => {
      checkIfOutsideBounds();
    }, [state.position, state.size, checkIfOutsideBounds]);

    useEffect(() => {
      if (
        !resizeApplied.current &&
        (resizeBy.width !== 0 || resizeBy.height !== 0)
      ) {
        const newSize = {
          width: state.size.width + resizeBy.width,
          height: state.size.height + resizeBy.height,
        };
        setState((prevState) => ({ ...prevState, size: newSize }));

        const fabricCanvas =
          targetRef.current.querySelector("canvas")?.fabricCanvas;
        if (fabricCanvas) {
          const fabricTextBox = fabricCanvas.getObjects("textbox")[0];
          if (fabricTextBox) {
            fabricTextBox.set({ width: newSize.width, height: newSize.height });
            fabricCanvas.setWidth(newSize.width);
            fabricCanvas.setHeight(newSize.height);
            fabricCanvas.renderAll();
          }
        }
        onUpdate({ size: newSize });
        resizeApplied.current = true;
      }
    }, [resizeBy.width, resizeBy.height, state.size.width, state.size.height]);

    useEffect(() => {
      if (resizeBy.width === 0 && resizeBy.height === 0) {
        resizeApplied.current = false; // Allow resizing again
      }
    }, [resizeBy]);

    useEffect(() => {
      scheduleUpdate();
    }, [scheduleUpdate]);

    useEffect(() => {
      if (onUpdate) {
        onUpdate(state);
      }
    }, [
      state.position.x,
      state.position.y,
      state.size.width,
      state.size.height,
      state.rotation,
    ]);

    useEffect(() => {
      const handleArrowKeyPress = (e) => {
        // 1) Check if user is on an input/textarea/select/etc.
        const active = document.activeElement;
        const isFormElement =
          active && ["INPUT", "TEXTAREA", "SELECT"].includes(active.tagName);

        // 2) If we're on a form element, don't nudge the selected object
        if (isFormElement) return;

        // 3) Proceed with your existing logic otherwise
        if (!isSelected) return;
        const moveDistance = e.shiftKey ? 10 : 1;
        let newPosition = { ...position };

        switch (e.key) {
          case "ArrowUp":
            newPosition.y -= moveDistance;
            break;
          case "ArrowDown":
            newPosition.y += moveDistance;
            break;
          case "ArrowLeft":
            newPosition.x -= moveDistance;
            break;
          case "ArrowRight":
            newPosition.x += moveDistance;
            break;
          default:
            return;
        }

        onArrowKeyNudge(newPosition);
        applyTransformDirectly(newPosition, state.size, state.rotation);
        setState((prevState) => ({ ...prevState, position: newPosition }));
        onUpdate({ position: newPosition });
      };

      window.addEventListener("keydown", handleArrowKeyPress);
      return () => {
        window.removeEventListener("keydown", handleArrowKeyPress);
      };
    }, [
      isSelected,
      position,
      onArrowKeyNudge,
      state.size,
      state.rotation,
      onUpdate,
    ]);

    useEffect(() => {
      if (isEditing) {
        document.addEventListener("mousedown", handleClickOutside);
      } else {
        document.removeEventListener("mousedown", handleClickOutside);
      }
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isEditing]);

    useEffect(() => {
      if (!isEditing) {
        if (targetRef.current) {
          targetRef.current.style.cursor = "move";
        }
      }
    }, [isEditing]);

    useEffect(() => {
      updateMoveable();
    }, [state.position, state.size, state.rotation, updateMoveable]);

    // Force update when component is first mounted (for newly created shapes)
    useEffect(() => {
      const timer = setTimeout(() => {
        updateMoveable();
      }, 50); // Small delay to ensure DOM is ready

      return () => clearTimeout(timer);
    }, [updateMoveable]);

    // Additional fix: Force update when isSelected changes (critical for new shapes)
    useEffect(() => {
      if (isSelected && targetRef.current) {
        // Multiple update attempts to ensure Moveable detects the element
        requestAnimationFrame(() => {
          updateMoveable();
          requestAnimationFrame(() => {
            updateMoveable();
          });
        });
      }
    }, [isSelected, updateMoveable]);

    // Critical fix: Force update when canvasZoom changes (fixes shape bounding box issue)
    useEffect(() => {
      if (targetRef.current && moveableRef.current) {
        // Force recalculation when zoom changes
        const timer = setTimeout(() => {
          updateMoveable();
          // Double update to ensure proper positioning
          requestAnimationFrame(() => {
            updateMoveable();
          });
        }, 100);

        return () => clearTimeout(timer);
      }
    }, [canvasZoom, updateMoveable]);

    // FIX: This hook watches for external changes from buttons/inputs
    useEffect(() => {
      // We only force a reset if the component is NOT currently being dragged/resized.
      // This prevents the interaction from being broken.
      if (!isTransforming) {
        setMoveableKey(c => c + 1);
      }
    }, [rotation, position, size, isTransforming]); // Watch all transform props

    useEffect(() => {
      if (!moveApplied.current && (moveBy.x !== 0 || moveBy.y !== 0)) {
        const newPosition = {
          x: state.position.x + moveBy.x,
          y: state.position.y + moveBy.y,
        };
        setState((prevState) => ({ ...prevState, position: newPosition }));
        onUpdate({ position: newPosition });
        moveApplied.current = true;
      }
    }, [moveBy.x, moveBy.y, state.position.x, state.position.y]);

    useEffect(() => {
      if (moveBy.x === 0 && moveBy.y === 0) {
        moveApplied.current = false;
      }
    }, [moveBy]);

    // Sync internal state with props from parent (fixes rotation updates)
    useEffect(() => {
      // Only sync with parent props if the user is not actively transforming the object
      if (
        !isTransforming &&
        position &&
        size &&
        typeof rotation !== "undefined"
      ) {
        setState((prevState) => ({
          ...prevState,
          position,
          size,
          rotation,
        }));
      }
    }, [position, size, rotation, isTransforming]);

    // Sync external rotation changes to internal visual state (critical fix for 90-degree rotation)
    useEffect(() => {
      if (Math.abs(rotation - state.rotation) > 0.1) {
        setState(prevState => ({
          ...prevState,
          rotation: rotation  // Update the visual rotation
        }));
      }
    }, [rotation, state.rotation]);

    //--------------------------------------------------------------------------
    // RENDER
    //--------------------------------------------------------------------------
    return (
      <>
        <div
          className="canvas-container"
          style={{
            width: "100%",
            height: "100%",
            overflow: isOutsideBounds ? "visible" : "hidden",
            position: "absolute",
          }}
        >
          <div
            ref={(el) => {
              targetRef.current = el;
              if (ref) ref.current = el;
            }}
            style={{
              position: "absolute",
              transform: `translate(${state.position.x}px, ${state.position.y}px) rotate(${state.rotation}deg) scale(${state.scale[0]}, ${state.scale[1]})`,
              width: `${state.size.width}px`,
              height: `${state.size.height}px`,
              zIndex: zIndex,
              userSelect: isEditing ? "text" : "none",
              cursor: isEditing ? "text" : "move",
              willChange: "transform", // Add this line
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onMouseDown={(e) => {
              if (!isEditing) {
                e.stopPropagation();
                if (onClick) onClick();
              }
            }}
            onDoubleClick={handleDoubleClick}
            onBlur={handleBlur}
          >
            <div
              style={{
                transform: `scale(${flip.scaleX}, ${flip.scaleY})`,
                width: `${state.size.width}px`,
                height: `${state.size.height}px`,
                overflow: "hidden",
              }}
            >
              {children}
            </div>
          </div>
        </div>
        {(isSelected || isHovered || isLayerHovered) && targetRef.current && (
          <Moveable
            key={moveableKey} // We apply the special key HERE.
            ref={moveableRef}
            target={targetRef.current}
            draggable={!isEditing}
            resizable={!isEditing}
            rotatable={!isEditing}
            scalable={!isEditing}
            keepRatio={state.keepRatio}
            snappable={true}
            snapThreshold={10}
            snapCenter={false}
            snapHorizontal={true}
            snapVertical={true}
            snapDirections={{
              top: true,
              left: true,
              bottom: true,
              right: true,
              center: true,
              middle: true,
            }}
            elementSnapDirections={{
              top: true,
              left: true,
              bottom: true,
              right: true,
              center: true,
              middle: true,
            }}
            maxSnapElementGuidelineDistance={500}
            elementGuidelines={elementGuidelines}
            verticalGuidelines={verticalGuidelines}
            horizontalGuidelines={horizontalGuidelines}
            onDragStart={() => setIsTransforming(true)}
            onRotateStart={() => setIsTransforming(true)}
            onResizeStart={handleResizeStart}
            onResize={handleResize}
            onDrag={handleDrag}
            onRotate={handleRotate}
            onScale={handleScale}
            renderDirections={["nw", "n", "ne", "w", "e", "sw", "s", "se"]}
            onResizeEnd={({ lastEvent: { width, height } }) =>
              handleResizeEnd({ width, height })
            }
            onRotateEnd={({ lastEvent: { rotation } }) =>
              handleRotateEnd(rotation)
            }
            onDragEnd={({ lastEvent }) => {
              if (lastEvent && lastEvent.beforeTranslate) {
                // Use beforeTranslate to match the coordinates from the onDrag event
                const finalPosition = {
                  x: lastEvent.beforeTranslate[0],
                  y: lastEvent.beforeTranslate[1],
                };
                handleDragEnd(finalPosition);
              }
            }}
            rotationPosition={"top"}
            throttleDrag={0}
            throttleResize={0}
            throttleRotate={0}
            startDragRotate={0}
            throttleDragRotate={0}
            zoom={canvasZoom ? 1 / canvasZoom : 1}
          />
        )}
      </>
    );
  },
);

const ThreeCanvas = () => {
  //=============================================================================
  // HOOKS AND SERVICES
  //=============================================================================

  // Router hooks
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location || {};
  const {
    canvasSize: initialCanvasSize,
    zoom: initialZoom,
    canvasId: existingCanvasId,
  } = state || {};
  const savedState = location.state?.savedState;

  // Authentication hooks
  const { currentUser } = useAuth();
  const userId = currentUser?.uid;

  // Idle timer hook
  useIdleTimer(3600000); // 1 hour

  //=============================================================================
  // STATE DECLARATIONS
  //=============================================================================

  // Canvas Configuration State
  const [canvasBackgroundColor, setCanvasBackgroundColor] = useState({
    r: 255,
    g: 255,
    b: 255,
    a: 1,
  });
  const [canvasSize, setCanvasSize] = useState(
    initialCanvasSize || { width: "1080px", height: "1080px" },
  );
  const [zoom, setZoom] = useState(initialZoom || null);
  const [canvasId, setCanvasId] = useState(existingCanvasId || null);
  const [canvasKey, setCanvasKey] = useState(Date.now());
  const [fileName, setFileName] = useState("File Name");
  const [exportFormat, setExportFormat] = useState("png");
  const [transparentBg, setTransparentBg] = useState(false);

  // Canvas Elements State
  const [bgFiles, setBgFiles] = useState([]);
  const [imageButtons, setImageButtons] = useState([
    {
      id: "1",
      name: "Shadow 1",
      thumbnailSrc: "Shadow-Icon-1.png",
      fileSrc: "Shadow-1.png",
    },
    {
      id: "2",
      name: "Shadow 2",
      thumbnailSrc: "Shadow-Icon-2.png",
      fileSrc: "Shadow-2.png",
    },
    {
      id: "3",
      name: "Shadow 3",
      thumbnailSrc: "Shadow-Icon-3.png",
      fileSrc: "Shadow-3.png",
    },
    {
      id: "4",
      name: "Shadow 4",
      thumbnailSrc: "Shadow-Icon-4.png",
      fileSrc: "Shadow-4.png",
    },
    {
      id: "5",
      name: "Shadow 5",
      thumbnailSrc: "Shadow-Icon-5.png",
      fileSrc: "Shadow-5.png",
    },
    {
      id: "6",
      name: "Shadow 6",
      thumbnailSrc: "Shadow-Icon-6.png",
      fileSrc: "Shadow-6.png",
    },
    {
      id: "7",
      name: "Shadow 7",
      thumbnailSrc: "Shadow-Icon-7.png",
      fileSrc: "Shadow-7.png",
    },
    {
      id: "8",
      name: "Shadow 8",
      thumbnailSrc: "Shadow-Icon-8.png",
      fileSrc: "Shadow-8.png",
    },
    {
      id: "9",
      name: "Shadow 9",
      thumbnailSrc: "Shadow-Icon-9.png",
      fileSrc: "Shadow-9.png",
    },
    {
      id: "10",
      name: "Shadow 10",
      thumbnailSrc: "Shadow-Icon-10.png",
      fileSrc: "Shadow-10.png",
    },
  ]);
  const [modelFiles, setModelFiles] = useState([]);
  const [textBoxes, setTextBoxes] = useState([]);
  const [shapes, setShapes] = useState([]);
  const [layers, setLayers] = useState([]);

  // Style and Selection State
  const [selectedContainer, setSelectedContainer] = useState(null);
  const [activeSidebar, setActiveSidebar] = useState(1);
  const [fontSize, setFontSize] = useState("25px");
  const [fontFamily, setFontFamily] = useState("Poppins");
  const [selectedFontWeight, setSelectedFontWeight] = useState("400");
  const [fontColor, setFontColor] = useState("rgba(0, 0, 0, 1)");
  const [textOpacity, setTextOpacity] = useState(1);
  const [shapeColor, setShapeColor] = useState("#CBCBCB");
  const [shapeOpacity, setShapeOpacity] = useState(1);

  // Guidelines State
  const [verticalGuidelines, setVerticalGuidelines] = useState([]);
  const [horizontalGuidelines, setHorizontalGuidelines] = useState([]);

  // Transformation State
  const [controlsState, setControlsState] = useState(null);
  const [moveBy, setMoveBy] = useState({ x: 0, y: 0 });
  const [resizeBy, setResizeBy] = useState({ width: 0, height: 0 });
  const [isLinked, setIsLinked] = useState(false);
  const [shiftKeyPressed, setShiftKeyPressed] = useState(false);
  const [isRotationUpdating, setIsRotationUpdating] = useState(false);

  // Interface State
  const [isSliderShifted, setIsSliderShifted] = useState(false);
  const [saveIndicatorVisible, setSaveIndicatorVisible] = useState(false);

  // Loading State
  const [isUploadingBg, setIsUploadingBg] = useState(false);
  const [isUploadingModel, setIsUploadingModel] = useState(false);
  const [isLoadingCanvas, setIsLoadingCanvas] = useState(false);
  const [isSavingCanvas, setIsSavingCanvas] = useState(false);

  // History State
  const [history, setHistory] = useState({
    past: [],
    present: {
      bgFiles: [],
      modelFiles: [],
      textBoxes: [],
      shapes: [],
      layers: [],
      canvasBackgroundColor: { r: 255, g: 255, b: 255, a: 1 },
    },
    future: [],
  });
  const [savedStates, setSavedStates] = useState([]);

  // Modals and Indicators State
  const [showControlsIndicator, setShowControlsIndicator] = useState(false);
  const [showInteractiveModal, setShowInteractiveModal] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [isFirstSaveDone, setIsFirstSaveDone] = useState(false);
  const [showCanvasLimitModal, setShowCanvasLimitModal] = useState(false);
  const [exportCount, setExportCount] = useState(0);
  const [showExportLimitModal, setShowExportLimitModal] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [usageModalMessage, setUsageModalMessage] = useState("");
  const [showPromptRequiredModal, setShowPromptRequiredModal] = useState(false);
  const [showStorageLimitModal, setShowStorageLimitModal] = useState(false);
  const [blockedFileSize, setBlockedFileSize] = useState(null);

  // Assets and Media Library State
  const [assets, setAssets] = useState([]);
  const [assetsSearchTerm, setAssetsSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);

  // Subscription State
  const [subscriptionPlan, setSubscriptionPlan] = useState(null);

  // Export Usage State
  const [exportUsageStats, setExportUsageStats] = useState({
    currentCount: 0,
    monthlyLimit: 0,
    remaining: 0,
  });

  // Drag and Drop State
  const [hoveredLayerId, setHoveredLayerId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);

  //=============================================================================
  // CONSTANTS
  //=============================================================================
  const LINKED_BUTTON_STYLE = {
    active: { backgroundColor: "#8729CF", border: "none" },
    inactive: { border: "none" },
  };

  const defaultState = {
    bgFiles: [],
    modelFiles: [],
    textBoxes: [],
    shapes: [],
    layers: [],
    canvasBackgroundColor: { r: 255, g: 255, b: 255, a: 1 },
  };

  const MAX_EXPORTS_ESSENTIAL = 10;
  const MAX_EXPORTS_PRO = 75;
  const allowedExports =
    subscriptionPlan === "pro" ? MAX_EXPORTS_PRO : MAX_EXPORTS_ESSENTIAL;

  // Canvas limits will be fetched from profile document
  const [maxCanvases, setMaxCanvases] = useState(20);

  //=============================================================================
  // REFS
  //=============================================================================

  const moveableRefs = useRef([]);
  const canvasRef = useRef();
  const bgInputRef = useRef();
  const modelInputRef = useRef();
  const captureDivRef = useRef();
  const canvasScaleRef = useRef(1);
  const sidebarRef1 = useRef();
  const sidebarRef2 = useRef();
  const sidebarRef3 = useRef();
  const sidebarRef4 = useRef();
  const sidebarRef5 = useRef();
  const sidebarRef6 = useRef();
  const sidebarRef7 = useRef();
  const isInitialized = useRef(false);
  const isCanvasInitialized = useRef(false);
  const handleSaveRef = useRef();

  //=============================================================================
  // MODAL COMPONENTS
  //=============================================================================

  const UsageLimitModal = ({ onClose, message }) => {
    return (
      <div className="modal-overlay-dashboard">
        <div className="modal-content-dashboard">
          <h2>Token Limit Reached</h2>
          <p>{message}</p>
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    );
  };

  const PromptRequiredModal = ({ onClose }) => {
    return (
      <div className="modal-overlay-dashboard">
        <div className="modal-content-dashboard">
          <h2>Prompt Required</h2>
          <p>Please enter a prompt before generating content.</p>
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    );
  };

  //=============================================================================
  // API AND AUTHENTICATION HELPERS
  //=============================================================================

  // Fetch all usage stats (including export) from backend
  const fetchAllUsageStats = async () => {
    try {
      if (!currentUser) return;
      const idToken = await currentUser.getIdToken();

      // Validate token to prevent 'match' error
      if (!idToken || typeof idToken !== "string" || idToken.trim() === "") {
        throw new Error("Failed to obtain valid authentication token");
      }

      const response = await fetch("/api/ai/usage-stats", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setExportUsageStats({
          currentCount: data.exportUsage?.currentCount || 0,
          monthlyLimit: data.exportUsage?.monthlyLimit || 0,
          remaining: data.exportUsage?.remaining || 0,
        });

      }
    } catch (error) {

    }
  };

  // Authentication helpers - using Firebase Authentication

  // User and subscription API with Firestore
  async function fetchUserSubscriptionPlan() {
    if (!currentUser) return null;

    try {
      const { getUserSubscriptionPlan } = await import(
        "../../services/firestoreService"
      );
      return await getUserSubscriptionPlan(currentUser.uid);
    } catch (error) {

      return null;
    }
  }

  async function fetchUserSettings(userId) {
    try {
      const { getUserSettings } = await import(
        "../../services/firestoreService"
      );
      return await getUserSettings(userId);
    } catch (error) {

      return { dontShowInteractive: false };
    }
  }

  async function upsertUserSettings(userId, userSettings) {
    try {
      const { updateUserSettings } = await import(
        "../../services/firestoreService"
      );
      await updateUserSettings(userId, userSettings);
      return { success: true };
    } catch (error) {

      throw new Error("Failed to upsert user settings");
    }
  }

  // Canvas and Assets API

  async function fetchUserAssets(userId) {
    // Get auth token for API call
    const auth = await getFirebaseAuth();
    if (!auth.currentUser) {
      throw new Error("User not authenticated - no current user");
    }

    // Force token refresh to ensure we have a valid token
    const token = await auth.currentUser.getIdToken(true);

    // Add safeguard to prevent 'match' error - validate token before sending
    if (!token || typeof token !== "string" || token.trim() === "") {
      throw new Error("Failed to obtain valid authentication token");
    }

    // Use appropriate API URL based on environment
    const apiUrl = import.meta.env.PROD 
      ? "https://youcreate-app-498148088233.australia-southeast2.run.app/api/list-assets"
      : `${window.location.protocol}//${window.location.host}/api/list-assets`;

    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userId }),
    });
    if (!response.ok) {
      throw new Error("Failed to fetch assets");
    }
    const data = await response.json();
    return data.assets || [];
  }

  async function uploadFileToServer(file, userId, canvasId, folder) {
    try {
      // Convert file to base64
      const fileData = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Upload via server endpoint
      const response = await fetch("/api/upload-asset", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fileData,
          fileName: file.name,
          fileType: file.type,
          folder: folder || "images",
          userId: userId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }

      const data = await response.json();
      if (!data.filename) {
        throw new Error("File upload did not return a valid filename");
      }

      // Return the raw file path, not the full URL
      return data.filename;
    } catch (error) {

      throw new Error(`Upload failed: ${error.message}`);
    }
  }

  //=============================================================================
  // FILE AND IMAGE UTILITIES
  //=============================================================================

  async function generateImagePreview(imagePath) {
    return new Promise((resolve, reject) => {
      if (!imagePath) {
        return reject("No image path provided for preview generation");
      }
      const tempCanvas = document.createElement("canvas");
      const ctx = tempCanvas.getContext("2d");

      const img = new Image();
      img.crossOrigin = "anonymous";
      // imagePath is now a direct Firebase Storage URL
      img.src = imagePath;

      img.onload = () => {
        // Let's cap it at 800×800 max, preserving aspect ratio
        const MAX_WIDTH = 800;
        const MAX_HEIGHT = 800;
        let { width, height } = img;
        if (width > MAX_WIDTH || height > MAX_HEIGHT) {
          const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        tempCanvas.width = width;
        tempCanvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to WebP at ~90% quality
        const dataUrl = tempCanvas.toDataURL("image/webp", 0.9);
        resolve(dataUrl);
      };

      img.onerror = (err) => {
        reject(err);
      };
    });
  }

  async function fileToDataURL(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  }

  async function fetchUrlToDataURL(url) {
    const resp = await fetch(url);
    if (!resp.ok) {
      throw new Error(`Failed to fetch image from ${url}`);
    }
    const blob = await resp.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  async function applyLocalExposure(base64Data, exposure) {
    // base64Data is the "original" unmodified image
    // 1) Make an offscreen <canvas>:
    const img = new Image();
    img.src = base64Data; // always start from the original
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });

    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);

    // 2) Pixel loop for brightness
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      data[i] = Math.min(data[i] * exposure, 255); // R
      data[i + 1] = Math.min(data[i + 1] * exposure, 255); // G
      data[i + 2] = Math.min(data[i + 2] * exposure, 255); // B
      // alpha (data[i+3]) is unchanged
    }
    ctx.putImageData(imageData, 0, 0);

    // 3) Convert to a new base64 data URL
    return canvas.toDataURL();
  }

  function dataURLtoBlob(dataurl) {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  function calculateBoundingBoxDimensions(width, height, rotationAngle) {
    const radians = (rotationAngle * Math.PI) / 180;
    const rotatedWidth =
      Math.abs(width * Math.cos(radians)) +
      Math.abs(height * Math.sin(radians));
    const rotatedHeight =
      Math.abs(width * Math.sin(radians)) +
      Math.abs(height * Math.cos(radians));
    return { rotatedWidth, rotatedHeight };
  }

  // Font utilities
  function loadFontWithWeight(fontFamily, weight) {
    return new Promise((resolve, reject) => {
      WebFont.load({
        google: {
          // IMPORTANT: the "families" array must include "FontName:weights" or "FontName:100,400,600"
          families: [`${fontFamily}:${weight}`],
        },
        active: resolve, // Fired when the font is fully loaded
        inactive: reject, // Fired if the font can't be loaded
      });
    });
  }

  function truncateAssetName(name, maxLength = 12) {
    if (!name) return "";
    return name.length <= maxLength ? name : name.slice(0, maxLength) + "...";
  }

  // Asset management utilities
  async function findDuplicateAsset(file, assetsList) {
    const fileHash = await computeFileHash(file);
    // Return the asset if its fileHash matches; otherwise, return null.
    return assetsList.find((asset) => asset.fileHash === fileHash) || null;
  }

  async function computeFileHash(file) {
    // Read the file as an ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();

    // Use the SubtleCrypto API (in modern browsers)
    const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);

    // Convert ArrayBuffer to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    return hashHex;
  }

  async function validate3DModel(url) {
    try {
      const response = await fetch(url, { method: "GET" });
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  //=============================================================================
  // CANVAS UTILITY FUNCTIONS
  //=============================================================================

  const parseRGBString = (rgbaString) => {
    const result = /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d*\.?\d+))?\)/.exec(
      rgbaString,
    );
    return result
      ? {
          r: parseInt(result[1], 10),
          g: parseInt(result[2], 10),
          b: parseInt(result[3], 10),
          a: result[4] !== undefined ? parseFloat(result[4]) : 1, // Default alpha to 1
        }
      : null;
  };

  const getCanvasCenterPosition = () => {
    const canvasWidth = parseInt(canvasSize.width, 10);
    const canvasHeight = parseInt(canvasSize.height, 10);
    return {
      x: canvasWidth / 2,
      y: canvasHeight / 2,
    };
  };

  const getSelectedObject = () => {
    // Logic to get the currently selected object (shape, textBox, etc.)
    return (
      shapes.find((shape) => shape.id === selectedContainer) ||
      textBoxes.find((textBox) => textBox.id === selectedContainer) ||
      bgFiles.find((bg) => bg.id === selectedContainer) ||
      modelFiles.find((model) => model.id === selectedContainer)
    );
  };

  const getElementGuidelines = () => {
    return moveableRefs.current
      .map((ref) => ref.current)
      .filter((ref) => ref !== null);
  };

  const updateGuidelines = useCallback(() => {
    const canvasWidth = parseInt(canvasSize.width, 10);
    const canvasHeight = parseInt(canvasSize.height, 10);
    setVerticalGuidelines([0, canvasWidth / 2, canvasWidth]);
    setHorizontalGuidelines([0, canvasHeight / 2, canvasHeight]);
  }, [canvasSize]);

  function getClickedObjectId(x, y) {
    // Gather everything that can be clicked
    const allObjects = [...bgFiles, ...modelFiles, ...textBoxes, ...shapes];

    // Loop from front to back (or back to front,
    // depending on how you prefer to handle stacking)
    for (const obj of allObjects) {
      if (!obj.position || !obj.size) continue; // no bounding box => skip

      const { position, size } = obj;
      const withinX = x >= position.x && x <= position.x + size.width;
      const withinY = y >= position.y && y <= position.y + size.height;
      if (withinX && withinY) {
        return obj.id; // Return the FIRST object's ID you find
      }
    }
    return null; // No hit
  }

  //=============================================================================
  // HISTORY AND STATE MANAGEMENT
  //=============================================================================

  const saveHistory = (newState) => {
    setHistory((prev) => ({
      past: [...prev.past, prev.present],
      present: newState,
      future: [],
    }));
  };

  const updateState = (updatedState) => {
    saveHistory({
      shapes: updatedState.shapes || shapes,
      layers: updatedState.layers || layers,
      bgFiles: updatedState.bgFiles || bgFiles,
      modelFiles: updatedState.modelFiles || modelFiles,
      textBoxes: updatedState.textBoxes || textBoxes,
      canvasBackgroundColor:
        updatedState.canvasBackgroundColor || canvasBackgroundColor,
    });
  };

  const handleUndo = () => {
    setHistory((prev) => {
      if (prev.past.length === 0) return prev;
      const newPresent = prev.past[prev.past.length - 1];
      const newPast = prev.past.slice(0, -1);
      setShapes(newPresent.shapes);
      setLayers(newPresent.layers);
      setBgFiles(newPresent.bgFiles);
      setModelFiles(newPresent.modelFiles);
      setTextBoxes(newPresent.textBoxes);
      setCanvasKey(Date.now());
      return {
        past: newPast,
        present: newPresent,
        future: [prev.present, ...prev.future],
      };
    });
  };

  const handleRedo = () => {
    setHistory((prev) => {
      if (prev.future.length === 0) return prev;
      const newPresent = prev.future[0];
      const newFuture = prev.future.slice(1);
      setShapes(newPresent.shapes);
      setLayers(newPresent.layers);
      setBgFiles(newPresent.bgFiles);
      setModelFiles(newPresent.modelFiles);
      setTextBoxes(newPresent.textBoxes);
      setCanvasKey(Date.now());
      return {
        past: [...prev.past, prev.present],
        present: newPresent,
        future: newFuture,
      };
    });
  };

  //=============================================================================
  // LAYER MANAGEMENT FUNCTIONS
  //=============================================================================

  const handleLayerHover = (layerId) => {
    setHoveredLayerId(layerId);
  };

  const handleLayerHoverEnd = () => {
    setHoveredLayerId(null);
  };

  const handleSelectLayer = (id) => {
    setSelectedContainer(id);
    handleObjectClick(id);
  };

  const handleRenameLayer = (id, newName) => {
    // Update the layers array
    const updatedLayers = layers.map((layer) =>
      layer.id === id ? { ...layer, name: newName } : layer,
    );

    // Update the shapes array if the layer is a shape
    const updatedShapes = shapes.map((shape) =>
      shape.id === id ? { ...shape, name: newName } : shape,
    );

    // Update other arrays (e.g., bgFiles, modelFiles, textBoxes) if needed
    const updatedBgFiles = bgFiles.map((file) =>
      file.id === id ? { ...file, name: newName } : file,
    );

    const updatedModelFiles = modelFiles.map((model) =>
      model.id === id ? { ...model, name: newName } : model,
    );

    const updatedTextBoxes = textBoxes.map((textBox) =>
      textBox.id === id ? { ...textBox, name: newName } : textBox,
    );

    // Replace the entire arrays to trigger a re-render
    setLayers([...updatedLayers]);
    setShapes([...updatedShapes]);
    setBgFiles([...updatedBgFiles]);
    setModelFiles([...updatedModelFiles]);
    setTextBoxes([...updatedTextBoxes]);

    // Save the updated state to history
    updateState({
      layers: updatedLayers,
      bgFiles: updatedBgFiles,
      modelFiles: updatedModelFiles,
      textBoxes: updatedTextBoxes,
      shapes: updatedShapes,
    });
  };

  const handleMoveLayer = (fromIndex, toIndex, saveToHistory) => {
    const updatedLayers = [...layers];
    const [movedLayer] = updatedLayers.splice(fromIndex, 1);
    updatedLayers.splice(toIndex, 0, movedLayer);

    // Reorder zIndex for all affected items
    const reorderedBgFiles = reorderZIndex(bgFiles, updatedLayers);
    const reorderedModelFiles = reorderZIndex(modelFiles, updatedLayers);
    const reorderedTextBoxes = reorderZIndex(textBoxes, updatedLayers);
    const reorderedShapes = reorderZIndex(shapes, updatedLayers);

    // Update state
    setLayers(updatedLayers);
    setBgFiles(reorderedBgFiles);
    setModelFiles(reorderedModelFiles);
    setTextBoxes(reorderedTextBoxes);
    setShapes(reorderedShapes);

    // Save the updated state to history only if requested
    if (saveToHistory) {
      updateState({
        layers: updatedLayers,
        bgFiles: reorderedBgFiles,
        modelFiles: reorderedModelFiles,
        textBoxes: reorderedTextBoxes,
        shapes: reorderedShapes,
      });
    }
  };

  const handleToggleVisibility = (id, isVisible) => {
    const updateVisibility = (items, setItems) => {
      const updatedItems = items.map((item) => {
        if (item.id === id) {
          const latestPosition = item.position;
          const latestSize = item.size;
          const latestRotation = item.rotation;

          // Ensure these values have defaults if undefined
          const latestLetterSpacing = item.letterSpacing ?? 0;
          const latestLineHeight = item.lineHeight ?? 100;
          const latestTextAlign = item.textAlign ?? "left";
          const latestVerticalAlign = item.verticalAlign ?? "top";

          const is3DModel = modelFiles.some((model) => model.id === item.id);

          const updatedItem = {
            ...item,
            isVisible,
            position:
              isVisible && item.hiddenState
                ? item.hiddenState.position
                : latestPosition,
            size:
              isVisible && item.hiddenState
                ? item.hiddenState.size
                : latestSize,
            rotation:
              isVisible && item.hiddenState
                ? item.hiddenState.rotation
                : latestRotation,
            letterSpacing:
              isVisible && item.hiddenState
                ? item.hiddenState.letterSpacing
                : latestLetterSpacing,
            lineHeight:
              isVisible && item.hiddenState
                ? item.hiddenState.lineHeight
                : latestLineHeight,
            textAlign:
              isVisible && item.hiddenState
                ? item.hiddenState.textAlign
                : latestTextAlign,
            verticalAlign:
              isVisible && item.hiddenState
                ? item.hiddenState.verticalAlign
                : latestVerticalAlign,
            hiddenState: !isVisible
              ? {
                  position: latestPosition,
                  size: latestSize,
                  rotation: latestRotation,
                  letterSpacing: latestLetterSpacing,
                  lineHeight: latestLineHeight,
                  textAlign: latestTextAlign,
                  verticalAlign: latestVerticalAlign,
                  ...(is3DModel && item.controlsState
                    ? { controlsState: item.controlsState }
                    : {}),
                }
              : item.hiddenState,
            ...(is3DModel && isVisible && item.hiddenState?.controlsState
              ? { controlsState: item.hiddenState.controlsState }
              : {}),
          };

          return updatedItem;
        }
        return item;
      });

      setItems(updatedItems);
      return updatedItems;
    };

    // Update visibility for all the relevant objects
    const updatedBgFiles = updateVisibility(bgFiles, setBgFiles);
    const updatedModelFiles = updateVisibility(modelFiles, setModelFiles);
    const updatedTextBoxes = updateVisibility(textBoxes, setTextBoxes);
    const updatedShapes = updateVisibility(shapes, setShapes);

    // Synchronize layers array
    const updatedLayers = layers.map((layer) => {
      const correspondingItem =
        updatedBgFiles.find((item) => item.id === layer.id) ||
        updatedModelFiles.find((item) => item.id === layer.id) ||
        updatedTextBoxes.find((item) => item.id === layer.id) ||
        updatedShapes.find((item) => item.id === layer.id);

      if (correspondingItem) {
        return {
          ...layer,
          isVisible: correspondingItem.isVisible, // Sync isVisible state
        };
      }
      return layer;
    });

    // Update the layers array
    setLayers(updatedLayers);

    // Save the updated state to history
    updateState({
      layers: updatedLayers,
      bgFiles: updatedBgFiles,
      modelFiles: updatedModelFiles,
      textBoxes: updatedTextBoxes,
      shapes: updatedShapes,
    });
  };

  //=============================================================================
  // ASSET MANAGEMENT FUNCTIONS
  //=============================================================================

  const fetchAssets = async () => {
    try {
      if (!userId) return;

      // Fetch assets using backend API
      const assets = await fetchUserAssets(userId);

      setAssets(assets);
    } catch (error) {

    }
  };

  const filteredAssets = assets
    .filter((asset) => {
      // Filter by text search:
      if (!asset.name) return false; // or adjust as you like
      return asset.name.toLowerCase().includes(assetsSearchTerm.toLowerCase());
    })
    .filter((asset) => {
      // Filter by 'all' | 'images' | 'models'
      if (filterType === "all") return true;
      if (filterType === "images") {
        // For images, maybe anything that's not .glb?
        return !(
          asset.path?.toLowerCase().endsWith(".glb") ||
          asset.path?.toLowerCase().endsWith(".fbx") ||
          asset.path?.toLowerCase().endsWith(".obj")
        );
      }
      if (filterType === "models") {
        return (
          asset.path?.toLowerCase().endsWith(".glb") ||
          asset.path?.toLowerCase().endsWith(".fbx") ||
          asset.path?.toLowerCase().endsWith(".obj")
        );
      }
      return true;
    });

  const toggleFilterDropdown = () => {
    setIsFilterDropdownOpen((prev) => !prev);
  };

  const handleFilterClick = (type) => {
    setFilterType(type);
    setIsFilterDropdownOpen(false);
  };

  const handleAddAssetToCanvas = async (asset) => {
    // 1) Check if asset is a 3D model vs. an image
    const is3DModel =
      asset.path?.toLowerCase().endsWith(".glb") ||
      asset.path?.toLowerCase().endsWith(".fbx") ||
      asset.path?.toLowerCase().endsWith(".obj");

    if (is3DModel) {
      // === 3D MODEL BRANCH (unchanged) ===
      setShowControlsIndicator(true);
      const newModelFile = {
        id: `model-${uuidv4()}`,
        sourceAssetId: asset.id,
        name: asset.name || "3D Model",
        path: asset.path,
        url: asset.url,
        position: { x: 100, y: 100 },
        size: { width: 300, height: 300 },
        rotation: 0,
        exposure: 1,
        zIndex: getHighestZIndex(layers) + 1,
        isVisible: true,
        scale: 1,
        flip: { scaleX: 1, scaleY: 1 },
        controlsState: {
          cameraPosition: new THREE.Vector3(0, 0, 10),
          controlsTarget: new THREE.Vector3(0, 0, 0),
        },
        uploadedAt: asset.uploadedAt || new Date().toISOString(),
        fileHash: asset.fileHash || null,
      };

      // Update local state (don't call upsertAsset for existing assets)
      updateState({
        modelFiles: [...modelFiles, newModelFile],
        layers: [newModelFile, ...layers],
      });

      // Optionally auto-select
      setSelectedContainer(newModelFile.id);
      handleObjectClick(newModelFile.id);
    } else {
      // === IMAGE BRANCH: replicate handleBgUpload approach ===

      try {
        // 1) Use the backend proxy to avoid CORS issues
        //    Construct URL to serve image through backend
        const secureUrl = `/api/serve-image?filename=${encodeURIComponent(asset.path)}`;

        // 2) Convert to dataURL so we can store originalDataUrl
        const originalDataUrl = await fetchUrlToDataURL(secureUrl);

        // 3) Measure the image
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = originalDataUrl;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });

        // 4) Build a newBgFile matching handleBgUpload's shape
        const newBgFile = {
          id: `bg-${uuidv4()}`,
          sourceAssetId: asset.id,
          name: asset.name || "Image",
          path: asset.path, // Firebase Storage path
          url: secureUrl, // Use proxy URL for display
          originalUrl: secureUrl, // Use proxy URL for original reference
          originalDataUrl, // store the "unmodified" dataURL
          currentDataUrl: originalDataUrl, // start with no brightness changes
          position: { x: 100, y: 100 },
          size: { width: img.width * 0.5, height: img.height * 0.5 },
          zIndex: getHighestZIndex(layers) + 1,
          rotation: 0,
          exposure: 1,
          isVisible: true,
          flip: { scaleX: 1, scaleY: 1 },
          uploadedAt: asset.uploadedAt || new Date().toISOString(),
          fileHash: asset.fileHash || null,
          imageSnapshotPath: asset.imageSnapshotPath || null,
        };

        // 5) Add to local state
        updateState({
          bgFiles: [...bgFiles, newBgFile],
          layers: [newBgFile, ...layers],
        });

        // 6) Auto-select
        setSelectedContainer(newBgFile.id);
        handleObjectClick(newBgFile.id);
      } catch (err) {}
    }
  };

  //=============================================================================
  // UI INTERACTION HANDLERS
  //=============================================================================

  const handleInputFocus = (e) => {
    e.target.select();
  };

  const toggleLinkDimensions = () => {
    setIsLinked((prev) => !prev);
  };

  const handleImageAiBarClick = (e) => {
    e.stopPropagation();
    setIsSliderShifted(!isSliderShifted);
  };

  const clearActiveState = () => {
    const toggleButtons = document.querySelectorAll(".navbar .toggle-button");
    toggleButtons.forEach((button) => button.classList.remove("active"));
  };

  const openSidebar = (sidebarId) => {
    setActiveSidebar(sidebarId);
    clearActiveState();
    const toggleButton = document.querySelector(`.toggle-button-${sidebarId}`);
    if (toggleButton) {
      toggleButton.classList.add("active");
    }
  };

  const toggleSidebar = (sidebarId) => {
    // Always set the clicked sidebar as active, without closing it
    setActiveSidebar(sidebarId);
    openSidebar(sidebarId); // Highlight the active button
  };

  const deselectAllObjects = () => {
    setSelectedContainer(null);
    setBgFiles((prev) => prev.map((file) => ({ ...file, isSelected: false })));
    setModelFiles((prev) =>
      prev.map((file) => ({ ...file, isSelected: false })),
    );
    setTextBoxes((prev) =>
      prev.map((box) => ({ ...box, isSelected: false, isEditing: false })),
    );
    setShapes((prev) => prev.map((shape) => ({ ...shape, isSelected: false })));
  };

  //=============================================================================
  // OBJECT SELECTION AND MANIPULATION HANDLERS
  //=============================================================================

  const handleObjectClick = (id) => {
    setSelectedContainer(id);
    setBgFiles((prev) =>
      prev.map((file) => ({
        ...file,
        isSelected: file.id === id,
      })),
    );
    setModelFiles((prev) =>
      prev.map((file) => ({
        ...file,
        isSelected: file.id === id,
      })),
    );
    setTextBoxes((prev) => {
      const updatedTextBoxes = prev.map((box) => ({
        ...box,
        isSelected: box.id === id,
        isEditing: false,
      }));
      const selectedTextBox = updatedTextBoxes.find((box) => box.id === id);
      if (selectedTextBox) {
        setFontSize(selectedTextBox.fontSize);
        setFontFamily(selectedTextBox.fontFamily);
        setFontColor(selectedTextBox.color);
        setSelectedFontWeight(selectedTextBox.fontWeight || "Normal");
        openSidebar(4);
      }
      return updatedTextBoxes;
    });
    setShapes((prev) => {
      const updatedShapes = prev.map((shape) => ({
        ...shape,
        isSelected: shape.id === id,
      }));
      const selectedShape = updatedShapes.find((shape) => shape.id === id);
      if (selectedShape) {
        setShapeColor(selectedShape.color);
        setShapeOpacity(selectedShape.opacity);
        openSidebar(5);
      }
      return updatedShapes;
    });

    const selectedBgFile = bgFiles.find((file) => file.id === id);
    const selectedModelFile = modelFiles.find((file) => file.id === id);

    if (selectedBgFile) {
      openSidebar(2);
    } else if (selectedModelFile) {
      openSidebar(3);
    }
  };

  const handleCanvasClick = (e) => {
    // Get the canvas area
    const canvasArea = document.querySelector(".three-canvas-area");
    const canvasBackground = document.querySelector(
      ".three-canvas-area-background",
    );

    // Check if an input field is currently focused
    const activeElement = document.activeElement;
    if ((activeElement && activeElement.tagName === "INPUT") || "SELECT") {
      // Let the input blur event trigger first
      activeElement.blur();
      setTimeout(() => {
        // After blur has occurred, handle the canvas click
        handleCanvasClickLogic(e, canvasArea, canvasBackground);
      }, 0);
      return;
    }

    // Proceed with canvas click logic immediately if no input is focused
    handleCanvasClickLogic(e, canvasArea, canvasBackground);
  };

  const handleCanvasClickLogic = (e, canvasArea, canvasBackground) => {
    const canvasRect = canvasArea.getBoundingClientRect();
    const clickX = (e.clientX - canvasRect.left) / zoom;
    const clickY = (e.clientY - canvasRect.top) / zoom;

    // If we truly want to restrict clicks to inside the canvas background:
    if (
      !canvasArea.contains(e.target) &&
      !canvasBackground.contains(e.target)
    ) {
      return;
    }

    // NEW: find out which object (if any) was clicked
    const clickedId = getClickedObjectId(clickX, clickY);

    if (!clickedId) {
      // That means no object was clicked => deselect everything
      setSelectedContainer(null);
      setBgFiles((prev) => prev.map((f) => ({ ...f, isSelected: false })));
      setModelFiles((prev) => prev.map((m) => ({ ...m, isSelected: false })));
      setTextBoxes((prev) =>
        prev.map((t) => ({ ...t, isSelected: false, isEditing: false })),
      );
      setShapes((prev) => prev.map((s) => ({ ...s, isSelected: false })));
      return;
    }

    // If we get here, an object was clicked
    if (clickedId === selectedContainer) {
      // They clicked the SAME object that's already selected.
      // Typically, do nothing. (Or if you want to toggle "deselect," you could.)
      // Example: do nothing
      return;
    }

    // They clicked a DIFFERENT object => select that one
    handleObjectClick(clickedId);
  };

  const handleDeleteSelected = (event) => {
    if (event.key === "Delete" || event.key === "Backspace") {
      const isEditing =
        textBoxes.some((textBox) => textBox.isEditing) ||
        document.activeElement.tagName === "INPUT" ||
        document.activeElement.tagName === "TEXTAREA" ||
        sidebarRef1.current.contains(document.activeElement) ||
        sidebarRef2.current.contains(document.activeElement) ||
        sidebarRef3.current.contains(document.activeElement) ||
        sidebarRef4.current.contains(document.activeElement) ||
        sidebarRef5.current.contains(document.activeElement) ||
        sidebarRef6.current.contains(document.activeElement);

      if (!isEditing && selectedContainer) {
        const updatedBgFiles = bgFiles.filter(
          (file) => file.id !== selectedContainer,
        );
        const updatedModelFiles = modelFiles.filter(
          (file) => file.id !== selectedContainer,
        );
        const updatedTextBoxes = textBoxes.filter(
          (box) => box.id !== selectedContainer,
        );
        const updatedShapes = shapes.filter(
          (shape) => shape.id !== selectedContainer,
        );
        const updatedLayers = layers.filter(
          (layer) => layer.id !== selectedContainer,
        );

        // Use updateState to record the deletion in the undo stack
        updateState({
          bgFiles: updatedBgFiles,
          modelFiles: updatedModelFiles,
          textBoxes: updatedTextBoxes,
          shapes: updatedShapes,
          layers: updatedLayers,
        });

        // Reset the selection as part of the deletion
        setSelectedContainer(null);
      }
    }
  };

  const handleDuplicateSelected = () => {
    const selectedObject = getSelectedObject();

    if (selectedObject) {
      // Create a deep copy of the selected object with a new ID
      const duplicate = {
        ...selectedObject,
        id: `${selectedObject.id}-duplicate-${Date.now()}`, // Unique ID for the duplicate
        position: {
          x: selectedObject.position.x + 100, // Offset to avoid exact overlap
          y: selectedObject.position.y + 20,
        },
        isSelected: false, // Deselect initially
      };

      // For 3D models, ensure the controlsState is properly copied
      if (selectedObject.controlsState) {
        duplicate.controlsState = {
          cameraPosition: { ...selectedObject.controlsState.cameraPosition },
          controlsTarget: { ...selectedObject.controlsState.controlsTarget },
        };
      }

      // Determine the type of object to add the duplicate to the correct category
      let newBgFiles = bgFiles;
      let newModelFiles = modelFiles;
      let newTextBoxes = textBoxes;
      let newShapes = shapes;
      let newLayers = [duplicate, ...layers]; // Add duplicate to layers

      if (bgFiles.some((bg) => bg.id === selectedContainer)) {
        newBgFiles = [...bgFiles, duplicate];
      } else if (modelFiles.some((model) => model.id === selectedContainer)) {
        newModelFiles = [...modelFiles, duplicate];
      } else if (
        textBoxes.some((textBox) => textBox.id === selectedContainer)
      ) {
        newTextBoxes = [...textBoxes, duplicate];
      } else if (shapes.some((shape) => shape.id === selectedContainer)) {
        newShapes = [...shapes, duplicate];
      }

      // Update state and history in one function call
      updateState({
        bgFiles: newBgFiles,
        modelFiles: newModelFiles,
        textBoxes: newTextBoxes,
        shapes: newShapes,
        layers: newLayers,
      });
    }
  };

  const updateObjectAndLayerPositions = (newPosition) => {
    if (!selectedContainer) return;

    const updatedShapes = shapes.map((shape) =>
      shape.id === selectedContainer
        ? { ...shape, position: newPosition }
        : shape,
    );
    const updatedBgFiles = bgFiles.map((bg) =>
      bg.id === selectedContainer ? { ...bg, position: newPosition } : bg,
    );
    const updatedModelFiles = modelFiles.map((model) =>
      model.id === selectedContainer
        ? { ...model, position: newPosition }
        : model,
    );
    const updatedTextBoxes = textBoxes.map((textBox) =>
      textBox.id === selectedContainer
        ? { ...textBox, position: newPosition }
        : textBox,
    );
    const updatedLayers = layers.map((layer) =>
      layer.id === selectedContainer
        ? { ...layer, position: newPosition }
        : layer,
    );
    setShapes(updatedShapes);
    setBgFiles(updatedBgFiles);
    setModelFiles(updatedModelFiles);
    setTextBoxes(updatedTextBoxes);
    setLayers(updatedLayers);
    updateState({
      shapes: updatedShapes,
      bgFiles: updatedBgFiles,
      modelFiles: updatedModelFiles,
      textBoxes: updatedTextBoxes,
      layers: updatedLayers,
    });
  };

  const handleTransformationChange = (type, updatedProperties) => {
    if (!selectedContainer) return;

    // This function creates an updated version of any canvas item
    const newUpdater = (item) => {
      if (item.id !== selectedContainer) return item;

      switch (type) {
        case "drag":
          return { ...item, position: updatedProperties };
        case "resize":
          // For resize, updatedProperties is an object: { width, height }
          return { ...item, size: updatedProperties };
        case "rotate":
          // For rotate, updatedProperties is just the rotation number
          return { ...item, rotation: updatedProperties };
        default:
          return item;
      }
    };

    // Apply the update to all types of objects
    const newBgFiles = bgFiles.map(newUpdater);
    const newModelFiles = modelFiles.map(newUpdater);
    const newTextBoxes = textBoxes.map(newUpdater);
    const newShapes = shapes.map(newUpdater);
    const newLayers = layers.map(newUpdater);

    // Save the new, correct state to history
    updateState({
      bgFiles: newBgFiles,
      modelFiles: newModelFiles,
      textBoxes: newTextBoxes,
      shapes: newShapes,
      layers: newLayers,
    });
  };

  //=============================================================================
  // OBJECT POSITIONING HANDLERS
  //=============================================================================

  const handleMoveObjectCenterX = () => {
    const object = getSelectedObject();

    if (object) {
      const canvasWidth = parseInt(canvasSize.width, 10);
      const canvasCenterX = canvasWidth / 2;
      const objectCenterX = object.position.x + object.size.width / 2;
      const moveByX = canvasCenterX - objectCenterX;

      if (moveByX !== 0) {
        setMoveBy({ x: moveByX, y: 0 });

        const updatedObjects = {
          bgFiles: bgFiles.map((bg) =>
            bg.id === selectedContainer
              ? {
                  ...bg,
                  position: { ...bg.position, x: bg.position.x + moveByX },
                }
              : bg,
          ),
          modelFiles: modelFiles.map((model) =>
            model.id === selectedContainer
              ? {
                  ...model,
                  position: {
                    ...model.position,
                    x: model.position.x + moveByX,
                  },
                }
              : model,
          ),
          textBoxes: textBoxes.map((textBox) =>
            textBox.id === selectedContainer
              ? {
                  ...textBox,
                  position: {
                    ...textBox.position,
                    x: textBox.position.x + moveByX,
                  },
                }
              : textBox,
          ),
          shapes: shapes.map((shape) =>
            shape.id === selectedContainer
              ? {
                  ...shape,
                  position: {
                    ...shape.position,
                    x: shape.position.x + moveByX,
                  },
                }
              : shape,
          ),
        };

        updateState(updatedObjects);

        setTimeout(() => setMoveBy({ x: 0, y: 0 }), 200);
      }
    }
  };

  const handleMoveObjectCenterY = () => {
    const object = getSelectedObject();

    if (object) {
      const canvasHeight = parseInt(canvasSize.height, 10);
      const canvasCenterY = canvasHeight / 2;
      const objectCenterY = object.position.y + object.size.height / 2;
      const moveByY = canvasCenterY - objectCenterY;

      if (moveByY !== 0) {
        setMoveBy({ x: 0, y: moveByY });

        const updatedObjects = {
          bgFiles: bgFiles.map((bg) =>
            bg.id === selectedContainer
              ? {
                  ...bg,
                  position: { ...bg.position, y: bg.position.y + moveByY },
                }
              : bg,
          ),
          modelFiles: modelFiles.map((model) =>
            model.id === selectedContainer
              ? {
                  ...model,
                  position: {
                    ...model.position,
                    y: model.position.y + moveByY,
                  },
                }
              : model,
          ),
          textBoxes: textBoxes.map((textBox) =>
            textBox.id === selectedContainer
              ? {
                  ...textBox,
                  position: {
                    ...textBox.position,
                    y: textBox.position.y + moveByY,
                  },
                }
              : textBox,
          ),
          shapes: shapes.map((shape) =>
            shape.id === selectedContainer
              ? {
                  ...shape,
                  position: {
                    ...shape.position,
                    y: shape.position.y + moveByY,
                  },
                }
              : shape,
          ),
        };

        updateState(updatedObjects);

        setTimeout(() => setMoveBy({ x: 0, y: 0 }), 200);
      }
    }
  };

  const handleMoveObjectRight = () => {
    const object = getSelectedObject();

    if (object) {
      const canvasWidth = parseInt(canvasSize.width, 10);
      const currentRightEdge = object.position.x + object.size.width;
      const moveByX = canvasWidth - currentRightEdge;

      if (moveByX > 0) {
        setMoveBy({ x: moveByX, y: 0 });

        const updatedObjects = {
          bgFiles: bgFiles.map((bg) =>
            bg.id === selectedContainer
              ? {
                  ...bg,
                  position: { ...bg.position, x: bg.position.x + moveByX },
                }
              : bg,
          ),
          modelFiles: modelFiles.map((model) =>
            model.id === selectedContainer
              ? {
                  ...model,
                  position: {
                    ...model.position,
                    x: model.position.x + moveByX,
                  },
                }
              : model,
          ),
          textBoxes: textBoxes.map((textBox) =>
            textBox.id === selectedContainer
              ? {
                  ...textBox,
                  position: {
                    ...textBox.position,
                    x: textBox.position.x + moveByX,
                  },
                }
              : textBox,
          ),
          shapes: shapes.map((shape) =>
            shape.id === selectedContainer
              ? {
                  ...shape,
                  position: {
                    ...shape.position,
                    x: shape.position.x + moveByX,
                  },
                }
              : shape,
          ),
        };

        updateState(updatedObjects);

        setTimeout(() => setMoveBy({ x: 0, y: 0 }), 200);
      }
    }
  };

  const handleMoveObjectLeft = () => {
    const object = getSelectedObject();

    if (object) {
      const currentLeftEdge = object.position.x;
      const moveByX = -currentLeftEdge;

      if (moveByX !== 0) {
        setMoveBy({ x: moveByX, y: 0 });

        const updatedObjects = {
          bgFiles: bgFiles.map((bg) =>
            bg.id === selectedContainer
              ? {
                  ...bg,
                  position: { ...bg.position, x: bg.position.x + moveByX },
                }
              : bg,
          ),
          modelFiles: modelFiles.map((model) =>
            model.id === selectedContainer
              ? {
                  ...model,
                  position: {
                    ...model.position,
                    x: model.position.x + moveByX,
                  },
                }
              : model,
          ),
          textBoxes: textBoxes.map((textBox) =>
            textBox.id === selectedContainer
              ? {
                  ...textBox,
                  position: {
                    ...textBox.position,
                    x: textBox.position.x + moveByX,
                  },
                }
              : textBox,
          ),
          shapes: shapes.map((shape) =>
            shape.id === selectedContainer
              ? {
                  ...shape,
                  position: {
                    ...shape.position,
                    x: shape.position.x + moveByX,
                  },
                }
              : shape,
          ),
        };

        updateState(updatedObjects);

        setTimeout(() => setMoveBy({ x: 0, y: 0 }), 200);
      }
    }
  };

  const handleMoveObjectTop = () => {
    const object = getSelectedObject();

    if (object) {
      const currentTopEdge = object.position.y;
      const moveByY = -currentTopEdge;

      if (moveByY !== 0) {
        setMoveBy({ x: 0, y: moveByY });

        const updatedObjects = {
          bgFiles: bgFiles.map((bg) =>
            bg.id === selectedContainer
              ? {
                  ...bg,
                  position: { ...bg.position, y: bg.position.y + moveByY },
                }
              : bg,
          ),
          modelFiles: modelFiles.map((model) =>
            model.id === selectedContainer
              ? {
                  ...model,
                  position: {
                    ...model.position,
                    y: model.position.y + moveByY,
                  },
                }
              : model,
          ),
          textBoxes: textBoxes.map((textBox) =>
            textBox.id === selectedContainer
              ? {
                  ...textBox,
                  position: {
                    ...textBox.position,
                    y: textBox.position.y + moveByY,
                  },
                }
              : textBox,
          ),
          shapes: shapes.map((shape) =>
            shape.id === selectedContainer
              ? {
                  ...shape,
                  position: {
                    ...shape.position,
                    y: shape.position.y + moveByY,
                  },
                }
              : shape,
          ),
        };
        updateState(updatedObjects);
        setTimeout(() => setMoveBy({ x: 0, y: 0 }), 200);
      }
    }
  };

  const handleMoveObjectBottom = () => {
    const object = getSelectedObject();

    if (object) {
      const canvasHeight = parseInt(canvasSize.height, 10);
      const currentBottomEdge = object.position.y + object.size.height;
      const moveByY = canvasHeight - currentBottomEdge;

      if (moveByY > 0) {
        setMoveBy({ x: 0, y: moveByY });

        const updatedObjects = {
          bgFiles: bgFiles.map((bg) =>
            bg.id === selectedContainer
              ? {
                  ...bg,
                  position: { ...bg.position, y: bg.position.y + moveByY },
                }
              : bg,
          ),
          modelFiles: modelFiles.map((model) =>
            model.id === selectedContainer
              ? {
                  ...model,
                  position: {
                    ...model.position,
                    y: model.position.y + moveByY,
                  },
                }
              : model,
          ),
          textBoxes: textBoxes.map((textBox) =>
            textBox.id === selectedContainer
              ? {
                  ...textBox,
                  position: {
                    ...textBox.position,
                    y: textBox.position.y + moveByY,
                  },
                }
              : textBox,
          ),
          shapes: shapes.map((shape) =>
            shape.id === selectedContainer
              ? {
                  ...shape,
                  position: {
                    ...shape.position,
                    y: shape.position.y + moveByY,
                  },
                }
              : shape,
          ),
        };
        updateState(updatedObjects);
        setTimeout(() => setMoveBy({ x: 0, y: 0 }), 200);
      }
    }
  };

  //=============================================================================
  // OBJECT TRANSFORMATION HANDLERS
  //=============================================================================

  const setObjectRotation = (newRotation, saveOnEnd = false) => {
    if (isNaN(newRotation)) return;

    const updateRotationInArray = (item) =>
      item.id === selectedContainer ? { ...item, rotation: newRotation } : item;

    const updatedBgFiles = bgFiles.map(updateRotationInArray);
    const updatedModelFiles = modelFiles.map(updateRotationInArray);
    const updatedTextBoxes = textBoxes.map(updateRotationInArray);
    const updatedShapes = shapes.map(updateRotationInArray);
    const updatedLayers = layers.map(updateRotationInArray);

    setBgFiles(updatedBgFiles);
    setModelFiles(updatedModelFiles);
    setTextBoxes(updatedTextBoxes);
    setShapes(updatedShapes);
    setLayers(updatedLayers);

    if (saveOnEnd) {
      updateState({
        bgFiles: updatedBgFiles,
        modelFiles: updatedModelFiles,
        textBoxes: updatedTextBoxes,
        shapes: updatedShapes,
        layers: updatedLayers
      });
    }
  };

  const rotateBy90Degrees = () => {
    const selectedObject = getSelectedObject();
    if (selectedObject) {
      setIsRotationUpdating(true);  // Block input onChange events
      // Calculate the new absolute rotation
      const newRotation = (selectedObject.rotation + 90) % 360;
      setObjectRotation(newRotation, true);
      
      // Re-enable input onChange events after a short delay
      setTimeout(() => {
        setIsRotationUpdating(false);
      }, 500);
    }
  };

  const handleHorizontalFlip = () => {
    const updatedBgFiles = bgFiles.map((bg) =>
      bg.id === selectedContainer
        ? { ...bg, flip: { ...bg.flip, scaleX: -bg.flip.scaleX } }
        : bg,
    );
    const updatedModelFiles = modelFiles.map((model) =>
      model.id === selectedContainer
        ? { ...model, flip: { ...model.flip, scaleX: -model.flip.scaleX } }
        : model,
    );
    const updatedTextBoxes = textBoxes.map((textBox) =>
      textBox.id === selectedContainer
        ? {
            ...textBox,
            flip: { ...textBox.flip, scaleX: -textBox.flip.scaleX },
          }
        : textBox,
    );
    const updatedShapes = shapes.map((shape) =>
      shape.id === selectedContainer
        ? { ...shape, flip: { ...shape.flip, scaleX: -shape.flip.scaleX } }
        : shape,
    );
    updateState({
      bgFiles: updatedBgFiles,
      modelFiles: updatedModelFiles,
      textBoxes: updatedTextBoxes,
      shapes: updatedShapes,
      layers,
    });
  };

  const handleVerticalFlip = () => {
    const updatedBgFiles = bgFiles.map((bg) =>
      bg.id === selectedContainer
        ? { ...bg, flip: { ...bg.flip, scaleY: -bg.flip.scaleY } }
        : bg,
    );
    const updatedModelFiles = modelFiles.map((model) =>
      model.id === selectedContainer
        ? { ...model, flip: { ...model.flip, scaleY: -model.flip.scaleY } }
        : model,
    );
    const updatedTextBoxes = textBoxes.map((textBox) =>
      textBox.id === selectedContainer
        ? {
            ...textBox,
            flip: { ...textBox.flip, scaleY: -textBox.flip.scaleY },
          }
        : textBox,
    );
    const updatedShapes = shapes.map((shape) =>
      shape.id === selectedContainer
        ? { ...shape, flip: { ...shape.flip, scaleY: -shape.flip.scaleY } }
        : shape,
    );
    updateState({
      bgFiles: updatedBgFiles,
      modelFiles: updatedModelFiles,
      textBoxes: updatedTextBoxes,
      shapes: updatedShapes,
      layers,
    });
  };

  const handleResizeObjectWidth = useCallback(
    (deltaWidth, saveOnEnd = false) => {
      if (isNaN(deltaWidth) || deltaWidth === "") return;

      const selectedObject = getSelectedObject();
      if (selectedObject) {
        const newWidth = selectedObject.size.width + deltaWidth;
        const newHeight = isLinked
          ? Math.round(
              (selectedObject.size.height / selectedObject.size.width) *
                newWidth,
            )
          : selectedObject.size.height;

        setResizeBy({
          width: deltaWidth,
          height: isLinked ? newHeight - selectedObject.size.height : 0,
        });

        const updatedObjects = {
          bgFiles: bgFiles.map((bg) =>
            bg.id === selectedContainer
              ? { ...bg, size: { width: newWidth, height: newHeight } }
              : bg,
          ),
          modelFiles: modelFiles.map((model) =>
            model.id === selectedContainer
              ? { ...model, size: { width: newWidth, height: newHeight } }
              : model,
          ),
          textBoxes: textBoxes.map((textBox) =>
            textBox.id === selectedContainer
              ? { ...textBox, size: { width: newWidth, height: newHeight } }
              : textBox,
          ),
          shapes: shapes.map((shape) =>
            shape.id === selectedContainer
              ? { ...shape, size: { width: newWidth, height: newHeight } }
              : shape,
          ),
        };

        if (saveOnEnd) {
          updateState({ ...updatedObjects, layers });
        }
        setBgFiles(updatedObjects.bgFiles);
        setModelFiles(updatedObjects.modelFiles);
        setTextBoxes(updatedObjects.textBoxes);
        setShapes(updatedObjects.shapes);
        requestAnimationFrame(() => {
          setResizeBy({ width: 0, height: 0 });
        });
      }
    },
    [isLinked, selectedContainer, bgFiles, modelFiles, textBoxes, shapes],
  );

  const handleResizeObjectHeight = useCallback(
    (deltaHeight, saveOnEnd = false) => {
      if (isNaN(deltaHeight) || deltaHeight === "") return;

      const selectedObject = getSelectedObject();
      if (selectedObject) {
        const newHeight = selectedObject.size.height + deltaHeight;
        const newWidth = isLinked
          ? Math.round(
              (selectedObject.size.width / selectedObject.size.height) *
                newHeight,
            )
          : selectedObject.size.width;

        // Update resize dimensions
        setResizeBy({
          width: isLinked ? newWidth - selectedObject.size.width : 0,
          height: deltaHeight,
        });

        // Batch updates to avoid redundant re-renders
        const updatedObjects = {
          bgFiles: bgFiles.map((bg) =>
            bg.id === selectedContainer
              ? { ...bg, size: { width: newWidth, height: newHeight } }
              : bg,
          ),
          modelFiles: modelFiles.map((model) =>
            model.id === selectedContainer
              ? { ...model, size: { width: newWidth, height: newHeight } }
              : model,
          ),
          textBoxes: textBoxes.map((textBox) =>
            textBox.id === selectedContainer
              ? { ...textBox, size: { width: newWidth, height: newHeight } }
              : textBox,
          ),
          shapes: shapes.map((shape) =>
            shape.id === selectedContainer
              ? { ...shape, size: { width: newWidth, height: newHeight } }
              : shape,
          ),
        };

        // Save to history only on blur
        if (saveOnEnd) {
          updateState({ ...updatedObjects, layers });
        }

        setBgFiles(updatedObjects.bgFiles);
        setModelFiles(updatedObjects.modelFiles);
        setTextBoxes(updatedObjects.textBoxes);
        setShapes(updatedObjects.shapes);

        // Reset resizeBy after a slight delay
        requestAnimationFrame(() => {
          setResizeBy({ width: 0, height: 0 });
        });
      }
    },
    [isLinked, selectedContainer, bgFiles, modelFiles, textBoxes, shapes],
  );

  //=============================================================================
  // TEXT EDITING HANDLERS
  //=============================================================================

  const addTextBox = () => {
    const initialPosition = getCanvasCenterPosition();
    const newTextBox = {
      id: `text-${Date.now()}`,
      name: `Text Box`,
      position: initialPosition,
      size: { width: 200, height: 100 },
      rotation: 0,
      content: "Insert text here",
      isSelected: true,
      isEditing: false,
      fontSize: "25px",
      fontFamily: "Poppins",
      fontWeight: "Normal",
      letterSpacing: 0, // Default to no letter spacing
      lineHeight: 100, // Default to 100% (1.0)
      textAlign: "left", // Default to left-aligned
      verticalAlign: "top",
      color: `rgba(0, 0, 0, ${textOpacity})`,
      zIndex: getHighestZIndex(layers) + 1,
      isVisible: true,
      flip: { scaleX: 1, scaleY: 1 },
    };

    const updatedTextBoxes = textBoxes.map((textBox) => ({
      ...textBox,
      isSelected: false,
    }));
    const newTextBoxes = [...updatedTextBoxes, newTextBox];
    const newLayers = [newTextBox, ...layers];

    // Use updateState to add the new text box and update layers
    updateState({
      bgFiles,
      modelFiles,
      textBoxes: newTextBoxes,
      shapes,
      layers: newLayers,
    });

    // Other state updates
    setSelectedContainer(newTextBox.id);
    handleObjectClick(newTextBox.id);
    setFontSize(newTextBox.fontSize);
    setFontFamily(newTextBox.fontFamily);
    setFontColor(newTextBox.color);
    setSelectedFontWeight(newTextBox.fontWeight);
    setTextOpacity(1);
    openSidebar(4);
    captureDivRef.current?.focus();
  };

  const handleTextboxBlur = (id, newText) => {
    const updatedTextBoxes = textBoxes.map((box) =>
      box.id === id ? { ...box, content: newText } : box,
    );
    updateState({ textBoxes: updatedTextBoxes });
  };

  const handleTextChange = (id, newText) => {
    setTextBoxes((prev) => {
      const newTextBoxes = [...prev];
      const index = newTextBoxes.findIndex((box) => box.id === id);
      newTextBoxes[index].content = newText;
      return newTextBoxes;
    });
  };

  const handleFontSizeChange = (e, saveOnEnd = false) => {
    if (selectedContainer) {
      setTextBoxes((prev) => {
        const newTextBoxes = [...prev];
        const index = newTextBoxes.findIndex(
          (box) => box.id === selectedContainer,
        );

        if (index !== -1) {
          newTextBoxes[index].fontSize = `${e.target.value}px`;

          // Update the Fabric.js textbox
          const canvasElement = document.getElementById(
            `fabricCanvas-${newTextBoxes[index].id}`,
          );
          const fabricCanvas = canvasElement.fabricCanvas;
          const fabricTextBox = fabricCanvas.getObjects("textbox")[0];

          if (fabricTextBox) {
            fabricTextBox.set({ fontSize: parseInt(e.target.value, 10) });
            setTimeout(() => {
              fabricCanvas.renderAll();
            }, 10);
          }
        }

        return newTextBoxes;
      });

      setFontSize(`${e.target.value}px`);

      // Save the updated state to history if this is the final change
      if (saveOnEnd) {
        updateState({ textBoxes }); // Save the updated textBoxes to history
      }
    }
  };

  const handleFontFamilyChange = (e, saveOnEnd = false) => {
    if (!selectedContainer) return;

    const newFontFamily = e.target.value;

    // We'll also see if the currently selected textBox has a numericWeight
    // or fall back to 400 if none is set:
    const selectedTextBox = textBoxes.find(
      (box) => box.id === selectedContainer,
    );
    const numericWeight = selectedTextBox
      ? selectedTextBox.fontWeight || 400
      : 400;

    setTextBoxes((prev) => {
      const updated = [...prev];
      const index = updated.findIndex((box) => box.id === selectedContainer);

      if (index !== -1) {
        updated[index].fontFamily = newFontFamily; // Update React side

        const canvasElement = document.getElementById(
          `fabricCanvas-${updated[index].id}`,
        );
        if (canvasElement) {
          const fabricCanvas = canvasElement.fabricCanvas;
          const fabricTextBox = fabricCanvas.getObjects("textbox")[0];
          if (fabricTextBox) {
            // 1) Wait for the specified family & weight to load
            loadFontWithWeight(newFontFamily, numericWeight)
              .then(() => {
                // 2) After it's loaded, apply to Fabric
                fabricTextBox.set({
                  fontFamily: newFontFamily,
                  fontWeight: numericWeight,
                });
                fabricCanvas.renderAll();
              })
              .catch((err) => {
                // still do a fallback set
                fabricTextBox.set({
                  fontFamily: newFontFamily,
                  fontWeight: numericWeight,
                });
                fabricCanvas.renderAll();
              });
          }
        }
      }
      return updated;
    });

    setFontFamily(newFontFamily); // So the sidebar also shows the new family

    if (saveOnEnd) {
      updateState({ textBoxes });
    }
  };

  const handleFontWeightChange = (id, weight, saveOnEnd = false) => {
    // 1) Convert something like "Semi Bold" => numeric
    let numericWeight = 400;
    if (!isNaN(weight)) {
      numericWeight = Number(weight);
    } else {
      switch (weight.toLowerCase()) {
        case "normal":
          numericWeight = 400;
          break;
        case "bold":
          numericWeight = 700;
          break;
        default:
          numericWeight = 400;
      }
    }

    // 2) Update local state & Fabric
    setTextBoxes((prevTextBoxes) => {
      const newTextBoxes = [...prevTextBoxes];
      const index = newTextBoxes.findIndex((box) => box.id === id);
      if (index !== -1) {
        // A) Update your React side
        newTextBoxes[index].fontWeight = numericWeight;

        // B) Load the font weight from Google before we call set on Fabric
        const canvasElement = document.getElementById(
          `fabricCanvas-${newTextBoxes[index].id}`,
        );
        const fabricCanvas = canvasElement?.fabricCanvas;
        const fabricTextBox = fabricCanvas?.getObjects("textbox")[0];
        if (fabricTextBox) {
          // NEW: wait for the font to be downloaded first
          const currentFamily = newTextBoxes[index].fontFamily; // e.g. "Kanit", "Poppins", etc.
          loadFontWithWeight(currentFamily, numericWeight) // <--- from our helper
            .then(() => {
              // Only now do we actually set the numeric weight
              fabricTextBox.set({
                fontWeight: numericWeight,
                fontFamily: currentFamily,
              });
              fabricCanvas.renderAll();
            })
            .catch((err) => {
              // Even if loading fails, we can still do something like:
              fabricTextBox.set({
                fontWeight: numericWeight,
                fontFamily: currentFamily,
              });
              fabricCanvas.renderAll();
            });
        }
      }
      return newTextBoxes;
    });

    // 3) If user is "done" (onBlur), record in history
    if (saveOnEnd) {
      updateState({ textBoxes });
    }
  };

  const handleFontColorChange = (
    textboxId,
    colorValue,
    opacity,
    saveOnEnd = false,
  ) => {
    const updatedTextBoxes = textBoxes.map((textbox) =>
      textbox.id === textboxId
        ? { ...textbox, color: colorValue, opacity }
        : textbox,
    );

    setTextBoxes(updatedTextBoxes);

    // Update the Fabric.js textbox directly
    const canvasElement = document.getElementById(`fabricCanvas-${textboxId}`);
    if (canvasElement) {
      const fabricCanvas = canvasElement.fabricCanvas;
      const fabricTextBox = fabricCanvas?.getObjects("textbox")[0];

      if (fabricTextBox) {
        fabricTextBox.set("fill", colorValue);
        fabricTextBox.set("opacity", opacity);
        fabricCanvas.renderAll();
      }
    }

    // Save to history only if this is the final change
    if (saveOnEnd) {
      updateState({
        textBoxes: updatedTextBoxes,
        shapes, // Retain current state for other entities
        layers,
        bgFiles,
        modelFiles,
      });
    }
  };

  const handleLetterSpacingChange = (id, value, saveOnEnd = false) => {
    const percentageValue = value / 100; // Convert percentage to a scale factor (0% to 100%)
    let historyUpdated = false; // Prevent duplicate updates

    setTextBoxes((prev) => {
      const newTextBoxes = [...prev];
      const index = newTextBoxes.findIndex((box) => box.id === id);
      newTextBoxes[index].letterSpacing = value; // Keep the percentage in state

      // Update the Fabric.js textbox
      const canvasElement = document.getElementById(
        `fabricCanvas-${newTextBoxes[index].id}`,
      );
      const fabricCanvas = canvasElement.fabricCanvas;
      const fabricTextBox = fabricCanvas.getObjects("textbox")[0];

      fabricTextBox.set({
        charSpacing: percentageValue * 1000, // Fabric.js uses thousandths of an em
      });

      fabricCanvas.renderAll(); // Redraw the canvas

      if (saveOnEnd && !historyUpdated) {
        updateState({ textBoxes: newTextBoxes }); // Save to history
        historyUpdated = true;
      }

      return newTextBoxes;
    });
  };

  const handleLineHeightChange = (id, value, saveOnEnd = false) => {
    const lineHeightValue = value / 100; // Convert percentage to lineHeight ratio (100% = 1)
    let historyUpdated = false;

    setTextBoxes((prev) => {
      const newTextBoxes = [...prev];
      const index = newTextBoxes.findIndex((box) => box.id === id);
      newTextBoxes[index].lineHeight = value; // Keep the percentage in state

      // Update the Fabric.js textbox
      const canvasElement = document.getElementById(
        `fabricCanvas-${newTextBoxes[index].id}`,
      );
      const fabricCanvas = canvasElement.fabricCanvas;
      const fabricTextBox = fabricCanvas.getObjects("textbox")[0];

      fabricTextBox.set({
        lineHeight: lineHeightValue, // Set the lineHeight ratio in Fabric.js
      });

      fabricCanvas.renderAll(); // Redraw the canvas

      if (saveOnEnd && !historyUpdated) {
        updateState({ textBoxes: newTextBoxes });
        historyUpdated = true;
      }

      return newTextBoxes;
    });
  };

  const handleTextAlignChange = (id, align, saveOnEnd = false) => {
    let historyUpdated = false;

    setTextBoxes((prev) => {
      const newTextBoxes = [...prev];
      const index = newTextBoxes.findIndex((box) => box.id === id);
      newTextBoxes[index].textAlign = align;

      // Update the Fabric.js textbox
      const canvasElement = document.getElementById(
        `fabricCanvas-${newTextBoxes[index].id}`,
      );
      const fabricCanvas = canvasElement.fabricCanvas;
      const fabricTextBox = fabricCanvas.getObjects("textbox")[0];

      fabricTextBox.set({
        textAlign: align,
      });

      fabricCanvas.renderAll(); // Redraw the canvas

      if (saveOnEnd && !historyUpdated) {
        updateState({ textBoxes: newTextBoxes });
        historyUpdated = true;
      }

      return newTextBoxes;
    });
  };

  const handleVerticalAlignChange = (id, verticalAlign, saveOnEnd = false) => {
    let historyUpdated = false;

    setTextBoxes((prev) => {
      const newTextBoxes = [...prev];
      const index = newTextBoxes.findIndex((box) => box.id === id);
      const selectedTextBox = newTextBoxes[index];

      // Update the Fabric.js canvas textbox
      const canvasElement = document.getElementById(
        `fabricCanvas-${selectedTextBox.id}`,
      );
      const fabricCanvas = canvasElement.fabricCanvas;
      const fabricTextBox = fabricCanvas.getObjects("textbox")[0];

      const textHeight = fabricTextBox.height;
      const containerHeight = fabricCanvas.height; // Assuming the container is the Fabric.js canvas height

      let newTop;
      switch (verticalAlign) {
        case "top":
          fabricTextBox.set({ originY: "top" });
          newTop = 0;
          break;
        case "middle":
          fabricTextBox.set({ originY: "top" });
          newTop = (containerHeight - textHeight) / 2;
          break;
        case "bottom":
          fabricTextBox.set({ originY: "bottom" });
          newTop = containerHeight;
          break;
        default:
          return;
      }

      fabricTextBox.set({ top: newTop });

      fabricCanvas.renderAll();

      newTextBoxes[index].verticalAlign = verticalAlign;

      if (saveOnEnd && !historyUpdated) {
        updateState({ textBoxes: newTextBoxes });
        historyUpdated = true;
      }

      return newTextBoxes;
    });
  };

  //=============================================================================
  // SHAPE EDITING HANDLERS
  //=============================================================================

  const addShape = (shapeType) => {
    const initialPosition = getCanvasCenterPosition();
    const newShape = {
      id: `shape-${Date.now()}`,
      name: `${shapeType.charAt(0).toUpperCase() + shapeType.slice(1)}`,
      type: shapeType,
      position: initialPosition,
      rotation: 0,
      size: { width: 100, height: 100 },
      color: "#CBCBCB",
      opacity: 1,
      isSelected: true,
      zIndex: getHighestZIndex(layers) + 1,
      isVisible: true,
      flip: { scaleX: 1, scaleY: 1 },
    };

    // Update the shape state with history handling
    const updatedShapes = shapes.map((shape) => ({
      ...shape,
      isSelected: false,
    }));
    const newShapes = [...updatedShapes, newShape];
    const newLayers = [newShape, ...layers];

    // Save the new state to history, replacing previous setShapes and setLayers calls
    updateState({
      bgFiles,
      modelFiles,
      textBoxes,
      shapes: newShapes,
      layers: newLayers,
    });

    // Other state updates that don't affect shapes array directly
    setSelectedContainer(newShape.id);
    handleObjectClick(newShape.id);
    setShapeColor(newShape.color);
    setShapeOpacity(newShape.opacity);
    openSidebar(5);
    captureDivRef.current?.focus();
  };

  const handleShapeUpdate = useCallback((updatedShape) => {
    setShapes((prevShapes) =>
      prevShapes.map((shape) =>
        shape.id === updatedShape.id ? { ...shape, ...updatedShape } : shape,
      ),
    );
    setLayers((prevLayers) =>
      prevLayers.map((layer) =>
        layer.id === updatedShape.id ? { ...layer, ...updatedShape } : layer,
      ),
    );
  }, []);

  const handleShapeColorChange = (
    shapeId,
    colorValue,
    opacity,
    saveOnEnd = false,
  ) => {
    const updatedShapes = shapes.map((shape) =>
      shape.id === shapeId ? { ...shape, color: colorValue, opacity } : shape,
    );

    setShapes(updatedShapes);

    // Save to history only if this is the final change
    if (saveOnEnd) {
      updateState({
        shapes: updatedShapes,
        layers, // Layers are not updated, so retain the current state
        bgFiles,
        modelFiles,
        textBoxes,
      });
    }
  };

  //=============================================================================
  // IMAGE/BACKGROUND HANDLERS
  //=============================================================================

  const handleBgFileUpdate = useCallback((updatedBg) => {
    setBgFiles((prevBgFiles) =>
      prevBgFiles.map((bg) =>
        bg.id === updatedBg.id ? { ...bg, ...updatedBg } : bg,
      ),
    );
    setLayers((prevLayers) =>
      prevLayers.map((layer) =>
        layer.id === updatedBg.id ? { ...layer, ...updatedBg } : layer,
      ),
    );
  }, []);

  const handleModelUpdate = useCallback((updatedModel) => {
    setModelFiles((prevModelFiles) =>
      prevModelFiles.map((model) =>
        model.id === updatedModel.id ? { ...model, ...updatedModel } : model,
      ),
    );
    setLayers((prevLayers) =>
      prevLayers.map((layer) =>
        layer.id === updatedModel.id ? { ...layer, ...updatedModel } : layer,
      ),
    );
  }, []);

  const handleTextBoxUpdate = useCallback((updatedTextBox) => {
    setTextBoxes((prevTextBoxes) =>
      prevTextBoxes.map((textBox) =>
        textBox.id === updatedTextBox.id
          ? { ...textBox, ...updatedTextBox }
          : textBox,
      ),
    );
    setLayers((prevLayers) =>
      prevLayers.map((layer) =>
        layer.id === updatedTextBox.id
          ? { ...layer, ...updatedTextBox }
          : layer,
      ),
    );
  }, []);

  const handleBgExposureChange = async (e, bgId, saveOnBlur = false) => {
    const newExposure = parseFloat(e.target.value);

    // 1) Update the numeric value in state
    const updatedBgFiles = bgFiles.map((bg) =>
      bg.id === bgId ? { ...bg, exposure: newExposure } : bg,
    );
    setBgFiles(updatedBgFiles);

    // 2) Optionally record in history if user just released mouse or blurred
    if (e.type === "mouseup" || saveOnBlur) {
      updateState({ bgFiles: updatedBgFiles });
    }

    // 3) Now actually re-generate the brightened data in memory
    const targetBg = updatedBgFiles.find((bg) => bg.id === bgId);
    if (targetBg?.originalDataUrl) {
      try {
        const newDataUrl = await applyLocalExposure(
          targetBg.originalDataUrl,
          newExposure,
        );
        // 4) Update the <img> to show the new brightness
        const finalBgFiles = updatedBgFiles.map((bg) =>
          bg.id === bgId ? { ...bg, currentDataUrl: newDataUrl } : bg,
        );
        setBgFiles(finalBgFiles);
      } catch (err) {}
    }
  };

  const handleExposureChange = (e, id, saveOnBlur = false) => {
    const newExposure = parseFloat(e.target.value);

    // Update model files immediately
    const updatedModelFiles = modelFiles.map((model) =>
      model.id === id ? { ...model, exposure: newExposure } : model,
    );
    setModelFiles(updatedModelFiles);

    // Save to history only on mouse up
    if (e.type === "mouseup") {
      updateState({
        modelFiles: updatedModelFiles,
      });
    }

    if (saveOnBlur) {
      updateState({
        modelFiles: updatedModelFiles,
      });
    }
  };

  const handleCanvasBackgroundColorChange = (color, saveOnEnd = false) => {
    const newColor = color?.rgb
      ? {
          r: color.rgb.r,
          g: color.rgb.g,
          b: color.rgb.b,
          a: color.rgb.a || 1, // Default opacity to 1 if not provided
        }
      : parseRGBString(color);

    if (newColor) {
      setCanvasBackgroundColor(newColor);

      // Save to history only if this is the final change (onMouseUp or onBlur)
      if (saveOnEnd) {
        updateState({ canvasBackgroundColor: newColor });
      }
    } else {
    }
  };

  const handleCanvasSizeChange = (e) => {
    const { name, value } = e.target;
    if (!value.match(/^\d+$/)) return; // Ensure the value is numeric

    // Convert the string value to an integer for calculation
    const intValue = parseInt(value, 10);

    // Get the current canvas dimensions without "px"
    const currentWidth = parseInt(canvasSize.width.replace("px", ""), 10);
    const currentHeight = parseInt(canvasSize.height.replace("px", ""), 10);

    // Calculate the aspect ratio based on current dimensions
    const aspectRatio = currentHeight / currentWidth;

    let newWidth = currentWidth;
    let newHeight = currentHeight;

    // Adjust the opposite dimension if linked
    if (isLinked) {
      if (name === "width") {
        newWidth = intValue;
        newHeight = Math.round(newWidth * aspectRatio);
      } else if (name === "height") {
        newHeight = intValue;
        newWidth = Math.round(newHeight / aspectRatio);
      }
    } else {
      // If not linked, only update the dimension that was changed
      if (name === "width") newWidth = intValue;
      if (name === "height") newHeight = intValue;
    }

    // Update the canvas size with px suffix
    setCanvasSize({
      width: `${newWidth}px`,
      height: `${newHeight}px`,
    });
  };

  const handleZoomChange = (e) => {
    setZoom(e.target.value);
  };

  const handleBgUpload = async (event) => {
    const files = Array.from(event.target.files);

    // Check storage limits before processing any files
    try {
      const currentUsage = await fetchUserStorageUsage();
      const storageLimit = await getStorageLimitBytes();

      // Calculate total size of files being uploaded
      const totalUploadSize = files.reduce((sum, file) => sum + file.size, 0);

      // Check if upload would exceed storage limit
      if (currentUsage + totalUploadSize > storageLimit) {
        setShowStorageLimitModal(true);
        setBlockedFileSize(totalUploadSize);
        return;
      }
    } catch (error) {
    }

    setIsUploadingBg(true);

    try {
      const results = await Promise.all(
        files.map(async (file, index) => {
          // 1) Check duplicates
          const fileHash = await computeFileHash(file);
          const existingAsset = assets.find((a) => a.fileHash === fileHash);

          const originalDataUrl = await fileToDataURL(file);

          if (existingAsset) {
            // Generate standardized asset ID for canvas objects
            const assetId = `asset-image-${Math.floor(Math.random() * 900000) + 100000}`;
            const newBgFile = {
              id: assetId,
              sourceAssetId: existingAsset.id,
              originalDataUrl,
              currentDataUrl: originalDataUrl,
              name: existingAsset.name,
              path: existingAsset.path,
              url: existingAsset.url,
              originalUrl: existingAsset.originalUrl,
              position: { x: 100, y: 100 },
              size: { width: 400, height: 400 },
              zIndex: getHighestZIndex(layers) + 1,
              rotation: 0,
              exposure: 1,
              isVisible: true,
              flip: { scaleX: 1, scaleY: 1 },
              uploadedAt: new Date().toISOString(),
              // If the existing doc has a preview:
              imageSnapshotPath: existingAsset.imageSnapshotPath || null,
            };

            updateState({
              bgFiles: [...bgFiles, newBgFile],
              layers: [newBgFile, ...layers],
            });

            return { success: true, reused: true, newBgFile };
          } else {
            const filePath = await uploadFileToServer(
              file,
              userId,
              canvasId,
              "images",
            );

            // 3) Measure the final image size - use full URL for loading
            const fullImageUrl = `/api/serve-image?filename=${encodeURIComponent(filePath)}`;
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.src = fullImageUrl;
            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
            });

            // 4) Build the newBgFile with standardized asset ID
            const assetId = `asset-image-${Math.floor(Math.random() * 900000) + 100000}`;
            const newBgFile = {
              id: assetId,
              name: file.name,
              path: filePath, // Store raw file path
              url: fullImageUrl, // Use full URL for canvas display
              originalDataUrl,
              currentDataUrl: originalDataUrl,
              originalUrl: fullImageUrl, // Use full URL for display
              position: { x: 100, y: 100 },
              size: { width: img.width * 0.5, height: img.height * 0.5 },
              zIndex: getHighestZIndex(layers) + 1,
              rotation: 0,
              exposure: 1,
              isVisible: true,
              flip: { scaleX: 1, scaleY: 1 },
              uploadedAt: new Date().toISOString(),
              imageSnapshotPath: null, // We'll fill this in next
            };

            // 5) Generate a small WebP preview for thumbnail only
            try {
              const previewDataUrl = await generateImagePreview(fullImageUrl);
              if (previewDataUrl) {
                const blob = dataURLtoBlob(previewDataUrl);
                const previewFile = new File(
                  [blob],
                  `${newBgFile.id}-preview.webp`,
                  { type: "image/webp" },
                );
                const previewPath = await uploadFileToServer(
                  previewFile,
                  userId,
                  canvasId,
                  "thumbnails", // Use thumbnails folder instead of images
                );
                newBgFile.imageSnapshotPath = previewPath;
              }
            } catch (err) {}

            // 6) Upsert an "asset" doc with fileHash & imageSnapshotPath


            const assetData = {
              id: newBgFile.id,
              assetType: "image",
              name: newBgFile.name,
              path: newBgFile.path, // Raw file path only
              url: newBgFile.path, // Store raw path, not full URL
              originalUrl: newBgFile.path, // Store raw path, not full URL
              fileHash,
              uploadedAt: newBgFile.uploadedAt,
              imageSnapshotPath: newBgFile.imageSnapshotPath,
            };

            await upsertAsset(userId, assetData);

            // 7) Update local state
            updateState({
              bgFiles: [...bgFiles, newBgFile],
              layers: [newBgFile, ...layers],
            });
            await fetchAssets();

            return { success: true, reused: false, newBgFile };
          }
        }),
      );
    } catch (error) {
    } finally {
      setIsUploadingBg(false);
      // Clear file input so user can re-upload if desired
      if (bgInputRef.current) bgInputRef.current.value = null;
    }
  };

  const handleModelUpload = async (event) => {
    const files = Array.from(event.target.files);

    // Check storage limits before processing any files
    try {
      const currentUsage = await fetchUserStorageUsage();
      const storageLimit = await getStorageLimitBytes();

      // Calculate total size of files being uploaded
      const totalUploadSize = files.reduce((sum, file) => sum + file.size, 0);

      // Check if upload would exceed storage limit
      if (currentUsage + totalUploadSize > storageLimit) {
        setShowStorageLimitModal(true);
        setBlockedFileSize(totalUploadSize);
        return;
      }
    } catch (error) {
    }

    setIsUploadingModel(true);
    try {
      const results = await Promise.all(
        files.map(async (file, index) => {
          // 1. Compute the file's hash and check the unified assets array
          const duplicate = await findDuplicateAsset(file, assets);
          if (duplicate) {
            // Create a new model file instance with standardized asset ID
            const assetId = `asset-model-${Math.floor(Math.random() * 900000) + 100000}`;
            const newModelFile = {
              id: assetId, // standardized asset ID for this instance
              sourceAssetId: duplicate.id, // reference to the original asset
              name: file.name,
              path: duplicate.path, // reuse the uploaded file path
              url: duplicate.url,
              position: { x: 100, y: 100 },
              size: { width: 300, height: 300 },
              rotation: 0,
              exposure: 1,
              zIndex: getHighestZIndex(layers) + 1,
              isVisible: true,
              scale: 1,
              flip: { scaleX: 1, scaleY: 1 },
              controlsState: {
                cameraPosition: new THREE.Vector3(0, 0, 10),
                controlsTarget: new THREE.Vector3(0, 0, 0),
              },
              // Use the duplicate's uploadedAt date if available
              uploadedAt: duplicate.uploadedAt || new Date().toISOString(),
              fileHash: duplicate.fileHash,
            };

            // Update local state so that this model appears on the canvas
            updateState({
              modelFiles: [...modelFiles, newModelFile],
              layers: [newModelFile, ...layers],
            });
            // Return early from this file's upload
            return { success: true, newModelFile };
          }

          // 2. If no duplicate is found, proceed with upload
          const folder = "models";
          const filePath = await uploadFileToServer(file, userId, null, folder);


          // Build the full URL for validation and rendering
          const modelFullUrl = `/api/serve-image?filename=${encodeURIComponent(filePath)}`;
          const modelValidation = await validate3DModel(modelFullUrl);
          if (!modelValidation) {
            throw new Error(`Model validation failed for ${file.name}`);
          }

          // 3. Create a new model file object with standardized asset ID
          const fileHash = await computeFileHash(file); // compute again or reuse from above if you cache it
          const assetId = `asset-model-${Math.floor(Math.random() * 900000) + 100000}`;
          const newModelFile = {
            id: assetId,
            name: file.name,
            path: filePath, // Store raw file path
            url: modelFullUrl, // Use full URL for canvas display
            position: { x: 100, y: 100 },
            size: { width: 300, height: 300 },
            rotation: 0,
            exposure: 1,
            zIndex: getHighestZIndex(layers) + 1,
            isVisible: true,
            scale: 1,
            flip: { scaleX: 1, scaleY: 1 },
            controlsState: {
              cameraPosition: new THREE.Vector3(0, 0, 10),
              controlsTarget: new THREE.Vector3(0, 0, 0),
            },
            uploadedAt: new Date().toISOString(),
            modelSnapshotPath: null,
            fileHash, // store the computed hash for future duplicate checks
          };

          // 4. Optionally generate a snapshot for the 3D model.
          let snapshotDataUrl = null;
          try {
            snapshotDataUrl = await generateModelSnapshotInMemory(
              modelFullUrl,
              1080,
              1080,
            );
          } catch (err) {}
          if (snapshotDataUrl) {
            const snapshotBlob = dataURLtoBlob(snapshotDataUrl);
            const snapshotFile = new File(
              [snapshotBlob],
              `${newModelFile.id}-snapshot.webp`,
              { type: "image/webp" },
            );
            const snapshotPath = await uploadFileToServer(
              snapshotFile,
              userId,
              null,
              "models",
            );
            newModelFile.modelSnapshotPath = snapshotPath;
          }

          // 5. Upsert the asset document (include fileHash)
          const modelAssetData = {
            id: newModelFile.id,
            assetType: "model",
            name: newModelFile.name,
            path: newModelFile.path, // Raw file path only
            url: newModelFile.path, // Store raw path, not full URL
            uploadedAt: newModelFile.uploadedAt,
            modelSnapshotPath: newModelFile.modelSnapshotPath,
            fileHash,
          };

          await upsertAsset(userId, modelAssetData);

          // 6. Update local state so that the new model is available.


          updateState({
            modelFiles: [...modelFiles, newModelFile],
            layers: [newModelFile, ...layers],
          });


          await fetchAssets();
          return { success: true, newModelFile };
        }),
      );
    } catch (error) {
    } finally {
      setIsUploadingModel(false);
      setShowControlsIndicator(true);
    }
    if (modelInputRef.current) modelInputRef.current.value = null;
  };

  const handleAddToCanvas = async (openAiUrl) => {
    try {
      setIsUploadingBg(true);

      if (!currentUser) {
        throw new Error("User not authenticated");
      }

      // Get Firebase ID token for authentication
      const idToken = await currentUser.getIdToken();

      // Validate token to prevent 'match' error
      if (!idToken || typeof idToken !== "string" || idToken.trim() === "") {
        throw new Error("Failed to obtain valid authentication token");
      }

      // 1) Use server-side proxy to fetch and upload the AI-generated image
      const proxyResponse = await fetch(`/api/ai/proxyImage`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ imageUrl: openAiUrl }),
      });

      if (!proxyResponse.ok) {
        throw new Error("Failed to proxy AI image from server");
      }

      // 2) The server returns { filePath, filename }, extract both
      const { filePath, filename } = await proxyResponse.json();

      // 3) Create a data URL for the image for processing
      const response = await fetch(filePath);
      const blob = await response.blob();

      // Check storage limits before processing the AI-generated image
      try {
        const currentUsage = await fetchUserStorageUsage();
        const storageLimit = await getStorageLimitBytes();

        // Check if the AI-generated image would exceed storage limit
        if (currentUsage + blob.size > storageLimit) {
          setShowStorageLimitModal(true);
          setBlockedFileSize(blob.size);
          return;
        }
      } catch (error) {
      }

      const originalDataUrl = await fileToDataURL(
        new File([blob], `ai_generated_${Date.now()}.png`, { type: blob.type }),
      );

      // 4) Measure the image size
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = filePath;
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });

      // 5) Calculate proper size and position to fit within canvas
      const canvasWidth = parseInt(
        canvasSize?.width?.replace("px", "") || "1080",
        10,
      );
      const canvasHeight = parseInt(
        canvasSize?.height?.replace("px", "") || "1080",
        10,
      );

      // Calculate scale to fit image within canvas with padding
      const padding = 50; // 50px padding from edges
      const maxWidth = canvasWidth - padding * 2;
      const maxHeight = canvasHeight - padding * 2;

      const scaleX = maxWidth / img.width;
      const scaleY = maxHeight / img.height;
      const scale = Math.min(scaleX, scaleY, 0.8); // Max 80% of canvas size

      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;

      // Center the image on canvas
      const centerX = (canvasWidth - scaledWidth) / 2;
      const centerY = (canvasHeight - scaledHeight) / 2;

      // 6) Build the newBgFile with standardized asset ID
      const assetId = `asset-image-${Math.floor(Math.random() * 900000) + 100000}`;
      const fileName = `ai_generated_${Date.now()}.png`;
      const newBgFile = {
        id: assetId,
        name: fileName,
        path: filename, // Store raw file path
        url: filePath, // Use full URL for canvas display
        originalDataUrl,
        currentDataUrl: originalDataUrl,
        originalUrl: filePath, // Use full URL for display
        position: { x: centerX, y: centerY },
        size: { width: scaledWidth, height: scaledHeight },
        zIndex: getHighestZIndex(layers) + 1,
        rotation: 0,
        exposure: 1,
        isVisible: true,
        flip: { scaleX: 1, scaleY: 1 },
        uploadedAt: new Date().toISOString(),
        imageSnapshotPath: null,
      };

      // 7) Generate a small WebP preview for thumbnail
      try {
        const previewDataUrl = await generateImagePreview(filePath);
        if (previewDataUrl) {
          const blob = dataURLtoBlob(previewDataUrl);
          const previewFile = new File([blob], `${newBgFile.id}-preview.webp`, {
            type: "image/webp",
          });
          const previewPath = await uploadFileToServer(
            previewFile,
            userId,
            canvasId,
            "thumbnails",
          );
          newBgFile.imageSnapshotPath = previewPath;
        }
      } catch (err) {}

      // 8) Generate file hash for AI-generated image (for duplicate detection)
      const file = new File([blob], fileName, { type: blob.type });
      const fileHash = await computeFileHash(file);

      // 9) Save asset metadata to Firestore
      const aiAssetData = {
        id: newBgFile.id,
        assetType: "image",
        name: newBgFile.name,
        path: newBgFile.path, // Raw file path only
        url: newBgFile.path, // Store raw path, not full URL
        originalUrl: newBgFile.path, // Store raw path, not full URL
        fileHash,
        uploadedAt: newBgFile.uploadedAt,
        imageSnapshotPath: newBgFile.imageSnapshotPath,
      };

      await upsertAsset(userId, aiAssetData);

      // 10) Add to local state so it appears on the canvas
      updateState({
        bgFiles: [...bgFiles, newBgFile],
        layers: [newBgFile, ...layers],
      });

      // 11) Refresh assets list to show in Assets tab and update storage tracking
      await fetchAssets();

      // 12) Auto-select the new AI-generated image
      setSelectedContainer(newBgFile.id);
      handleObjectClick(newBgFile.id);


    } catch (err) {

    } finally {
      setIsUploadingBg(false);
    }
  };

  //=============================================================================
  // EXPORT AND SAVE HANDLERS
  //=============================================================================

  const handleClearCanvas = () => {
    updateState({
      bgFiles: [],
      modelFiles: [],
      textBoxes: [],
      shapes: [],
      layers: [],
    });
    setSelectedContainer(null);
  };

  async function captureSnapshot() {
    try {
      const canvasElement = document.querySelector(".three-canvas-area");
      if (!canvasElement) {
        return null;
      }

      const canvasImage = await html2canvas(canvasElement, {
        useCORS: true,
        allowTaint: false,
        imageTimeout: 15000,
      });

      // Instead of .toDataURL("image/png")
      const snapshot = canvasImage.toDataURL("image/webp", 0.8);

      const snapshotBlob = await fetch(snapshot).then((res) => res.blob());

      return snapshot;
    } catch (error) {
      return null;
    }
  }

  const handleSave = async () => {
    setIsSavingCanvas(true);
    const userId = currentUser?.uid; // from Firebase
    if (!userId) {
      return;
    }

    // Grab all user's canvases just like you do.
    const allCanvases = await fetchUserCanvases(userId);
    const totalCount = allCanvases.length;
    let existingCanvas = allCanvases.find((c) => c.canvasId === canvasId);
    let newCanvasName = existingCanvas?.canvasName;

    // Generate intelligent canvas name if it doesn't exist
    if (!newCanvasName) {
      newCanvasName = await generateNextCanvasName(userId);
    }

    const isBrandNewCanvas = !existingCanvas;

    // Check if this is a new canvas and adding it would exceed the allowed limit from profile
    if (isBrandNewCanvas) {
      const currentCanvasCount = await getCurrentCanvasCount();
      const canvasLimit = await getCanvasLimit();

      if (currentCanvasCount >= canvasLimit) {
        setShowCanvasLimitModal(true);
        setIsSavingCanvas(false);
        return;
      }
    }

    // If we never set canvasId yet, create one
    if (!canvasId) {
      const newId = uuidv4();
      setCanvasId(newId);
    }

    // 1) separate new BGs vs existing BGs
    const newlyAddedBg = bgFiles.filter(
      (file) => !file.path || file.path.trim() === "",
    );
    const existingBg = bgFiles.filter(
      (file) => file.path && file.path.trim() !== "",
    );

    // 2) separate new Models vs existing Models
    const newlyAddedModels = modelFiles.filter(
      (file) => !file.path || file.path.trim() === "",
    );
    const existingModels = modelFiles.filter(
      (file) => file.path && file.path.trim() !== "",
    );

    for (let i = 0; i < newlyAddedBg.length; i++) {
      const file = newlyAddedBg[i];
      try {
        // If you have a real File or Blob, do:
        if (file.originalFile) {
          const folder = "images";
          const filePath = await uploadFileToServer(
            file.originalFile,
            userId,
            canvasId,
            folder,
          );
          file.path = filePath; // Store raw file path
          file.url = `/api/serve-image?filename=${encodeURIComponent(filePath)}`; // Generate full URL for display
        }

        // Upsert the newly-added BG as an asset in Firebase
        const { originalDataUrl, currentDataUrl, ...rest } = file;
        const bgAssetData = {
          id: file.id,
          assetType: "image",
          ...rest,
        };
        await upsertAsset(userId, bgAssetData);
      } catch (err) {}
    }

    for (let i = 0; i < newlyAddedModels.length; i++) {
      const file = newlyAddedModels[i];
      try {
        if (file.originalFile) {
          const folder = "models";
          const filePath = await uploadFileToServer(
            file.originalFile,
            userId,
            canvasId,
            folder,
          );
          file.path = filePath; // Store raw file path
          file.url = `/api/serve-image?filename=${encodeURIComponent(filePath)}`; // Generate full URL for display
        }

        // Upsert the newly-added Model as an asset in Firebase
        const modelSaveAssetData = {
          id: file.id,
          assetType: "model",
          name: file.name, // <--- ensure this is not undefined
          ...file,
        };
        await upsertAsset(userId, modelSaveAssetData);
      } catch (err) {}
    }

    // 4) Merge them back with updated path/urls for the newly uploaded items
    const finalBgFiles = [...existingBg, ...newlyAddedBg];
    const finalModelFiles = [...existingModels, ...newlyAddedModels];

    // 5) Now build your final canvasData with updated arrays
    // NOTE: We'll store everything under docType: "canvas"
    const canvasData = {
      docType: "canvas",
      canvasId,
      canvasName: newCanvasName,
      canvasBackgroundColor, // <--- Add this
      canvasSize,
      zoom,
      lastModified: new Date().toISOString(),
      bgFiles: finalBgFiles.map((file, index) => ({
        id: file.id ?? `bg-${uuidv4()}`,
        sourceAssetId: file.sourceAssetId, // <-- Add this line
        path: file.path,
        name: file.name,
        url: file.url,
        originalUrl: file.originalUrl,
        position: file.position,
        size: file.size,
        zIndex: file.zIndex,
        rotation: file.rotation,
        exposure: file.exposure,
        isVisible: file.isVisible,
        flip: file.flip,
        uploadedAt: file.uploadedAt || null,
      })),
      modelFiles: finalModelFiles.map((file, index) => ({
        id: file.id ?? `model-${uuidv4()}`,
        sourceAssetId: file.sourceAssetId, // Add this line!
        path: file.path,
        name: file.name,
        url: file.url,
        position: file.position,
        size: file.size,
        zIndex: file.zIndex,
        rotation: file.rotation,
        exposure: file.exposure,
        isVisible: file.isVisible,
        scale: file.scale,
        flip: file.flip,
        controlsState: file.controlsState,
        uploadedAt: file.uploadedAt || null,
      })),
      textBoxes,
      shapes,
      layers: layers.map((layer, index) => {
        // Remove the big fields
        const { originalDataUrl, currentDataUrl, ...rest } = layer;

        return {
          ...rest,
          id: rest.id || `layer-${Date.now()}-${index}`,
          path: rest.path,
          name: rest.name,
        };
      }),
    };

    try {
      // 6) Capture snapshot (html2canvas)
      const snapshot = await captureSnapshot();
      if (snapshot) {
        const snapshotBlob = await fetch(snapshot).then((res) => res.blob());
        
        // --- FIX START: Use a unique filename within a canvas-specific folder ---
        const snapshotFileName = `snap_${Date.now()}.webp`;
        const snapshotFile = new File([snapshotBlob], snapshotFileName, {
          type: "image/webp",
        });

        // The folder path now includes the canvasId, organizing snapshots per-canvas
        const folderPath = `snapshots/${canvasId}`;
        
        const snapshotUrl = await uploadFileToStorage(
          snapshotFile,
          folderPath, // Use the new, specific path
          () => {} // No progress callback
        );
        // We still save the latest path for the dashboard preview.
        // The new delete logic will ignore this and delete the whole folder.
        canvasData.mainSnapshotPath = snapshotUrl;
        // --- FIX END ---

      } else {
        throw new Error("Snapshot could not be created.");
      }

      await upsertCanvas(userId, canvasId, canvasData);

      setSaveIndicatorVisible(true);
      setTimeout(() => setSaveIndicatorVisible(false), 2000);

      if (isBrandNewCanvas) {
        navigate("/canvas", {
          replace: true,
          state: {
            canvasId,
            canvasSize,
            zoom,
          },
        });
      }
    } catch (error) {
    } finally {
      // 3) Hide the "Saving canvas" popup no matter what:
      setIsSavingCanvas(false);
    }
  };

  // Keep a ref to the latest handleSave function to avoid stale closures in intervals/listeners
  useEffect(() => {
    handleSaveRef.current = handleSave;
  }); // No dependency array, so it updates on every render

  const handleExport = async (fileName, exportFormat, transparentBg) => {
    const captureDiv = captureDivRef.current;
    if (captureDiv) {
      const boundingRect = captureDiv.getBoundingClientRect();

      // Get the specified canvas size from the sidebar inputs
      const exportWidth = parseInt(canvasSize.width, 10);
      const exportHeight = parseInt(canvasSize.height, 10);

      // Get the current zoom level (scale)
      const currentZoom = zoom || 1;

      // Get computed transform to calculate rotation angle
      const transformStyle = window.getComputedStyle(captureDiv).transform;
      const matrix = new DOMMatrix(transformStyle);

      // Ensure the matrix is valid
      if (!matrix) {
        return;
      }

      // Calculate rotation angle in radians
      const rotationAngle = Math.atan2(matrix.b, matrix.a);

      // Calculate the rotated width and height using utility function
      const { rotatedWidth, rotatedHeight } = calculateBoundingBoxDimensions(
        exportWidth, // Use the specified canvas size for width
        exportHeight, // Use the specified canvas size for height
        rotationAngle * (180 / Math.PI), // Convert radians to degrees for the utility function
      );

      // Use a higher pixel density for better quality (e.g., 2x or custom value)
      const pixelRatio = 2;

      // Create offscreen canvas with increased pixel density
      const offscreenCanvas = document.createElement("canvas");
      const ctx = offscreenCanvas.getContext("2d");
      offscreenCanvas.width =
        rotatedWidth * pixelRatio * window.devicePixelRatio;
      offscreenCanvas.height =
        rotatedHeight * pixelRatio * window.devicePixelRatio;

      // Set image smoothing for higher quality
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      // Save original background color and apply transparency if needed
      const originalBackgroundColor = captureDiv.style.backgroundColor;
      if (transparentBg) {
        captureDiv.style.backgroundColor = "transparent";
      }

      // **Apply text enhancements to the captureDiv before rendering**
      const originalTextRendering = captureDiv.style.textRendering;
      const originalFontSmoothing = captureDiv.style.webkitFontSmoothing;
      await document.fonts.ready; // Wait for all fonts to be ready before rendering
      captureDiv.style.textRendering = "optimizeLegibility";
      captureDiv.style.webkitFontSmoothing = "antialiased";

      // Introduce a delay to ensure all rendering is complete
      await new Promise((resolve) => setTimeout(resolve, 200)); // 200ms delay

      // Adjust for zoom and pixel ratio: apply scaling
      ctx.scale((1 / currentZoom) * pixelRatio, (1 / currentZoom) * pixelRatio);

      // Rotate the canvas context around the center of the offscreen canvas
      ctx.translate(rotatedWidth / 2, rotatedHeight / 2);
      ctx.rotate(rotationAngle);
      ctx.translate(-exportWidth / 2, -exportHeight / 2);

      // Render the div onto the offscreen canvas using html2canvas
      const canvas = await html2canvas(captureDiv, {
        canvas: offscreenCanvas,
        backgroundColor: transparentBg ? null : originalBackgroundColor,
        useCORS: true,
        width: exportWidth, // Match the specified canvas size
        height: exportHeight, // Match the specified canvas size
      });

      // Restore the original text rendering styles
      captureDiv.style.textRendering = originalTextRendering;
      captureDiv.style.webkitFontSmoothing = originalFontSmoothing;

      // Create a temporary canvas for resizing to the original dimensions
      const finalCanvas = document.createElement("canvas");
      finalCanvas.width = exportWidth;
      finalCanvas.height = exportHeight;
      const finalCtx = finalCanvas.getContext("2d");

      // Draw the high-resolution image onto the final canvas at original dimensions
      finalCtx.drawImage(offscreenCanvas, 0, 0, exportWidth, exportHeight);

      // Create a download link
      const link = document.createElement("a");
      link.href = finalCanvas.toDataURL(`image/${exportFormat}`, 1.0);
      link.download = `${fileName}.${exportFormat}`;
      link.click();

      // Restore original background color
      captureDiv.style.backgroundColor = originalBackgroundColor;
    }
  };

  const handleExportClick = async () => {
    try {
      // Get Firebase ID token for authentication
      if (!currentUser) {
        throw new Error("User not authenticated");
      }
      const idToken = await currentUser.getIdToken();

      // Validate token to prevent 'match' error
      if (!idToken || typeof idToken !== "string" || idToken.trim() === "") {
        throw new Error("Failed to obtain valid authentication token");
      }

      // Call the backend endpoint to increment the export counter
      const response = await fetch("/api/exports/increment", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (response.ok) {
        // If the counter incremented successfully, proceed with export
        deselectAllObjects();
        await new Promise((resolve) => setTimeout(resolve, 100)); // short delay to update state
        handleExport(fileName, exportFormat, transparentBg);

        // Refresh export usage stats after successful export
        fetchAllUsageStats();
      } else {
        // If not allowed, show the export limit modal
        setShowExportLimitModal(true);
      }
    } catch (error) {

    }
  };

  //=============================================================================
  // DRAG AND DROP HANDLERS
  //=============================================================================

  // Check if the drag contains actual files and not internal UI elements
  const hasFiles = (dataTransfer) => {
    if (!dataTransfer || !dataTransfer.types) return false;
    return Array.from(dataTransfer.types).includes("Files");
  };

  // Check if the target is within excluded areas (sidebars, layers panel, etc.)
  const isExcludedArea = (target) => {
    if (!target) return false;

    // Check if target or any parent has excluded classes/IDs
    const excludedSelectors = [
      ".three-canvas-sidebar-1",
      ".three-canvas-sidebar-2",
      ".three-canvas-sidebar-3",
      ".three-canvas-sidebar-4",
      ".three-canvas-sidebar-5",
      ".layers-panel",
      ".layer-item",
      ".layer-item-name",
      ".layer-item-buttons-container",
      ".sidebar-tab",
      ".sidebar-tab-icon",
      ".sidebar-tabs-container",
      ".sidebar-content-wrapper",
      ".drag-handle",
      "[data-dnd-item]",
      ".moveable-control",
      ".moveable-line",
      ".moveable-control-box",
      "button",
      "input",
      "select",
      "textarea",
    ];

    // Additional check for React DnD elements
    if (
      target.getAttribute &&
      (target.getAttribute("data-react-dnd") ||
        target.getAttribute("data-dnd-dragging") ||
        target.getAttribute("data-dnd-dropping"))
    ) {
      return true;
    }

    return excludedSelectors.some((selector) => {
      try {
        return target.closest(selector) !== null;
      } catch {
        return false;
      }
    });
  };

  const handleDragEnter = (e) => {
    // Only proceed if we have actual files and not in excluded areas
    if (!hasFiles(e.dataTransfer) || isExcludedArea(e.target)) {
      return;
    }

    e.preventDefault();
    setDragCounter((prev) => prev + 1);
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    // Only proceed if we have actual files and not in excluded areas
    if (!hasFiles(e.dataTransfer) || isExcludedArea(e.target)) {
      return;
    }

    e.preventDefault();
    setDragCounter((prev) => {
      const newCount = prev - 1;
      if (newCount === 0) {
        setIsDragging(false);
      }
      return newCount;
    });
  };

  const handleDragOver = (e) => {
    // Only prevent default for actual file drags, not internal UI drags
    if (!hasFiles(e.dataTransfer) || isExcludedArea(e.target)) {
      return;
    }

    e.preventDefault();
  };

  const handleDrop = (e) => {
    // Only handle drops with actual files and not in excluded areas
    if (!hasFiles(e.dataTransfer) || isExcludedArea(e.target)) {
      return;
    }

    e.preventDefault();
    setIsDragging(false);
    setDragCounter(0);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);

      // Separate 3D model files from image files
      const modelFiles = files.filter(
        (file) =>
          file.name.toLowerCase().endsWith(".glb") ||
          file.name.toLowerCase().endsWith(".fbx") ||
          file.name.toLowerCase().endsWith(".obj"),
      );

      const imageFiles = files.filter(
        (file) => file.type.startsWith("image/") && !modelFiles.includes(file),
      );

      // Create synthetic events to pass to existing handlers
      if (imageFiles.length > 0) {
        const imageEvent = {
          target: {
            files: imageFiles,
          },
        };
        handleBgUpload(imageEvent);
      }

      if (modelFiles.length > 0) {
        const modelEvent = {
          target: {
            files: modelFiles,
          },
        };
        handleModelUpload(modelEvent);
      }
    }
  };

  //=============================================================================
  // EFFECT HOOKS
  //=============================================================================

  // Initialize font loading
  const familiesToLoad = fontFamilies.map(
    (font) => `${font}:100,200,300,400,500,600,700,800,900`,
  );

  useEffect(() => {
    WebFont.load({
      google: {
        families: familiesToLoad,
      },
      active() {},
      inactive(err) {},
    });
  }, []);

  // Load subscription plan
  useEffect(() => {
    async function loadPlan() {
      const plan = await fetchUserSubscriptionPlan(); // your existing API helper
      setSubscriptionPlan(plan); // 'pro' or 'essential'
    }
    loadPlan();
  }, []);

  // Fetch export usage stats when component mounts and when user changes
  useEffect(() => {
    fetchAllUsageStats();
  }, [currentUser]);

  // Check for export usage stats updates every 10 seconds for real-time reset display
  useEffect(() => {
    if (!currentUser) return;

    const interval = setInterval(() => {
      fetchAllUsageStats();
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [currentUser]);

  // Load canvas limit from profile document
  useEffect(() => {
    async function loadCanvasLimit() {
      try {
        const limit = await getCanvasLimit();
        setMaxCanvases(limit);
      } catch (error) {

        setMaxCanvases(20); // Default to essential limit
      }
    }
    if (currentUser) {
      loadCanvasLimit();
    }
  }, [currentUser]);

  // Load user settings
  useEffect(() => {
    if (!userId) return; // userId from Firebase Auth
    (async () => {
      try {
        const settings = await fetchUserSettings(userId);
        if (settings && settings.dontShowInteractive === true) {
          setDontShowAgain(true);
        }
      } catch (err) {}
    })();
  }, [userId]);

  // Fetch assets on mount
  useEffect(() => {
    fetchAssets();
  }, []);

  // Initialize canvas ID
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      if (!canvasId) {
        const newCanvasId = uuidv4();
        setCanvasId(newCanvasId);
      }
    }
  }, [canvasId]);

  // Update canvas size and zoom from initial values
  useEffect(() => {
    if (initialCanvasSize) setCanvasSize(initialCanvasSize);
    if (initialZoom) setZoom(initialZoom);
  }, [initialCanvasSize, initialZoom]);

  // Setup history handling for navigation changes
  useEffect(() => {
    const handlePopState = (event) => {
      if (window.location.pathname === "/dashboard") {
        setHistory({
          past: [],
          present: {
            bgFiles: [],
            modelFiles: [],
            textBoxes: [],
            shapes: [],
            layers: [],
            canvasBackgroundColor: { r: 255, g: 255, b: 255, a: 1 },
          },
          future: [],
        });
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      const activeElement = document.activeElement;
      const isEditing =
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.isContentEditable);
      if (!isEditing) {
        // Only handle undo/redo if not editing
        if (event.ctrlKey || event.metaKey) {
          // Detect Ctrl on Windows/Linux or Command on Mac
          if (event.key === "z" || event.key === "Z") {
            event.preventDefault(); // Prevent default undo behavior in the browser
            handleUndo(); // Call the undo function
          } else if (event.key === "y" || event.key === "Y") {
            event.preventDefault(); // Prevent default redo behavior in the browser
            handleRedo(); // Call the redo function
          }
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleUndo, handleRedo]);

  // Authentication check
  useEffect(() => {
    // Debugging: Check if user is signed in
    if (!currentUser) {
      navigate("/login"); // Redirect to login page
    }
  }, [currentUser, navigate]);

  // Update canvas from history
  useEffect(() => {
    const {
      bgFiles,
      modelFiles,
      textBoxes,
      shapes,
      layers,
      canvasBackgroundColor,
    } = history.present;
    setBgFiles(bgFiles);
    setModelFiles(modelFiles);
    setTextBoxes(textBoxes);
    setShapes(shapes);
    setLayers(layers);
    setCanvasBackgroundColor(canvasBackgroundColor);
  }, [history.present]);

  // Handle text selection
  useEffect(() => {
    const selectedTextBox = textBoxes.find(
      (box) => box.id === selectedContainer,
    );
    if (selectedTextBox) {
      setSelectedFontWeight(selectedTextBox.fontWeight || "Normal");
    }
  }, [selectedContainer, textBoxes]);

  // Force canvas model re-renders
  useEffect(() => {
    if (modelFiles.length > 0) {
      invalidate(); // Forces the canvas to re-render
    }
  }, [modelFiles]);

  // Apply saved state
  useEffect(() => {
    if (savedState) {
      const { canvasSize: savedCanvasSize, zoom: savedZoom } = savedState;
      if (savedCanvasSize) {
        setCanvasSize(savedCanvasSize); // Restore canvas size
      }
      if (savedZoom) {
        setZoom(savedZoom); // Restore zoom value
      }
    }
  }, [savedState]);

  // Implement auto-save
  useEffect(() => {
    const saveInterval = setInterval(() => {
      // Call the function from the ref to get the latest version
      if (handleSaveRef.current) {
        handleSaveRef.current();
      }
    }, 45000); // 45 seconds

    return () => clearInterval(saveInterval);
  }, []); // Empty dependency array ensures this runs only once on mount

  // Add save keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (event) => {
      const activeElement = document.activeElement;
      const isEditing =
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.isContentEditable);
      if (!isEditing) {
        if ((event.ctrlKey || event.metaKey) && event.key === "s") {
          event.preventDefault(); // Prevent the browser's default save behavior
          // Call the function from the ref to get the latest version
          if (handleSaveRef.current) {
            handleSaveRef.current();
          }
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []); // Empty dependency array makes this listener set up only once

  // Track shift key for linked transforms
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Shift") {
        setShiftKeyPressed(true);
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === "Shift") {
        setShiftKeyPressed(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  // Update guidelines when canvas size changes
  useEffect(() => {
    updateGuidelines();
  }, [canvasSize, updateGuidelines]);

  // Context menu and delete handlers
  useEffect(() => {
    const handleContextMenu = (event) => {
      event.preventDefault();
    };

    const canvas = canvasRef.current;

    if (canvas) {
      canvas.addEventListener("contextmenu", handleContextMenu);
    }

    window.addEventListener("keydown", handleDeleteSelected);

    return () => {
      if (canvas) {
        canvas.removeEventListener("contextmenu", handleContextMenu);
      }
      window.removeEventListener("keydown", handleDeleteSelected);
    };
  }, [selectedContainer, textBoxes]);

  // Load canvas data
  useEffect(() => {
    const loadCanvasData = async () => {
      try {
        setIsLoadingCanvas(true);
        const userId = currentUser?.uid;
        if (!userId) {
          return;
        }
        // If we have a savedState or fetch from Firebase
        const canvasData =
          savedState && typeof savedState === "object"
            ? savedState
            : await (async () => {

                const canvases = await fetchUserCanvases(userId);

                const foundCanvas = canvases.find(
                  (c) => c.canvasId === canvasId,
                );

                return foundCanvas;
              })();

        if (canvasData) {


          const loadedBgColor = canvasData.canvasBackgroundColor || {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          };

          // Restore canvas dimensions and zoom if saved
          if (canvasData.canvasSize) {
            setCanvasSize(canvasData.canvasSize);
          }
          if (canvasData.zoom) {
            setZoom(canvasData.zoom);
          }
          if (canvasData.fileName) {
            setFileName(canvasData.fileName);
          }

          // Load background files with their saved metadata
          const loadedBgFiles = await Promise.all(
            (canvasData.bgFiles || []).map(async (file) => {
              let currentDataUrl = "";
              if (file.url) {
                currentDataUrl = file.url;
              }

              let originalDataUrl = null;
              if (file.originalUrl) {
                try {
                  originalDataUrl = await fetchUrlToDataURL(file.originalUrl);
                } catch (err) {

                }
              }

              if (file.exposure && file.exposure !== 1 && originalDataUrl) {
                try {
                  currentDataUrl = await applyLocalExposure(
                    originalDataUrl,
                    file.exposure,
                  );
                } catch (err) {

                }
              }

              return {
                ...file,
                originalDataUrl,
                currentDataUrl,
              };
            }),
          );

          // Load model files with their saved metadata
          const loadedModelFiles = (canvasData.modelFiles || []).map(
            (file) => file,
          );

          // Load text boxes with their saved metadata
          const loadedTextBoxes = (canvasData.textBoxes || []).map(
            (textBox) => ({
              ...textBox,
              content: textBox.content || "Insert text here", // Ensure content is never null
            }),
          );

          // Load shapes with their saved metadata
          const loadedShapes = (canvasData.shapes || []).map((shape) => shape);

          // Rebuild layers maintaining the saved order and properties
          const rebuiltLayers = (canvasData.layers || []).map((layer) => {
            const matchingBg = loadedBgFiles.find((bg) => bg.id === layer.id);
            if (matchingBg) return { ...layer, ...matchingBg };

            const matchingModel = loadedModelFiles.find(
              (m) => m.id === layer.id,
            );
            if (matchingModel) return { ...layer, ...matchingModel };

            const matchingTextBox = loadedTextBoxes.find(
              (tb) => tb.id === layer.id,
            );
            if (matchingTextBox) return { ...layer, ...matchingTextBox };

            const matchingShape = loadedShapes.find((s) => s.id === layer.id);
            if (matchingShape) return { ...layer, ...matchingShape };

            return layer;
          });



          // Set the complete restored state into history
          setHistory({
            past: [],
            present: {
              canvasBackgroundColor: loadedBgColor,
              bgFiles: loadedBgFiles,
              modelFiles: loadedModelFiles,
              textBoxes: loadedTextBoxes,
              shapes: loadedShapes,
              layers: rebuiltLayers,
            },
            future: [],
          });
        } else {

          setHistory({
            past: [],
            present: defaultState,
            future: [],
          });
        }
      } catch (error) {
      } finally {
        setIsLoadingCanvas(false);
      }
    };

    // Reset initialization flag when canvasId changes
    if (
      isCanvasInitialized.current &&
      canvasId !== isCanvasInitialized.lastCanvasId
    ) {

      isCanvasInitialized.current = false;
    }

    // Load canvas data when canvasId changes or on initial load
    if (!isCanvasInitialized.current && (canvasId || savedState)) {

      isCanvasInitialized.current = true;
      isCanvasInitialized.lastCanvasId = canvasId;
      loadCanvasData();
    } else if (!isCanvasInitialized.current && !canvasId && !savedState) {

      isCanvasInitialized.current = true;
      isCanvasInitialized.lastCanvasId = null;
      setHistory({
        past: [],
        present: defaultState,
        future: [],
      });
    }
  }, [canvasId, savedState, currentUser]);

  // Drag and drop setup
  useEffect(() => {
    // Prevent default behavior for all drag events at document level
    const preventDefaults = (e) => {
      e.preventDefault();
      // e.stopPropagation();
    };

    // Add event listeners to the document
    document.addEventListener("dragenter", handleDragEnter, true);
    document.addEventListener("dragleave", handleDragLeave, true);
    document.addEventListener("dragover", handleDragOver, true);
    document.addEventListener("drop", handleDrop, true);

    // Clean up
    return () => {
      document.removeEventListener("dragenter", handleDragEnter, true);
      document.removeEventListener("dragleave", handleDragLeave, true);
      document.removeEventListener("dragover", handleDragOver, true);
      document.removeEventListener("drop", handleDrop, true);
    };
  }, [dragCounter]);

  // Disable dragging for all images to prevent interference with file drop
  useEffect(() => {
    const disableImageDragging = () => {
      const images = document.querySelectorAll("img");
      images.forEach((img) => {
        img.draggable = false;
        img.ondragstart = () => false;
      });
    };

    // Disable on mount
    disableImageDragging();

    // Set up observer to handle dynamically added images
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Element node
            if (node.tagName === "IMG") {
              node.draggable = false;
              node.ondragstart = () => false;
            }
            // Check for images within added nodes
            const images = node.querySelectorAll?.("img");
            images?.forEach((img) => {
              img.draggable = false;
              img.ondragstart = () => false;
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  //=============================================================================
  // RENDER FUNCTION
  //=============================================================================

  return (
    <DndProvider backend={HTML5Backend}>
      {isDragging &&
        ReactDOM.createPortal(
          <div className="drop-zone-overlay">
            <div className="drop-zone-text">
              Drop Files Here
              <div className="drop-zone-subtext">
                Images (.png, .jpg, .jpeg, etc.) or 3D Models (.glb)
              </div>
            </div>
          </div>,
          document.body,
        )}
      <div className="three-canvas-container" onMouseDown={handleCanvasClick}>
        <div
          className={`header-container ${isSliderShifted ? "shifted" : ""} ${subscriptionPlan !== "pro" ? "essential-header" : ""}`}
        >
          <button onClick={handleUndo}>
            <img src="undo.png"></img>
          </button>
          <button onClick={handleRedo}>
            <img src="redo.png"></img>
          </button>
          <button>
            <img src="duplicate.png" onClick={handleDuplicateSelected}></img>
          </button>
          {subscriptionPlan === "pro" && (
            <div
              className="visual-assistant-button"
              onClick={handleImageAiBarClick}
            >
              <ImageAiBar
                className="visual-assistant"
                handleAddToCanvas={handleAddToCanvas}
                setUsageModalMessage={setUsageModalMessage}
                setShowUsageModal={setShowUsageModal}
                setShowPromptRequiredModal={setShowPromptRequiredModal}
                currentUser={currentUser}
              />
            </div>
          )}
        </div>
        {saveIndicatorVisible && (
          <div className={`save-indicator ${isSliderShifted ? "shifted" : ""}`}>
            Canvas saved!
          </div>
        )}

        {isSavingCanvas && (
          <div
            className={`saving-canvas-indicator ${isSliderShifted ? "shifted" : ""}`}
          >
            Saving canvas, please do not leave this page...
          </div>
        )}

        {isUploadingBg && (
          <div
            className={`upload-indicator-image ${isSliderShifted ? "shifted" : ""}`}
          >
            Uploading image file, please wait...
          </div>
        )}

        {isUploadingModel && (
          <div
            className={`upload-indicator-model ${isSliderShifted ? "shifted" : ""}`}
          >
            Uploading 3D model, please wait...
          </div>
        )}

        {showControlsIndicator && (
          <div
            className={`controls-indicator ${isSliderShifted ? "shifted" : ""}`}
          >
            Initializing 3D controls, please wait...
          </div>
        )}

        {showInteractiveModal && (
          <div className="interactive-modal-backdrop">
            <div
              className={`interactive-modal ${isSliderShifted ? "shifted" : ""}`}
            >
              <button
                className="interactive-modal-close"
                onClick={() => setShowInteractiveModal(false)}
              >
                ×
              </button>
              <h2>Interactive Renders</h2>
              <p>
                Based on the setup of your asset it may appear on the canvas in
                various sizes.
              </p>

              <div className="modal-illustrations">
                <img src="Render_Small.png" alt="Left Example" />
                <img src="Render_Large.png" alt="Right Example" />
              </div>

              <ul>
                <li>
                  <img src="click.png" alt="Mouse Wheel" /> Use your scroll
                  wheel to increase/decrease
                </li>
                <li>
                  <img src="right-click.png" alt="Right Click" /> Right click +
                  drag to rotate the render.
                </li>
              </ul>

              <label className="dont-show-again">
                <input
                  type="checkbox"
                  checked={dontShowAgain}
                  onChange={async (e) => {
                    const checked = e.target.checked;
                    setDontShowAgain(checked);

                    // 3) Immediately persist to userSettings doc
                    try {
                      await upsertUserSettings(userId, {
                        dontShowInteractive: true,
                      });
                    } catch (err) {}
                  }}
                />
                Don't show me again
              </label>
            </div>
          </div>
        )}

        {showCanvasLimitModal && (
          <div className="modal-overlay-dashboard">
            <div className="modal-content-dashboard">
              <h2>Canvas Limit Exceeded</h2>
              <p>
                Your subscription plan allows up to {maxCanvases} saved
                canvases. Please delete an existing canvas or upgrade your plan.
              </p>
              <div>
                <button onClick={() => setShowCanvasLimitModal(false)}>
                  Dismiss
                </button>
                <button onClick={() => navigate("/upgrade")}>
                  Upgrade Plan
                </button>
              </div>
            </div>
          </div>
        )}

        {showExportLimitModal && (
          <div className="modal-overlay-dashboard">
            <div className="modal-content-dashboard">
              <h2>Export Limit Reached</h2>
              <p>
                Your subscription plan allows up to {allowedExports} canvas
                exports. Please upgrade your plan to export more canvases.
              </p>
              <div>
                <button onClick={() => setShowExportLimitModal(false)}>
                  Dismiss
                </button>
                <button onClick={() => navigate("/upgrade")}>
                  Upgrade Plan
                </button>
              </div>
            </div>
          </div>
        )}

        {showUsageModal && (
          <UsageLimitModal
            message={usageModalMessage}
            onClose={() => setShowUsageModal(false)}
          />
        )}
        {/* Usage modal disabled - server handles all usage validation */}

        {showPromptRequiredModal && (
          <PromptRequiredModal
            onClose={() => setShowPromptRequiredModal(false)}
          />
        )}

        {showStorageLimitModal && (
          <div className="modal-overlay-dashboard">
            <div className="modal-content-dashboard">
              <h2>Storage Limit Exceeded</h2>
              <p>
                Your current storage limit is {(100).toFixed(0)} KB for testing.
              </p>
              {blockedFileSize && (
                <p>
                  The file(s) you attempted to upload total{" "}
                  {(blockedFileSize / 1000).toFixed(2)} KB.
                </p>
              )}
              <p>
                Please delete some files to free up space or upgrade your plan.
              </p>
              <div>
                <button onClick={() => setShowStorageLimitModal(false)}>
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="navbar">
          <div className="navbar-logo-container">
            <img src="YC-Canvas.png" alt="Logo" className="navbar-logo" />
          </div>
          <div className="button-container">
            <button
              className="toggle-button toggle-button-1"
              onClick={() => toggleSidebar(1)}
            >
              <img src="Canvas-Icon-Text.png" className="navbar-icon" />
            </button>
          </div>
          <div className="button-container">
            <button
              className="toggle-button toggle-button-7"
              onClick={() => toggleSidebar(7)}
            >
              <img src="AssetsIcon-Text.png" className="navbar-icon" />
            </button>
          </div>
          <div className="button-container">
            <button
              className="toggle-button toggle-button-2"
              onClick={() => toggleSidebar(2)}
            >
              <img src="Image-Icon-Text.png" className="navbar-icon" />
            </button>
          </div>
          <div className="button-container">
            <button
              className="toggle-button toggle-button-3"
              onClick={() => toggleSidebar(3)}
            >
              <img src="Render-Icon-Text.png" className="navbar-icon" />
            </button>
          </div>
          <div className="button-container">
            <button
              className="toggle-button toggle-button-4"
              onClick={() => toggleSidebar(4)}
            >
              <img src="Text-Icon-White.png" className="navbar-icon" />
            </button>
          </div>
          <div className="button-container">
            <button
              className="toggle-button toggle-button-5"
              onClick={() => toggleSidebar(5)}
            >
              <img src="Shape-Icon-Text.png" className="navbar-icon" />
            </button>
          </div>
          <div className="button-container">
            <button
              className="toggle-button toggle-button-6"
              onClick={() => toggleSidebar(6)}
            >
              <img src="Layers-Icon-Text.png" className="navbar-icon" />
            </button>
          </div>
        </div>

        {/* SIDEBARS */}
        {/* Canvas Settings Sidebar */}
        <div
          ref={sidebarRef1}
          className={`three-canvas-sidebar ${activeSidebar === 1 ? "expanded" : ""}`}
          style={{ display: activeSidebar === 1 ? "block" : "none" }}
        >
          <div className="sidebar-content-wrapper">
            <h1>Canvas Settings</h1>
            <hr></hr>
            <div className="size-input-container-shapes">
              <div className="size-input-container-shapes-w">
                <label>W</label>
                <input
                  type="text"
                  name="width"
                  value={(canvasSize?.width || "1080px").replace("px", "")}
                  onChange={handleCanvasSizeChange}
                  onFocus={handleInputFocus}
                  step={1}
                />
                <span className="suffix">px</span>
              </div>
              <div className="size-input-container-shapes-h">
                <label>H</label>
                <input
                  type="text"
                  name="height"
                  value={(canvasSize?.height || "1080px").replace("px", "")}
                  onChange={handleCanvasSizeChange}
                  onFocus={handleInputFocus}
                  step={1}
                />
                <span className="suffix">px</span>
              </div>
              <button
                onClick={toggleLinkDimensions}
                style={
                  isLinked
                    ? LINKED_BUTTON_STYLE.active
                    : LINKED_BUTTON_STYLE.inactive
                }
              >
                <img
                  src="link.png"
                  alt="Link Dimensions"
                  title="Link Dimensions"
                />
              </button>
            </div>
            <div className="buttons-container" id="clear-button">
              <button onClick={handleClearCanvas}>Clear Canvas</button>
            </div>
            <hr />
            <h2>Appearance</h2>
            <div>
              <ColorPickerBar
                color={`rgba(${canvasBackgroundColor.r}, ${canvasBackgroundColor.g}, ${canvasBackgroundColor.b}, ${canvasBackgroundColor.a})`}
                onColorChange={(newColor, saveOnEnd) =>
                  handleCanvasBackgroundColorChange(newColor, saveOnEnd)
                }
              />
            </div>
            <hr />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              <h2 style={{ margin: 0 }}>Export Options</h2>
              <span style={{ fontSize: "14px", color: "#666" }}>
                {exportUsageStats.currentCount}/{exportUsageStats.monthlyLimit}
              </span>
            </div>

            <div className="modal-content">
              <div className="export-inputs">
                <input
                  type="text"
                  value={fileName}
                  onChange={(e) => setFileName(e.target.value)}
                  onFocus={handleInputFocus}
                />
                <select
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value)}
                >
                  <option value="png">PNG</option>
                  <option value="jpeg">JPEG</option>
                  <option value="webp">WebP</option>
                </select>
              </div>

              <div className="export-checkbox">
                <input
                  type="checkbox"
                  checked={transparentBg}
                  onChange={(e) => setTransparentBg(e.target.checked)}
                />
                <label>Transparent Background</label>
              </div>
              <div className="export-button">
                <button onClick={handleExportClick}>Export Canvas</button>
              </div>
              <button className="save-button" onClick={handleSave}>
                Save
              </button>

              <div className="dashboard-button">
                <button
                  onClick={() => {
                    // Explicitly reset canvas state if necessary
                    setHistory({
                      past: [],
                      present: {
                        bgFiles: [],
                        modelFiles: [],
                        textBoxes: [],
                        shapes: [],
                        layers: [],
                        canvasBackgroundColor: { r: 255, g: 255, b: 255, a: 1 },
                      },
                      future: [],
                    });

                    // Navigate to Dashboard
                    navigate("/dashboard", { replace: true });
                  }}
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Image Sidebar */}
        <div
          ref={sidebarRef2}
          className={`three-canvas-sidebar-2 ${activeSidebar === 2 ? "expanded" : ""}`}
          style={{ display: activeSidebar === 2 ? "block" : "none" }}
        >
          <div className="sidebar-content-wrapper">
            <h1>Images</h1>
            <hr></hr>
            <div className="buttons-container">
              <button onClick={() => bgInputRef.current.click()}>
                Upload Image
              </button>
              <input
                ref={bgInputRef}
                id="bgUpload"
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={handleBgUpload}
              />
            </div>
            <button
              id="shutterstock-button"
              onClick={() =>
                window.open("https://www.shutterstock.com/", "_blank")
              }
            ></button>

            <hr />
            <h2>Position</h2>
            <div className="position-customization">
              <div className="move-buttons-shapes">
                <div className="move-buttons-horizontal-shapes">
                  <button
                    onClick={handleMoveObjectLeft}
                    disabled={!selectedContainer}
                    id="left-align"
                  >
                    <img src="left-align.png" />
                  </button>
                  <button
                    onClick={handleMoveObjectCenterX}
                    disabled={!selectedContainer}
                    id="center-align"
                  >
                    <img src="v-center-align.png" />
                  </button>
                  <button
                    onClick={handleMoveObjectRight}
                    disabled={!selectedContainer}
                    id="right-align"
                  >
                    <img src="right-align.png" />
                  </button>
                </div>
                <div className="move-buttons-vertical-shapes">
                  <button
                    onClick={handleMoveObjectTop}
                    disabled={!selectedContainer}
                    id="left-align"
                  >
                    <img src="top-align.png" />
                  </button>
                  <button
                    onClick={handleMoveObjectCenterY}
                    disabled={!selectedContainer}
                    id="center-align"
                  >
                    <img src="h-center-align.png" />
                  </button>
                  <button
                    onClick={handleMoveObjectBottom}
                    disabled={!selectedContainer}
                    id="right-align"
                  >
                    <img src="bottom-align.png" />
                  </button>
                </div>
              </div>
              <div className="rotation-customization-container-shapes">
                <div className="rotation-customization-input-shapes">
                  <label>
                    <img src="angle.png" alt="" />
                  </label>
                  <input
                    type="number"
                    disabled={
                      !bgFiles.some((bg) => bg.id === selectedContainer)
                    }
                    onFocus={handleInputFocus}
                    value={
                      bgFiles.find((bg) => bg.id === selectedContainer)
                        ? Math.round(
                            bgFiles.find((bg) => bg.id === selectedContainer)
                              .rotation,
                          )
                        : 0
                    }
                    onChange={(e) => {
                      if (!isRotationUpdating) {
                        setObjectRotation(parseInt(e.target.value, 10), false);
                      }
                    }}
                    onBlur={(e) => setObjectRotation(parseInt(e.target.value, 10), true)}
                  />
                </div>
                <div className="rotation-customization-buttons-shapes">
                  <button id="left-align" onClick={rotateBy90Degrees}>
                    <img src="rotate.png" />
                  </button>
                  <button
                    id="center-align"
                    onClick={handleHorizontalFlip}
                    disabled={!selectedContainer}
                  >
                    <img src="v-flip.png" />
                  </button>
                  <button
                    id="right-align"
                    onClick={handleVerticalFlip}
                    disabled={!selectedContainer}
                  >
                    <img src="h-flip.png" />
                  </button>
                </div>
              </div>
            </div>
            <hr />
            <h2>Layout</h2>
            <div className="size-input-container-shapes">
              <div className="size-input-container-shapes-w">
                <label>W</label>
                <input
                  type="number"
                  disabled={!bgFiles.some((bg) => bg.id === selectedContainer)}
                  value={
                    bgFiles.find((bg) => bg.id === selectedContainer)
                      ? Math.round(
                          bgFiles.find((bg) => bg.id === selectedContainer).size
                            .width,
                        )
                      : ""
                  }
                  onChange={(e) =>
                    handleResizeObjectWidth(
                      parseInt(e.target.value, 10) -
                        bgFiles.find((bg) => bg.id === selectedContainer).size
                          .width,
                    )
                  }
                  step="1" // Ensures only whole numbers are allowed
                  onFocus={handleInputFocus}
                />
                <span className="suffix">px</span>
              </div>
              <div className="size-input-container-shapes-h">
                <label>H</label>
                <input
                  type="number"
                  disabled={!bgFiles.some((bg) => bg.id === selectedContainer)}
                  value={
                    bgFiles.find((bg) => bg.id === selectedContainer)
                      ? Math.round(
                          bgFiles.find((bg) => bg.id === selectedContainer).size
                            .height,
                        )
                      : ""
                  }
                  onChange={(e) =>
                    handleResizeObjectHeight(
                      parseInt(e.target.value, 10) -
                        bgFiles.find((bg) => bg.id === selectedContainer).size
                          .height,
                    )
                  }
                  step="1" // Ensures only whole numbers are allowed
                  onFocus={handleInputFocus}
                />
                <span className="suffix">px</span>
              </div>
              <button
                onClick={toggleLinkDimensions}
                style={
                  isLinked
                    ? LINKED_BUTTON_STYLE.active
                    : LINKED_BUTTON_STYLE.inactive
                }
              >
                <img
                  src="link.png"
                  alt="Link Dimensions"
                  title="Link Dimensions"
                />
              </button>
            </div>
            <hr />
            <div className="exposure-sliders">
              {bgFiles.some((bg) => bg.id === selectedContainer) && (
                <ImageExposureSlider
                  image={bgFiles.find((bg) => bg.id === selectedContainer)}
                  handleBgExposureChange={handleBgExposureChange}
                  showHeading={true}
                />
              )}
            </div>
          </div>
        </div>

        {/* 3D Models Sidebar */}
        <div
          ref={sidebarRef3}
          className={`three-canvas-sidebar-3 ${activeSidebar === 3 ? "expanded" : ""}`}
          style={{ display: activeSidebar === 3 ? "block" : "none" }}
        >
          <div className="sidebar-content-wrapper">
            <h1>Interactive Render</h1>
            <hr></hr>
            <div className="buttons-container">
              <button onClick={() => modelInputRef.current.click()}>
                Upload Render .glb
              </button>
              <input
                ref={modelInputRef}
                id="modelUpload"
                type="file"
                accept=".glb"
                multiple
                style={{ display: "none" }}
                onChange={handleModelUpload}
              />
            </div>

            <hr />
            <h2>Position</h2>
            <div className="position-customization">
              <div className="move-buttons-shapes">
                <div className="move-buttons-horizontal-shapes">
                  <button
                    onClick={handleMoveObjectLeft}
                    disabled={!selectedContainer}
                    id="left-align"
                  >
                    <img src="left-align.png" />
                  </button>
                  <button
                    onClick={handleMoveObjectCenterX}
                    disabled={!selectedContainer}
                    id="center-align"
                  >
                    <img src="v-center-align.png" />
                  </button>
                  <button
                    onClick={handleMoveObjectRight}
                    disabled={!selectedContainer}
                    id="right-align"
                  >
                    <img src="right-align.png" />
                  </button>
                </div>
                <div className="move-buttons-vertical-shapes">
                  <button
                    onClick={handleMoveObjectTop}
                    disabled={!selectedContainer}
                    id="left-align"
                  >
                    <img src="top-align.png" />
                  </button>
                  <button
                    onClick={handleMoveObjectCenterY}
                    disabled={!selectedContainer}
                    id="center-align"
                  >
                    <img src="h-center-align.png" />
                  </button>
                  <button
                    onClick={handleMoveObjectBottom}
                    disabled={!selectedContainer}
                    id="right-align"
                  >
                    <img src="bottom-align.png" />
                  </button>
                </div>
              </div>

              <div className="rotation-customization-container-shapes">
                <div className="rotation-customization-input-shapes">
                  <label>
                    <img src="angle.png" alt="" />
                  </label>
                  <input
                    type="number"
                    onFocus={handleInputFocus}
                    disabled={
                      !modelFiles.some(
                        (model) => model.id === selectedContainer,
                      )
                    }
                    value={
                      modelFiles.find((model) => model.id === selectedContainer)
                        ? Math.round(
                            modelFiles.find(
                              (model) => model.id === selectedContainer,
                            ).rotation,
                          )
                        : 0
                    }
                    onChange={(e) => {
                      if (!isRotationUpdating) {
                        setObjectRotation(parseInt(e.target.value, 10), false);
                      }
                    }}
                    onBlur={(e) => setObjectRotation(parseInt(e.target.value, 10), true)}
                  />
                </div>
                <div className="rotation-customization-buttons-shapes">
                  <button id="left-align" onClick={rotateBy90Degrees}>
                    <img src="rotate.png" />
                  </button>
                  <button
                    id="center-align"
                    onClick={handleHorizontalFlip}
                    disabled={!selectedContainer}
                  >
                    <img src="v-flip.png" />
                  </button>
                  <button
                    id="right-align"
                    onClick={handleVerticalFlip}
                    disabled={!selectedContainer}
                  >
                    <img src="h-flip.png" />
                  </button>
                </div>
              </div>
            </div>
            <hr />
            <h2>Layout</h2>
            <div className="size-input-container-shapes">
              <div className="size-input-container-shapes-h">
                <label>W</label>
                <input
                  type="number"
                  disabled={
                    !modelFiles.some((model) => model.id === selectedContainer)
                  }
                  onFocus={handleInputFocus}
                  value={
                    modelFiles.find((model) => model.id === selectedContainer)
                      ? Math.round(
                          modelFiles.find(
                            (model) => model.id === selectedContainer,
                          ).size.width,
                        )
                      : ""
                  }
                  onChange={(e) =>
                    handleResizeObjectWidth(
                      parseInt(e.target.value, 10) -
                        modelFiles.find(
                          (model) => model.id === selectedContainer,
                        ).size.width,
                    )
                  }
                />
                <span className="suffix">px</span>
              </div>
              <div className="size-input-container-shapes-w">
                <label>H</label>
                <input
                  type="number"
                  onFocus={handleInputFocus}
                  disabled={
                    !modelFiles.some((model) => model.id === selectedContainer)
                  }
                  value={
                    modelFiles.find((model) => model.id === selectedContainer)
                      ? Math.round(
                          modelFiles.find(
                            (model) => model.id === selectedContainer,
                          ).size.height,
                        )
                      : ""
                  }
                  onChange={(e) =>
                    handleResizeObjectHeight(
                      parseInt(e.target.value, 10) -
                        modelFiles.find(
                          (model) => model.id === selectedContainer,
                        ).size.height,
                    )
                  }
                />
                <span className="suffix">px</span>
              </div>

              <button
                onClick={toggleLinkDimensions}
                style={
                  isLinked
                    ? LINKED_BUTTON_STYLE.active
                    : LINKED_BUTTON_STYLE.inactive
                }
              >
                <img
                  src="link.png"
                  alt="Link Dimensions"
                  title="Link Dimensions"
                />
              </button>
            </div>
            <hr />
            <h2>Appearance</h2>
            {/* Color Picker */}

            <div className="exposure-sliders">
              {modelFiles.map((model, index) => (
                <ExposureSlider
                  key={model.id}
                  model={model}
                  handleExposureChange={handleExposureChange}
                  showHeading={index === 0}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Text Editor Sidebar */}
        <div
          ref={sidebarRef4}
          className={`three-canvas-sidebar-4 ${activeSidebar === 4 ? "expanded" : ""}`}
          style={{ display: activeSidebar === 4 ? "block" : "none" }}
        >
          <div className="sidebar-content-wrapper">
            <h1>Text Editor</h1>
            <hr />
            <div className="buttons-container">
              <button onClick={addTextBox}>Add Text Box</button>
            </div>
            <hr />

            <h2>Typography</h2>
            <div className="font-customization-container">
              <div className="font-picker">
                <FontPickerBar
                  fontFamily={fontFamily}
                  onFontChange={handleFontFamilyChange}
                  fontFamilies={fontFamilies}
                />
              </div>
              {/* Font Size and Font Weight/Style in a single row */}
              <div className="font-size-font-weight">
                <input
                  type="number"
                  value={parseInt(fontSize)}
                  disabled={
                    !textBoxes.some((box) => box.id === selectedContainer)
                  }
                  onFocus={handleInputFocus}
                  onChange={(e) => handleFontSizeChange(e, false)} // Immediate change
                  onBlur={(e) => handleFontSizeChange(e, true)} // Final change for history
                  style={{ width: "100%" }}
                />
                <select
                  value={selectedFontWeight} // Fallback to "Normal"
                  onChange={(e) =>
                    handleFontWeightChange(
                      selectedContainer,
                      e.target.value,
                      false,
                    )
                  } // Immediate change
                  onBlur={() =>
                    handleFontWeightChange(
                      selectedContainer,
                      selectedFontWeight,
                      true,
                    )
                  } // Final change for history
                  disabled={
                    !textBoxes.some((box) => box.id === selectedContainer)
                  }
                >
                  {fontWeights.map((weight) => (
                    <option key={weight.value} value={weight.value}>
                      {weight.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="letter-spacing-container">
                <div className="h-letter-spacing">
                  <label>
                    <img src="h-letter-spacing.png" alt="Letter Spacing" />
                  </label>

                  <input
                    type="number"
                    value={
                      textBoxes.find((box) => box.id === selectedContainer)
                        ?.letterSpacing || 0
                    }
                    onChange={
                      (e) =>
                        handleLetterSpacingChange(
                          selectedContainer,
                          e.target.value,
                          false,
                        ) // Real-time update
                    }
                    onBlur={
                      (e) =>
                        handleLetterSpacingChange(
                          selectedContainer,
                          e.target.value,
                          true,
                        ) // Save to history
                    }
                    id="input-field"
                    min="0"
                    max="100"
                    onFocus={handleInputFocus}
                    disabled={
                      !textBoxes.some((box) => box.id === selectedContainer)
                    }
                  />
                </div>

                <div className="v-letter-spacing">
                  <label>
                    <img src="v-letter-spacing.png" alt="Line Height" />
                  </label>
                  <input
                    type="number"
                    value={
                      textBoxes.find((box) => box.id === selectedContainer)
                        ?.lineHeight || 100
                    }
                    onChange={
                      (e) =>
                        handleLineHeightChange(
                          selectedContainer,
                          parseInt(e.target.value, 10),
                          false,
                        ) // Real-time update
                    }
                    onBlur={
                      (e) =>
                        handleLineHeightChange(
                          selectedContainer,
                          parseInt(e.target.value, 10),
                          true,
                        ) // Save to history
                    }
                    id="input-field"
                    min="0"
                    max="200"
                    onFocus={handleInputFocus}
                    disabled={
                      !textBoxes.some((box) => box.id === selectedContainer)
                    }
                  />
                </div>
              </div>

              {/* Text Alignment (Left, Center, Right) */}
              <div className="letter-align-buttons-container">
                <button
                  className={`alignment-button ${textBoxes.find((box) => box.id === selectedContainer)?.textAlign === "left" ? "active" : ""}`}
                  onClick={() =>
                    handleTextAlignChange(selectedContainer, "left", true)
                  } // Save to history immediately
                  disabled={
                    !textBoxes.some((box) => box.id === selectedContainer)
                  }
                >
                  <img src="left-align-letter.png" alt="Align Left" />
                </button>
                <button
                  className={`alignment-button ${textBoxes.find((box) => box.id === selectedContainer)?.textAlign === "center" ? "active" : ""}`}
                  onClick={() =>
                    handleTextAlignChange(selectedContainer, "center", true)
                  }
                  disabled={
                    !textBoxes.some((box) => box.id === selectedContainer)
                  }
                >
                  <img src="center-align-letter.png" alt="Align Center" />
                </button>
                <button
                  className={`alignment-button ${textBoxes.find((box) => box.id === selectedContainer)?.textAlign === "right" ? "active" : ""}`}
                  onClick={() =>
                    handleTextAlignChange(selectedContainer, "right", true)
                  }
                  disabled={
                    !textBoxes.some((box) => box.id === selectedContainer)
                  }
                >
                  <img src="right-align-letter.png" alt="Align Right" />
                </button>
                <button
                  className={`alignment-button ${textBoxes.find((box) => box.id === selectedContainer)?.verticalAlign === "top" ? "active" : ""}`}
                  onClick={() =>
                    handleVerticalAlignChange(selectedContainer, "top", true)
                  }
                  disabled={
                    !textBoxes.some((box) => box.id === selectedContainer)
                  }
                >
                  <img src="top-align-letter.png" alt="Align Top" />
                </button>
                <button
                  className={`alignment-button ${textBoxes.find((box) => box.id === selectedContainer)?.verticalAlign === "middle" ? "active" : ""}`}
                  onClick={() =>
                    handleVerticalAlignChange(selectedContainer, "middle", true)
                  }
                  disabled={
                    !textBoxes.some((box) => box.id === selectedContainer)
                  }
                >
                  <img src="middle-align-letter.png" alt="Align Middle" />
                </button>
                <button
                  className={`alignment-button ${textBoxes.find((box) => box.id === selectedContainer)?.verticalAlign === "bottom" ? "active" : ""}`}
                  onClick={() =>
                    handleVerticalAlignChange(selectedContainer, "bottom", true)
                  }
                  disabled={
                    !textBoxes.some((box) => box.id === selectedContainer)
                  }
                >
                  <img src="bottom-align-letter.png" alt="Align Bottom" />
                </button>
              </div>
            </div>

            <hr />
            <h2>Position</h2>
            <div className="position-customization">
              <div className="move-buttons-shapes">
                <div className="move-buttons-horizontal-shapes">
                  <button
                    onClick={handleMoveObjectLeft}
                    disabled={!selectedContainer}
                    id="left-align"
                  >
                    <img src="left-align.png" />
                  </button>
                  <button
                    onClick={handleMoveObjectCenterX}
                    disabled={!selectedContainer}
                    id="center-align"
                  >
                    <img src="v-center-align.png" />
                  </button>
                  <button
                    onClick={handleMoveObjectRight}
                    disabled={!selectedContainer}
                    id="right-align"
                  >
                    <img src="right-align.png" />
                  </button>
                </div>
                <div className="move-buttons-vertical-shapes">
                  <button
                    onClick={handleMoveObjectTop}
                    disabled={!selectedContainer}
                    id="left-align"
                  >
                    <img src="top-align.png" />
                  </button>
                  <button
                    onClick={handleMoveObjectCenterY}
                    disabled={!selectedContainer}
                    id="center-align"
                  >
                    <img src="h-center-align.png" />
                  </button>
                  <button
                    onClick={handleMoveObjectBottom}
                    disabled={!selectedContainer}
                    id="right-align"
                  >
                    <img src="bottom-align.png" />
                  </button>
                </div>
              </div>
              <div className="rotation-customization-container-shapes">
                <div className="rotation-customization-input-shapes">
                  <label>
                    <img src="angle.png" alt="" />
                  </label>
                  <input
                    type="number"
                    onFocus={handleInputFocus}
                    disabled={
                      !textBoxes.some(
                        (textBox) => textBox.id === selectedContainer,
                      )
                    }
                    value={
                      textBoxes.find(
                        (textBox) => textBox.id === selectedContainer,
                      )
                        ? Math.round(
                            textBoxes.find(
                              (textBox) => textBox.id === selectedContainer,
                            ).rotation,
                          )
                        : 0
                    }
                    onChange={(e) => {
                      if (!isRotationUpdating) {
                        setObjectRotation(parseInt(e.target.value, 10), false);
                      }
                    }}
                    onBlur={(e) => setObjectRotation(parseInt(e.target.value, 10), true)}
                  />
                </div>
                <div className="rotation-customization-buttons-shapes">
                  <button id="left-align" onClick={rotateBy90Degrees}>
                    <img src="rotate.png" />
                  </button>
                  <button
                    id="center-align"
                    onClick={handleHorizontalFlip}
                    disabled={!selectedContainer}
                  >
                    <img src="v-flip.png" />
                  </button>
                  <button
                    id="right-align"
                    onClick={handleVerticalFlip}
                    disabled={!selectedContainer}
                  >
                    <img src="h-flip.png" />
                  </button>
                </div>
              </div>
            </div>
            <hr />

            <div className="size-customisation-container">
              <h2>Layout</h2>
              <div className="size-input-container-shapes">
                <div className="size-input-container-shapes-w">
                  <label>W</label>
                  <input
                    type="number"
                    onFocus={handleInputFocus}
                    disabled={
                      !textBoxes.some(
                        (textBox) => textBox.id === selectedContainer,
                      )
                    }
                    value={
                      textBoxes.find(
                        (textBox) => textBox.id === selectedContainer,
                      )
                        ? Math.round(
                            textBoxes.find(
                              (textBox) => textBox.id === selectedContainer,
                            ).size.width,
                          )
                        : ""
                    }
                    onChange={(e) =>
                      handleResizeObjectWidth(
                        parseInt(e.target.value, 10) -
                          textBoxes.find(
                            (textBox) => textBox.id === selectedContainer,
                          ).size.width,
                      )
                    }
                  />
                  <span className="suffix">px</span>
                </div>
                <div className="size-input-container-shapes-h">
                  <label>H</label>
                  <input
                    type="number"
                    onFocus={handleInputFocus}
                    disabled={
                      !textBoxes.some(
                        (textBox) => textBox.id === selectedContainer,
                      )
                    }
                    value={
                      textBoxes.find(
                        (textBox) => textBox.id === selectedContainer,
                      )
                        ? Math.round(
                            textBoxes.find(
                              (textBox) => textBox.id === selectedContainer,
                            ).size.height,
                          )
                        : ""
                    }
                    onChange={(e) =>
                      handleResizeObjectHeight(
                        parseInt(e.target.value, 10) -
                          textBoxes.find(
                            (textBox) => textBox.id === selectedContainer,
                          ).size.height,
                      )
                    }
                  />
                  <span className="suffix">px</span>
                </div>

                <button
                  onClick={toggleLinkDimensions}
                  style={
                    isLinked
                      ? LINKED_BUTTON_STYLE.active
                      : LINKED_BUTTON_STYLE.inactive
                  }
                >
                  <img
                    src="link.png"
                    alt="Link Dimensions"
                    title="Link Dimensions"
                  />
                </button>
              </div>
            </div>
            <hr />
            <h2>Appearance</h2>
            <ColorPickerBar
              color={fontColor} // Pass the current font color
              onColorChange={(newColor, saveOnEnd) => {
                setFontColor(newColor);
                handleFontColorChange(
                  selectedContainer,
                  newColor,
                  textOpacity,
                  saveOnEnd,
                );
              }}
            />
          </div>
        </div>

        {/* Shape Editor Sidebar */}
        <div
          ref={sidebarRef5}
          className={`three-canvas-sidebar-5 ${activeSidebar === 5 ? "expanded" : ""}`}
          style={{ display: activeSidebar === 5 ? "block" : "none" }}
        >
          <div className="sidebar-content-wrapper">
            <h1>Shape Editor</h1>
            <hr></hr>
            <div className="buttons-container-shapes">
              <button
                onClick={() => addShape("square")}
                aria-label="Add Square"
              >
                <svg width="100%" height="100%" viewBox="0 0 24 24">
                  <rect
                    x="2"
                    y="2"
                    width="20"
                    height="20"
                    style={{ fill: "currentColor" }}
                  />
                </svg>
              </button>
              <button
                onClick={() => addShape("circle")}
                aria-label="Add Circle"
              >
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    style={{ fill: "currentColor" }}
                  />
                </svg>
              </button>
              <button
                onClick={() => addShape("triangle")}
                aria-label="Add Triangle"
              >
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <polygon
                    points="12,2 22,22 2,22"
                    style={{ fill: "currentColor" }}
                  />
                </svg>
              </button>
            </div>

            <>
              <hr />
              <h2>Position</h2>
              <div className="position-customization">
                <div className="move-buttons-shapes">
                  <div className="move-buttons-horizontal-shapes">
                    <button
                      onClick={handleMoveObjectLeft}
                      disabled={!selectedContainer}
                      id="left-align"
                    >
                      <img src="left-align.png" />
                    </button>
                    <button
                      onClick={handleMoveObjectCenterX}
                      disabled={!selectedContainer}
                      id="center-align"
                    >
                      <img src="v-center-align.png" />
                    </button>
                    <button
                      onClick={handleMoveObjectRight}
                      disabled={!selectedContainer}
                      id="right-align"
                    >
                      <img src="right-align.png" />
                    </button>
                  </div>
                  <div className="move-buttons-vertical-shapes">
                    <button
                      onClick={handleMoveObjectTop}
                      disabled={!selectedContainer}
                      id="left-align"
                    >
                      <img src="top-align.png" />
                    </button>
                    <button
                      onClick={handleMoveObjectCenterY}
                      disabled={!selectedContainer}
                      id="center-align"
                    >
                      <img src="h-center-align.png" />
                    </button>
                    <button
                      onClick={handleMoveObjectBottom}
                      disabled={!selectedContainer}
                      id="right-align"
                    >
                      <img src="bottom-align.png" />
                    </button>
                  </div>
                </div>
                <div className="rotation-customization-container-shapes">
                  <div className="rotation-customization-input-shapes">
                    <label>
                      <img src="angle.png" alt="" />
                    </label>
                    <input
                      type="number"
                      disabled={
                        !shapes.some((shape) => shape.id === selectedContainer)
                      }
                      onFocus={handleInputFocus}
                      value={
                        shapes.find((shape) => shape.id === selectedContainer)
                          ? Math.round(
                              shapes.find(
                                (shape) => shape.id === selectedContainer,
                              ).rotation,
                            )
                          : 0
                      }
                      onChange={(e) => {
                        if (!isRotationUpdating) {
                          setObjectRotation(parseInt(e.target.value, 10), false);
                        }
                      }}
                      onBlur={(e) => setObjectRotation(parseInt(e.target.value, 10), true)}
                    />
                  </div>
                  <div className="rotation-customization-buttons-shapes">
                    <button id="left-align" onClick={rotateBy90Degrees}>
                      <img src="rotate.png" />
                    </button>
                    <button
                      id="center-align"
                      onClick={handleHorizontalFlip}
                      disabled={!selectedContainer}
                    >
                      <img src="v-flip.png" />
                    </button>
                    <button
                      id="right-align"
                      onClick={handleVerticalFlip}
                      disabled={!selectedContainer}
                    >
                      <img src="h-flip.png" />
                    </button>
                  </div>
                </div>
              </div>

              <hr></hr>
              <h2>Layout</h2>
              <div className="size-input-container-shapes">
                <div className="size-input-container-shapes-w">
                  <label>W</label>
                  <input
                    type="number"
                    disabled={
                      !shapes.some((shape) => shape.id === selectedContainer)
                    }
                    onFocus={handleInputFocus}
                    value={
                      shapes.find((shape) => shape.id === selectedContainer)
                        ? Math.round(
                            shapes.find(
                              (shape) => shape.id === selectedContainer,
                            ).size.width,
                          )
                        : ""
                    }
                    onChange={(e) =>
                      handleResizeObjectWidth(
                        parseInt(e.target.value, 10) -
                          shapes.find((shape) => shape.id === selectedContainer)
                            .size.width,
                      )
                    }
                    onBlur={(e) =>
                      handleResizeObjectWidth(
                        parseInt(e.target.value, 10) -
                          shapes.find((shape) => shape.id === selectedContainer)
                            .size.width,
                        true, // Save to history on blur
                      )
                    }
                  />
                  <span className="suffix">px</span>
                </div>
                <div className="size-input-container-shapes-h">
                  <label>H</label>
                  <input
                    type="number"
                    disabled={
                      !shapes.some((shape) => shape.id === selectedContainer)
                    }
                    onFocus={handleInputFocus}
                    value={
                      shapes.find((shape) => shape.id === selectedContainer)
                        ? Math.round(
                            shapes.find(
                              (shape) => shape.id === selectedContainer,
                            ).size.height,
                          )
                        : ""
                    }
                    onChange={(e) =>
                      handleResizeObjectHeight(
                        parseInt(e.target.value, 10) -
                          shapes.find((shape) => shape.id === selectedContainer)
                            .size.height,
                      )
                    }
                    onBlur={(e) =>
                      handleResizeObjectHeight(
                        parseInt(e.target.value, 10) -
                          shapes.find((shape) => shape.id === selectedContainer)
                            .size.height,
                        true, // Save to history on blur
                      )
                    }
                  />
                  <span className="suffix">px</span>
                </div>

                <button
                  onClick={toggleLinkDimensions}
                  style={
                    isLinked
                      ? LINKED_BUTTON_STYLE.active
                      : LINKED_BUTTON_STYLE.inactive
                  }
                >
                  <img
                    src="link.png"
                    alt="Link Dimensions"
                    title="Link Dimensions"
                  />
                </button>
              </div>
              <hr />
              <h2>Appearance</h2>

              <ColorPickerBar
                color={shapeColor} // Pass the current shape color
                onColorChange={(newColor, saveOnEnd) => {
                  setShapeColor(newColor);
                  handleShapeColorChange(
                    selectedContainer,
                    newColor,
                    shapeOpacity,
                    saveOnEnd,
                  );
                }}
              />
            </>
          </div>
        </div>

        {/* Layers Panel Sidebar */}
        <div
          ref={sidebarRef6}
          className={`three-canvas-sidebar-6 ${activeSidebar === 6 ? "expanded" : ""}`}
          style={{ display: activeSidebar === 6 ? "block" : "none" }}
        >
          <div className="sidebar-content-wrapper">
            <h1>Layers Panel</h1>
            <hr></hr>
            <LayerPanel
              layers={layers}
              onSelectLayer={handleSelectLayer}
              onMoveLayer={handleMoveLayer}
              onRenameLayer={handleRenameLayer}
              onToggleVisibility={handleToggleVisibility}
              onLayerHover={handleLayerHover}
              onLayerHoverEnd={handleLayerHoverEnd}
            />
          </div>
        </div>

        {/* Assets Sidebar */}
        <div
          ref={sidebarRef7}
          className={`three-canvas-sidebar-7 ${activeSidebar === 7 ? "expanded" : ""}`}
          style={{ display: activeSidebar === 7 ? "block" : "none" }}
        >
          <div className="sidebar-content-wrapper">
            <h1>Assets</h1>
            <hr />
            <div className="assets-filter-bar-7">
              {/* Left: Search input */}
              <input
                type="text"
                className="assets-search-input-7"
                placeholder="Search assets..."
                value={assetsSearchTerm}
                onChange={(e) => setAssetsSearchTerm(e.target.value)}
              />

              {/* Right: "burger bar" filter dropdown */}
              <div className="assets-filter-dropdown-7">
                <button
                  className="burger-button-7"
                  onClick={toggleFilterDropdown}
                >
                  ☰
                </button>
                {isFilterDropdownOpen && (
                  <ul className="filter-dropdown-menu-7">
                    <li onClick={() => handleFilterClick("all")}>All Assets</li>
                    <li onClick={() => handleFilterClick("images")}>Images</li>
                    <li onClick={() => handleFilterClick("models")}>
                      3D Models
                    </li>
                  </ul>
                )}
              </div>
            </div>

            {filteredAssets.length === 0 && (
              <div className="empty-assets-message">
                Your uploaded assets will appear here
              </div>
            )}

            {/* Then your scrollable container below */}
            <div className="assets-scroller-7">
              {filteredAssets.map((asset) => {
                const is3DModel =
                  asset.path?.toLowerCase().endsWith(".glb") ||
                  asset.path?.toLowerCase().endsWith(".fbx") ||
                  asset.path?.toLowerCase().endsWith(".obj");

                // Use thumbnail URL for preview
                const previewUrl = asset.thumbnailUrl || "placeholder.jpg";

                return (
                  <div className="asset-card-7" key={asset.id}>
                    <div
                      className="asset-preview-7"
                      style={{
                        backgroundImage: `url(${previewUrl})`,
                        backgroundColor: "white",
                      }}
                      onClick={() => handleAddAssetToCanvas(asset)}
                    />
                    <p className="asset-name-7">
                      {truncateAssetName(asset.name || "Untitled")}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Canvas Area */}
        <div
          className="three-canvas-area-background"
          onMouseDown={handleCanvasClick}
        >
          <div
            ref={captureDivRef}
            key={canvasKey}
            tabIndex={-1} // Makes the div focusable
            className={`three-canvas-area ${isSliderShifted ? "shifted" : ""}`}
            style={{
              width: canvasSize.width,
              height: canvasSize.height,
              transform: `scale(${zoom})`,
              backgroundColor: `rgba(${canvasBackgroundColor.r}, ${canvasBackgroundColor.g}, ${canvasBackgroundColor.b}, ${canvasBackgroundColor.a})`, // Apply RGBA color
            }}
          >
            {/* Render layers in the correct z-index order */}
            {reorderLayersByZIndex(layers).map((layer, index) => {
              // Render based on object type
              if (bgFiles.some((bg) => bg.id === layer.id)) {
                const bg = bgFiles.find((file) => file.id === layer.id);
                if (!bg.isVisible) return null;
                const refIndex = index;
                // Ensure the ref is initialized
                if (!moveableRefs.current[refIndex]) {
                  moveableRefs.current[refIndex] = React.createRef();
                }

                return (
                  <MoveableContainer
                    key={bg.id}
                    ref={moveableRefs.current[refIndex]}
                    verticalGuidelines={verticalGuidelines}
                    horizontalGuidelines={horizontalGuidelines}
                    elementGuidelines={getElementGuidelines()}
                    position={bg.position}
                    size={bg.size}
                    rotation={bg.rotation}
                    isSelected={bg.isSelected}
                    isLayerHovered={hoveredLayerId === bg.id}
                    onClick={() => handleObjectClick(bg.id)}
                    zIndex={bg.zIndex}
                    onUpdate={(updatedState) =>
                      handleBgFileUpdate({ id: bg.id, ...updatedState })
                    }
                    moveBy={
                      selectedContainer === bg.id ? moveBy : { x: 0, y: 0 }
                    }
                    resizeBy={
                      selectedContainer === bg.id
                        ? resizeBy
                        : { width: 0, height: 0 }
                    }
                    flip={bg.flip} // Pass the flip state as props
                    onTransformationChange={(type, updatedProperties) =>
                      handleTransformationChange(type, updatedProperties)
                    }
                    onArrowKeyNudge={updateObjectAndLayerPositions} // Pass the function as a prop
                  >
                    <img
                      crossOrigin="anonymous"
                      src={bg.currentDataUrl}
                      alt={`background-${bg.id}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        // filter: `brightness(${bg.exposure})`,
                      }}
                    />
                  </MoveableContainer>
                );
              }
              if (modelFiles.some((model) => model.id === layer.id)) {
                const model = modelFiles.find((file) => file.id === layer.id);
                if (!model.isVisible) return null;
                const refIndex = bgFiles.length + index;
                // Ensure the ref is initialized
                if (!moveableRefs.current[refIndex]) {
                  moveableRefs.current[refIndex] = React.createRef();
                }
                return (
                  <MoveableContainer
                    key={model.id}
                    ref={moveableRefs.current[refIndex]}
                    verticalGuidelines={verticalGuidelines}
                    horizontalGuidelines={horizontalGuidelines}
                    elementGuidelines={getElementGuidelines()}
                    position={model.position}
                    size={model.size}
                    rotation={model.rotation}
                    isSelected={model.isSelected}
                    isLayerHovered={hoveredLayerId === model.id}
                    onClick={() => handleObjectClick(model.id)}
                    zIndex={model.zIndex}
                    onUpdate={(updatedState) =>
                      handleModelUpdate({ id: model.id, ...updatedState })
                    }
                    moveBy={
                      selectedContainer === model.id ? moveBy : { x: 0, y: 0 }
                    }
                    resizeBy={
                      selectedContainer === model.id
                        ? resizeBy
                        : { width: 0, height: 0 }
                    }
                    flip={model.flip} // Pass the flip state as props
                    onTransformationChange={(type, updatedProperties) =>
                      handleTransformationChange(type, updatedProperties)
                    }
                    onArrowKeyNudge={updateObjectAndLayerPositions} // Pass the function as a prop
                  >
                    <Canvas
                      ref={canvasRef}
                      style={{
                        alignItems: "center",
                        justifyContent: "center",
                        width: `${model.size.width}px`,
                        height: `${model.size.height}px`,
                        transform: `scale(${1 / zoom})`,
                      }}
                      gl={{
                        preserveDrawingBuffer: true,
                        antialias: true, // Enable anti-aliasing for smoother edges
                        multisampling: 8, // Enable 8x MSAA
                        precision: "highp", // Use high precision for rendering
                        powerPreference: "high-performance",
                        // logarithmicDepthBuffer: true,
                      }}
                      //  camera={{ fov: 5, aspect: model.size.width / model.size.height }}
                      pixelratio={window.devicePixelRatio * 2} // Limits pixel ratio for better performance
                      shadows // Enable shadows if needed
                      tonemapping={THREE.ACESFilmicToneMapping}
                      outputcolorspace={THREE.LinearSRGBColorSpace}
                    >
                      <GLBViewerContent
                        fileUrl={model.url}
                        scale={zoom || 1}
                        exposure={model.exposure}
                        lightColor={model.lightColor}
                        controlsState={model.controlsState}
                        setControlsState={
                          (newControlsState) =>
                            handleModelUpdate({
                              id: model.id,
                              controlsState: newControlsState,
                            }) // Update only this model's controls state
                        }
                        setShowControlsIndicator={setShowControlsIndicator}
                        dontShowAgain={dontShowAgain}
                        setShowInteractiveModal={setShowInteractiveModal}
                      />
                    </Canvas>
                  </MoveableContainer>
                );
              }
              if (textBoxes.some((textBox) => textBox.id === layer.id)) {
                const textBox = textBoxes.find((box) => box.id === layer.id);
                if (!textBox.isVisible) return null;
                const refIndex = bgFiles.length + modelFiles.length + index;
                // Ensure the ref is initialized
                if (!moveableRefs.current[refIndex]) {
                  moveableRefs.current[refIndex] = React.createRef();
                }
                return (
                  <MoveableContainer
                    key={textBox.id}
                    ref={moveableRefs.current[refIndex]}
                    verticalGuidelines={verticalGuidelines}
                    horizontalGuidelines={horizontalGuidelines}
                    elementGuidelines={getElementGuidelines()}
                    position={textBox.position}
                    size={textBox.size}
                    rotation={textBox.rotation}
                    isSelected={textBox.isSelected}
                    isLayerHovered={hoveredLayerId === textBox.id}
                    onClick={() => handleObjectClick(textBox.id)}
                    zIndex={textBox.zIndex}
                    onUpdate={(updatedState) =>
                      handleTextBoxUpdate({ id: textBox.id, ...updatedState })
                    }
                    isTextbox={true}
                    moveBy={
                      selectedContainer === textBox.id ? moveBy : { x: 0, y: 0 }
                    }
                    resizeBy={
                      selectedContainer === textBox.id
                        ? resizeBy
                        : { width: 0, height: 0 }
                    }
                    flip={textBox.flip} // Pass the flip state as props
                    onTransformationChange={(type, updatedProperties) =>
                      handleTransformationChange(type, updatedProperties)
                    }
                    onArrowKeyNudge={updateObjectAndLayerPositions} // Pass the function as a prop
                    selectedContainer={textBox.id}
                    onBlur={handleTextboxBlur} // Pass the onBlur function
                  >
                    <canvas
                      id={`fabricCanvas-${textBox.id}`}
                      style={{
                        width: textBox.size.width,
                        height: textBox.size.height,
                        pointerEvents: "auto",
                        paddingLeft: "5px",
                        paddingRight: "5px",
                      }}
                      ref={(canvasElement) => {
                        if (canvasElement && !canvasElement.fabricInitialized) {
                          const fabricCanvas = new fabric.Canvas(
                            canvasElement,
                            {
                              width: textBox.size.width,
                              height: textBox.size.height,
                            },
                          );
                          fabricCanvas.selection = false; // disable group selection
                          // Add a Fabric.js textbox to the canvas
                          const fabricTextBox = new fabric.Textbox(
                            textBox.content || "Insert text here",
                            {
                              left: 0,
                              top: 0,
                              width: textBox.size.width, // Set textbox to full canvas width
                              fontSize: parseInt(textBox.fontSize, 10),
                              fontFamily: textBox.fontFamily, // Default font
                              fontWeight: textBox.fontWeight, // Use fontWeight from state
                              charSpacing: (textBox.letterSpacing / 100) * 1000, // Initialize letter spacing
                              lineHeight: textBox.lineHeight / 100, // Initialize line height
                              textAlign: textBox.textAlign, // Initialize text alignment
                              fill: textBox.color,
                              selectable: false,
                              editable: true,
                              splitByGrapheme: true, // Enable splitting by individual letters
                              objectCaching: false, // This helps in rendering the overflow text without caching
                              hasControls: false, // Enable controls for resizing
                              noScaleCache: true, // Keeps the text sharp while scaling
                              hasBorders: false,
                              padding: 69,
                            },
                          );

                          const containerHeight = textBox.size.height; // Assuming the canvas height
                          const textHeight = fabricTextBox.height;
                          let newTop;
                          switch (textBox.verticalAlign) {
                            case "top":
                              newTop = 0;
                              fabricTextBox.set({ originY: "top" });
                              break;
                            case "middle":
                              newTop = (containerHeight - textHeight) / 2;
                              fabricTextBox.set({ originY: "top" });
                              break;
                            case "bottom":
                              newTop = containerHeight - textHeight;
                              fabricTextBox.set({ originY: "bottom" });
                              break;
                            default:
                              newTop = 0;
                          }

                          fabricTextBox.set({ top: newTop });
                          fabricCanvas.add(fabricTextBox);
                          fabricCanvas.renderAll();
                          canvasElement.fabricInitialized = true;
                          canvasElement.fabricCanvas = fabricCanvas;

                          if (fabricTextBox.fontWeight !== textBox.fontWeight) {
                            fabricTextBox.set({
                              fontWeight: textBox.fontWeight || "Normal",
                            });
                            fabricCanvas.renderAll(); // Trigger re-rendering of the canvas
                          }
                          fabricCanvas.on("text:changed", (e) => {
                            const updatedText = e.target.text;
                            handleTextChange(textBox.id, updatedText); // Update the content in your state
                          });
                          fabricCanvas.on("object:scaling", (e) => {
                            const updatedWidth =
                              e.target.width * e.target.scaleX;
                            const updatedHeight =
                              e.target.height * e.target.scaleY;
                            handleTextBoxUpdate({
                              id: textBox.id,
                              size: {
                                width: updatedWidth,
                                height: updatedHeight,
                              },
                            });
                          });
                        }
                      }}
                    />
                  </MoveableContainer>
                );
              }

              if (shapes.some((shape) => shape.id === layer.id)) {
                const shape = shapes.find((s) => s.id === layer.id);
                if (!shape.isVisible) return null;
                const refIndex =
                  bgFiles.length + modelFiles.length + textBoxes.length + index;
                if (!moveableRefs.current[refIndex]) {
                  moveableRefs.current[refIndex] = React.createRef();
                }
                return (
                  <MoveableContainer
                    key={shape.id}
                    ref={moveableRefs.current[refIndex]}
                    verticalGuidelines={verticalGuidelines}
                    horizontalGuidelines={horizontalGuidelines}
                    elementGuidelines={getElementGuidelines()}
                    position={shape.position}
                    size={shape.size}
                    rotation={shape.rotation}
                    isSelected={shape.isSelected}
                    isLayerHovered={hoveredLayerId === shape.id} // Add this new prop
                    onClick={() => handleObjectClick(shape.id)}
                    onUpdate={(updatedState) =>
                      handleShapeUpdate({ id: shape.id, ...updatedState })
                    }
                    zIndex={shape.zIndex}
                    moveBy={
                      selectedContainer === shape.id ? moveBy : { x: 0, y: 0 }
                    }
                    resizeBy={
                      selectedContainer === shape.id
                        ? resizeBy
                        : { width: 0, height: 0 }
                    }
                    flip={shape.flip} // Pass the flip state as props
                    onTransformationChange={(type, updatedProperties) =>
                      handleTransformationChange(type, updatedProperties)
                    }
                    onArrowKeyNudge={updateObjectAndLayerPositions} // Pass the function as a prop
                    selectedContainer={shape.id} // This was missing!
                    canvasZoom={zoom} // ADD THIS LINE
                  >
                    {shape.type === "square" && (
                      <svg
                        className="shape-square"
                        width="100%"
                        height="100%"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                      >
                        <rect
                          x="0"
                          y="0"
                          width="100"
                          height="100"
                          fill={shape.color}
                          fillOpacity={shape.opacity}
                        />
                      </svg>
                    )}
                    {shape.type === "circle" && (
                      <svg
                        className="shape-circle"
                        width="100%"
                        height="100%"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                      >
                        <circle
                          cx="50"
                          cy="50"
                          r="50"
                          fill={shape.color}
                          fillOpacity={shape.opacity}
                        />
                      </svg>
                    )}
                    {shape.type === "triangle" && (
                      <svg
                        className="shape-triangle"
                        width="100%"
                        height="100%"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                      >
                        <polygon
                          points="50,0 0,100 100,100"
                          fill={shape.color}
                          fillOpacity={shape.opacity}
                        />
                      </svg>
                    )}
                  </MoveableContainer>
                );
              }
            })}
          </div>
        </div>
        <div
          className={`zoom-slider-container ${isSliderShifted ? "shifted" : ""}`}
        >
          <input
            type="range"
            min="0.1"
            max="3"
            step="0.01"
            value={zoom || 1}
            onChange={handleZoomChange}
            className="zoom-slider"
          />
        </div>
      </div>
    </DndProvider>
  );
};

const ThreeCanvasWrapper = () => (
  <ErrorBoundary>
    <ThreeCanvas />
  </ErrorBoundary>
);

export default ThreeCanvasWrapper;
